"use client";

import type { CartState } from "@/lib/cart";
import type { ProductionEditRpcLine } from "@/lib/adminCart";
import { cartSubtotal } from "@/lib/cartMulti";
import {
  allocateDpPaidByRow,
  computeOrderDpDue,
  orderGoodsAllReady,
  type DpMode
} from "@/lib/dp";
import {
  dpBilledQtyByItem,
  familyItemFromRow,
  familyReservedQtyByItem
} from "@/lib/itemFamilyOpen";
import { fetchCartDpCredit, fetchCartPaidResidual } from "@/lib/supabase/cartDpCredit";
import { type PreorderStatus, type Product, isOrderableStatus } from "@/lib/products";
import type { AdminCustomer } from "@/lib/admin";
import { mapBrandRenameResult, type BrandRenameResult } from "@/lib/brands";
import {
  CATEGORY_RENAME_CHECK_FAILED,
  CATEGORY_RENAME_MAX_ROUNDS,
  CATEGORY_RENAME_UNAVAILABLE,
  categoryRenameInterruptedMessage,
  mapCategoryRenameResult,
  type CategoryRenameResult
} from "@/lib/categoryRename";
import type { CustomerLevel } from "@/lib/orders";
import type { ClosedPOProduct, ClosedPOVariant, LocalOrder } from "@/lib/orders";
import { createBrowserSupabase } from "@/lib/supabase/browser";
import { isRawPermissionMessage, terjemahkanErrorRpc } from "@/lib/rpcError";
import { withStorePrefix } from "@/lib/supabase/storagePaths";
import { fetchAllProductVariants } from "@/lib/supabase/fetchAllVariants";
import { blockedCombosFromRows } from "@/lib/lazyVariants";
import {
  fetchPublicProductHasPriceAxis,
  fetchPublicProductHasPriceAxisStrict,
  resolvePublicInheritedPricing,
  resolvePublicInheritedPricingStrict
} from "@/lib/supabase/public-data";
import { getClientStoreId } from "@/lib/supabase/storeContextClient";
import {
  buildCartInvoiceIndex,
  cartInvoiceRefsForRow,
  deriveCartBadge,
  dpInvoiceSettledAmount,
  CART_BELUM_READY_ORDER_STATUSES,
  CART_INVOICE_ACTIVE_STATUSES,
  CART_INVOICE_VISIBLE_STATUSES,
  type CartBadge,
  type CartBucket,
  type CartInvoiceRef,
  cartBillingInvoicesByOrder,
  pickDpInvoicesByOrder,
  type DpHangusInfo,
  type DpHangusOrphanInfo,
  shipmentGoodsReadiness,
  type GoodsReadinessSummary
} from "@/lib/orderCart";
import { formatInvoiceNo, type InvoiceStatus } from "@/lib/invoices";
import {
  applyCustomerListFilters,
  CUSTOMER_SUMMARY_COLUMNS,
  type CustomerCounts,
  type CustomerFilterKey
} from "@/lib/customers";
import {
  matchVariant,
  resolveSnapshotUnitPrice
} from "@/lib/backfillUnitPrice";
import {
  aggregateOrderItemsForEdit,
  closedPoEditLockedRows,
  type AggregatedGiftItem
} from "@/lib/orderEditorLines";
import { fetchOrderGiftNames } from "@/lib/supabase/gifts";
import {
  brandDiscountsForGroup,
  levelForProduct,
  withBrandDiscounts,
  type GroupBrandRule
} from "@/lib/groupBrandDiscount";
import { fetchGroupBrandDiscountRules } from "@/lib/supabase/groupBrandDiscount";
import { sessionBrandRulesFor } from "@/lib/supabase/brandRulesSession";
import { resolveSelectionPrice, sellableSelectionPrice,
  type PriceCut,
  type PriceTier
} from "@/lib/pricing";
import {
  discountTracePayload,
  normalizeVoucherCode,
  voucherRpcLinePayload,
  type VoucherRpcLine
} from "@/lib/voucher";
import type { CartBundle } from "@/lib/bundleCart";
import { bundleCartLinePayload, bundlePriceIncreases, bundleReplayProbeVerdict } from "@/lib/bundleCheckout";
import {
  bundleAllowsLevel,
  bundleCustomerSlot,
  isBundleRunning,
  quoteBundle,
  type Bundle,
  type BundleQuote
} from "@/lib/bundle";
import { fetchBundlesForCheckout } from "@/lib/supabase/bundles";
import { selectInChunks } from "@/lib/inChunks";
import {
  isProductionEditableRow,
  productionDpInvoiceLocked,
  productionOrderEditLockedReason,
  type ProductionEditItemRow
} from "@/lib/productionDetailCore";
import {
  findUnorderableCarts,
  unavailableVariantMessage,
  unorderableCartsMessage,
  type CheckoutProductRow
} from "@/lib/checkoutGuard";
import { orderLinePriceExtraKey } from "@/lib/orderLinePriceExtra";
import {
  liveVoucherName,
  orderEditErrorMessage,
  orderEditLinesFromItems,
  orderEditNeedsInherited,
  orderEditPayloadLines,
  orderEditPricedLine,
  orderEditSelectionPrice,
  orderEditUnpricedNewVariants,
  orderHasManualPrice,
  orderHasLiveBundle,
  manualPriceLocksRewrite
} from "@/lib/orderEdit";
// Label baris bundel di Keranjang Pesanan (mig 8040) — nama pembelian bundel.
import { fetchOrderBundles } from "@/lib/supabase/bundles";
// Hadiah pembelian (mig 8680) — nama aturan & kunci Edit Pesanan.
import { fetchOrderGiftInfo, type OrderGiftInfo } from "@/lib/supabase/gifts";
import {
  discountTraceFromRow,
  manualDiscountFromRow,
  type DiscountTraceRow,
  type ItemManualDiscount
} from "@/lib/cartItemPrice";
import {
  checkoutOrdersWithVoucher,
  type VoucherCheckoutCart,
  type VoucherCheckoutResult
} from "@/lib/supabase/voucherStorefront";
import { dropExhaustedPromos, promoQuotaLeftForMe, type ProductPromo } from "@/lib/promo";
import {
  fetchPromosForProduct,
  fetchPromosForProductStrict,
  promosForProductFromClientCache
} from "@/lib/supabase/promo";
import { pickProductCoverUrl, resolveProductMediaUrl } from "@/lib/mediaUrl";
import {
  fetchCustomerGroup,
  fetchMyCustomerGroup,
  fetchProductBrandMeta,
  fetchProductPriceNormal,
  fetchProductVariantPricing
} from "@/lib/supabase/unitPriceSnapshot";

export type AdminBrand = {
  id: string;
  name: string;
  note?: string;
  iconUrl?: string;
  /** Link Telegram merek (mig 3440) — sudah dinormalisasi `https://t.me/…`. */
  telegramUrl?: string;
  totalProducts: number;
  createdAt?: string;
};

export type AdminBrandPayload = {
  id?: string;
  name: string;
  note?: string;
  iconUrl?: string;
  /**
   * Link Telegram merek. `undefined` = jangan sentuh kolomnya; string kosong =
   * hapus link. NAMA merek TIDAK diganti lewat sini — ganti nama punya jalur
   * sendiri (`renameAdminBrandInSupabase`) supaya snapshot produk ikut.
   */
  telegramUrl?: string;
};

export type AdminCategory = {
  id: string;
  name: string;
  note?: string;
  iconUrl?: string;
  totalProducts: number;
  createdAt?: string;
};

/**
 * Membuat kategori (tanpa `id`, `name` wajib) atau memperbarui kategori yang ada
 * (dengan `id`). Pembaruan TIDAK boleh membawa `name`: nama kategori berganti
 * lewat `renameAdminCategoryInSupabase` (RPC `admin_rename_category`), karena
 * `products.category` hanya snapshot teks dan harus ikut diperbarui sekaligus.
 */
export type AdminCategoryPayload =
  | {
      id?: undefined;
      name: string;
      note?: string;
      iconUrl?: string | null;
    }
  | {
      id: string;
      name?: never;
      note?: string;
      /** `null` = hapus ikon (ditulis `icon_url = null`); `undefined` = jangan sentuh. */
      iconUrl?: string | null;
    };

export type AdminSegment = {
  id: string;
  name: string;
  totalProducts: number;
  createdAt?: string;
};

export type AdminSegmentPayload = {
  id?: string;
  name: string;
};

export type CheckoutPayload = {
  cart: CartState;
  customerName: string;
  phone: string;
  address: string;
  note?: string;
  subtotal: number | null;
  paymentProof?: File | null;
};

type CheckoutLine = {
  variationId: string;
  variationName: string;
  unitPrice?: number;
  /** Tambahan yang termasuk di `unitPrice` keranjang (mig 7900); absen = tak diketahui. */
  priceExtra?: number;
};

type ResolvedCheckoutLine = {
  variantId: string | null;
  variantName: string;
  unitPrice: number | null;
  /**
   * Tambahan varian yang TERMASUK di `unitPrice` (mig 7900) — potret
   * `order_items.unit_price_extra`. `null` = tak ada harga yang memuatnya
   * (harga menyusul, atau jalur cadangan): trigger DB memotret tambahan
   * varian sendiri, supaya harga yang kelak diisi admin (Ubah Harga keranjang,
   * Buat Nota) ditempeli tambahan yang benar. Dulu harga menyusul menulis 0
   * EKSPLISIT, dan baris itu ditagih tanpa tambahannya (bughunt D14-9).
   */
  priceExtra: number | null;
  /**
   * JEJAK kampanye (mig 3650) — kampanye yang MENGHASILKAN `unitPrice` di atas,
   * bukan "kampanye yang kebetulan berjalan". Keduanya null saat harga tak
   * berasal dari promo; null TIDAK berarti "tanpa promo" bagi pembaca (lihat
   * catatan di migrasinya).
   */
  promoCampaignId: string | null;
  promoCampaignName: string | null;
  /**
   * HARGA JUAL UMUM sumber `unitPrice` (varian terpilih / perwakilan) dan TIER
   * (promo / override varian / diskon grup) yang membentuknya — bahan VOUCHER
   * (mig 5610): voucher dipotong dari harga umum, lalu tier diterapkan ulang
   * (`unitPriceAfterVoucher`). `normalPrice` null = harga menyusul / jalur
   * fallback → baris tak pernah layak voucher.
   */
  normalPrice: number | null;
  tier: PriceTier;
  /** Potongan KEDUA (promo harga coret + level) — mesin voucher dua tier. */
  tier2: PriceTier;
  /**
   * Seluruh potongan yang membentuk `unitPrice` — jejak ke
   * `order_items.promo_/level_discount_*` (mig promo_harga_coret) supaya
   * keranjang, pesanan, nota, dan WhatsApp memajang badge yang SAMA dengan
   * halaman produk tanpa menebak dari kampanye hari ini. Kosong = tak tercatat.
   */
  cuts: readonly PriceCut[];
};

/**
 * Bangun fungsi resolusi baris checkout: ambil varian produk + grup pelanggan
 * sekali, lalu kembalikan resolver yang menentukan `variant_id` TERKINI dan
 * harga jual via pricing.ts per baris keranjang.
 *
 * Kenapa variant_id ikut di-resolve (bukan dipercaya dari keranjang): keranjang
 * dipersist di localStorage dan ProductForm menghapus + membuat ulang SEMUA
 * varian saat admin mengedit produk — id varian di keranjang bisa basi. Insert
 * dengan id basi ditolak trigger DB `ensure_order_item_same_product` dengan
 * error mentah "Variation must belong to the order product". Di sini id basi
 * di-remap ke varian terkini lewat `matchVariant` (id dulu, fallback nama);
 * varian yang benar-benar hilang → error ramah yang menyuruh muat ulang.
 *
 * Sumber rumus harga tunggal = pricing.ts; SQL hanya menerima nilai ini. Bila
 * varian/grup gagal dimuat (mis. offline sesaat), jatuh ke nilai keranjang
 * (perilaku lama) agar checkout tak pernah gagal gara-gara ini — DB tetap
 * penjaga otoritatif.
 */
/**
 * Kampanye untuk SATU produk di jalur yang MENGUNCI harga (checkout & edit
 * pesanan OPEN PO): fetcher strict dicoba dua kali → cadangan cache TanStack
 * storefront → harga tanpa promo. Selalu bersuara lewat `console.warn`
 * ber-kode `[promo:checkout-fetch-failed]` (dicatat di GOTCHA); tak pernah
 * memakai angka keranjang. Rinciannya di komentar `buildCheckoutLineResolver`.
 */
async function loadPromosForCheckout(
  productId: string,
  opts?: { dropExhausted?: boolean }
): Promise<ProductPromo[]> {
  const promos = await loadPromosForCheckoutRaw(productId);
  return opts?.dropExhausted ? dropExhaustedPromos(promos, await myPromoQuotaLeft(promos)) : promos;
}

/**
 * Sisa kuota kampanye untuk pembeli yang login (`promo_campaign_my_quota`,
 * mig 6020) — HANYA kampanye yang memang berlaku pada produk ini (biasanya 0–1
 * panggilan). Galat / baris kosong = kunci absen = tak diketahui → promonya
 * tetap ditawarkan dan trigger `enforce_promo_quota` tetap penjaganya.
 */
async function myPromoQuotaLeft(promos: readonly ProductPromo[]): Promise<Map<string, number | null>> {
  const out = new Map<string, number | null>();
  if (!promos.length) return out;
  const db = createBrowserSupabase();
  if (!db) return out;
  await Promise.all(
    [...new Set(promos.map((p) => p.campaignId))].map(async (campaignId) => {
      try {
        const { data, error } = await db.rpc("promo_campaign_my_quota", { p_campaign: campaignId });
        if (error) throw new Error(error.message);
        const left = promoQuotaLeftForMe(Array.isArray(data) ? data[0] : data);
        if (left !== undefined) out.set(campaignId, left);
      } catch (err) {
        console.warn("[promo:quota-check-failed] kuota tak terbaca — kampanye tetap ditawarkan", campaignId, err);
      }
    })
  );
  return out;
}

async function loadPromosForCheckoutRaw(productId: string): Promise<ProductPromo[]> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return await fetchPromosForProductStrict(productId, "store");
    } catch (err) {
      lastErr = err;
    }
  }
  const cached = await promosForProductFromClientCache(productId);
  if (cached) {
    console.warn(
      "[promo:checkout-fetch-failed] memakai kampanye dari cache storefront",
      productId,
      lastErr
    );
    return cached;
  }
  console.warn(
    "[promo:checkout-fetch-failed] tanpa cadangan — harga dihitung tanpa promo",
    productId,
    lastErr
  );
  return [];
}

/**
 * Aturan diskon merek per level di CHECKOUT (mig 7070) — pola A5 promo di atas:
 * fetcher strict dua kali → aturan yang sedang dipajang storefront pembeli INI
 * (`sessionBrandRulesFor`, diisi CustomerContext) → tanpa aturan. Selalu
 * bersuara `[diskon-merek:checkout-fetch-failed]`; tak pernah melempar ke jalur
 * cadangan HARGA KERANJANG (angka localStorage yang bisa diedit siapa pun).
 */
async function loadBrandRulesForCheckout(
  db: NonNullable<ReturnType<typeof createBrowserSupabase>>,
  customerId: string
): Promise<GroupBrandRule[]> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return await fetchGroupBrandDiscountRules(db);
    } catch (err) {
      lastErr = err;
    }
  }
  const session = sessionBrandRulesFor(customerId);
  if (session) {
    console.warn("[diskon-merek:checkout-fetch-failed] memakai aturan sesi storefront", lastErr);
    return session;
  }
  console.warn(
    "[diskon-merek:checkout-fetch-failed] tanpa cadangan — harga dihitung tanpa aturan merek",
    lastErr
  );
  return [];
}

/**
 * Merek produk di CHECKOUT — strict dua kali, lalu `null` + peringatan (tanpa
 * aturan merek). Sengaja TIDAK melempar: lemparan di sini jatuh ke harga
 * keranjang (lihat `buildCheckoutLineResolver`).
 */
async function loadProductBrandForCheckout(
  db: NonNullable<ReturnType<typeof createBrowserSupabase>>,
  productId: string
): Promise<{ brand: string | null; isManual: boolean } | null> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return await fetchProductBrandMeta(db, productId);
    } catch (err) {
      lastErr = err;
    }
  }
  console.warn(
    "[diskon-merek:checkout-fetch-failed] merek produk tak terbaca — harga dihitung tanpa aturan merek",
    productId,
    lastErr
  );
  return null;
}

/**
 * SUMBU HARGA (2026-10-20, mig 7680) — dua RPC harga yang dipanggil resolver
 * checkout untuk baris varian TANPA harga sendiri, keduanya ber-toko eksplisit.
 * Toko klien TAK ter-resolusi → RPC tidak dipanggil, pulang `null`: `priceAxis`
 * null = "sumbu tak diketahui" yang `resolveSelectionPrice` perlakukan
 * fail-closed, dan warisan null = harga menyusul. Tak pernah "tanya toko mana
 * saja" (aturan A2; `storeScopeLint.test.ts`). Baris yang SUDAH berharga tak
 * lewat sini sama sekali.
 */
async function loadPriceAxisForCheckout(productId: string): Promise<boolean | null> {
  const storeId = getClientStoreId();
  if (!storeId) return null;
  return fetchPublicProductHasPriceAxis(productId, storeId).catch(() => null);
}

async function loadInheritedPricingForCheckout(
  productId: string,
  optionIds: string[]
): Promise<Awaited<ReturnType<typeof resolvePublicInheritedPricing>>> {
  const storeId = getClientStoreId();
  if (!storeId) return null;
  return resolvePublicInheritedPricing(productId, optionIds, storeId);
}

/**
 * Pemuat KETAT untuk Simpan Edit Pesanan (tinjauan PR E): gagal = LEMPAR →
 * `priceContextError` → baris dikirim TANPA kunci `unitPrice` (server
 * mempertahankan snapshot lama, kelas E2-01). Pemuat checkout di atas sengaja
 * memaafkan (baris BARU jatuh ke harga menyusul); di sini maaf yang sama
 * menulis `unitPrice: null` di atas harga yang sudah ada.
 */
async function loadPriceAxisForOrderEdit(productId: string): Promise<boolean> {
  const storeId = getClientStoreId();
  if (!storeId) throw new Error("Toko tidak dikenali — sumbu harga tidak bisa dimuat");
  return fetchPublicProductHasPriceAxisStrict(productId, storeId);
}

async function loadInheritedPricingForOrderEdit(
  productId: string,
  optionIds: string[]
): Promise<Awaited<ReturnType<typeof resolvePublicInheritedPricingStrict>>> {
  const storeId = getClientStoreId();
  if (!storeId) throw new Error("Toko tidak dikenali — warisan harga tidak bisa dimuat");
  return resolvePublicInheritedPricingStrict(productId, optionIds, storeId);
}

async function buildCheckoutLineResolver(
  db: NonNullable<ReturnType<typeof createBrowserSupabase>>,
  productId: string,
  customerId: string
): Promise<(line: CheckoutLine) => Promise<ResolvedCheckoutLine>> {
  try {
    // Promo kampanye (mig 3470) WAJIB ikut di sini: inilah satu-satunya tempat
    // harga dikunci ke order_items.unit_price. Kalau promo tak sampai ke baris
    // ini, storefront memajang harga promo tapi notanya menagih harga penuh.
    // Keduanya best-effort — gagal memuat promo TIDAK boleh menggagalkan
    // checkout; paling buruk pembeli membayar harga normal.
    // Audit A5 (2026-09-16, ditulis ulang 2026-09-17): dulu satu kegagalan
    // sesaat di sini (`catch → []`) membuat promo dianggap TIDAK ADA dan HARGA
    // NORMAL dikunci ke `order_items.unit_price` — padahal pembeli baru saja
    // melihat harga promo, dan tak ada satu pun tanda. Perbaikan pertamanya
    // tak pernah berjalan: `fetchPromosForProduct` menelan galatnya sendiri
    // (tak pernah melempar) sehingga cabang coba-ulang & cadangannya mati; dan
    // cadangan itu memakai HARGA KERANJANG (localStorage) — angka yang bisa
    // diedit siapa pun, sementara insert `order_items` tak punya gerbang harga
    // server (mig 5420 hanya `unit_price >= 0`). Kini: fetcher STRICT (yang
    // melempar) dicoba dua kali; masih gagal → kampanye yang SUDAH DIMUAT
    // storefront (cache TanStack `PromoContext`) dicocokkan ke produknya; bila
    // itu pun tak ada → harga tanpa promo seperti dulu. Selalu bersuara
    // (`console.warn` ber-kode, dicatat di GOTCHA), tak pernah memakai angka
    // keranjang.
    const [variants, customerGroup, promoContext, productPriceNormal, productMeta, priceAxis] =
      await Promise.all([
        fetchProductVariantPricing(db, productId),
        // Diskon merek per level (mig 7070) lewat pemuat ber-cadangan A5 — galat
        // aturannya TIDAK jatuh ke harga keranjang (galat GRUP tetap seperti dulu).
        fetchCustomerGroup(db, customerId, {
          rules: () => loadBrandRulesForCheckout(db, customerId)
        }),
        // Kampanye yang kuotanya HABIS bagi pembeli ini tak ikut bersaing
        // (bughunt D16-11, cermin storefront) — hanya di checkout BARU; Edit
        // Pesanan memakai pemuat yang sama TANPA saringan ini, karena pcs
        // berpromo miliknya sendiri ikut menghabiskan jatahnya.
        loadPromosForCheckout(productId, { dropExhausted: true }),
        // Lapis TERAKHIR model harga; paralel supaya tak menambah latensi.
        fetchProductPriceNormal(db, productId).catch(() => undefined),
        loadProductBrandForCheckout(db, productId),
        // SUMBU HARGA (2026-10-20, mig 7680): produk ber-sumbu → baris varian
        // tanpa harga hanya boleh berharga dari warisan slot (RPC yang SAMA
        // dengan halaman produk). Gagal / toko tak ter-resolusi → null →
        // fail-closed (harga menyusul).
        loadPriceAxisForCheckout(productId)
      ]);
    const group = levelForProduct(customerGroup, productMeta);
    if (variants.length) {
      // Warisan slot per varian tanpa harga — satu RPC per varian, bukan per
      // baris keranjang (dua baris varian yang sama tak bertanya dua kali).
      const inheritedCache = new Map<
        string,
        Awaited<ReturnType<typeof resolvePublicInheritedPricing>>
      >();
      return async (line) => {
        const match = matchVariant(
          {
            variantId: isUuid(line.variationId) ? line.variationId : null,
            variantName: line.variationName
          },
          variants
        );
        if (!match) {
          // KALIMATNYA SENGAJA TIDAK menyuruh "muat ulang lalu pilih ulang":
          // sebab tersering di PROD bukan varian yang berpindah id, melainkan
          // varian yang DIMATIKAN admin (`is_active = false`) — RLS pembeli
          // menuntut `is_active`, jadi barisnya lenyap dari pandangan dan
          // memuat ulang halaman tak akan pernah memunculkannya lagi. Pembeli
          // yang menuruti saran lama akan mencoba berulang kali tanpa hasil
          // (bug live natasyashop; tombolnya kini juga sudah dicoret sejak
          // mig 5830, jadi baris seperti ini hanya lahir dari keranjang lama).
          throw new Error(unavailableVariantMessage(line.variationName));
        }
        // SUMBU HARGA (mig 7680): baris varian yang ADA tapi tanpa harga pada
        // produk ber-sumbu (atau sumbunya tak diketahui) → tanya warisan slot
        // — saudara pada sumbu yang sama, RPC yang SAMA dengan halaman produk.
        // NULL (saudara belum berharga / tak sepakat / RPC gagal) → harga
        // menyusul lewat `unpricedVariant`, bukan harga varian lain.
        let inherited: Awaited<ReturnType<typeof resolvePublicInheritedPricing>> | undefined;
        if (match.pricing.price_normal === undefined && priceAxis !== false) {
          const cached = inheritedCache.get(match.variantId);
          if (cached !== undefined) {
            inherited = cached;
          } else {
            inherited = await loadInheritedPricingForCheckout(
              productId,
              match.pricing.optionIds ?? []
            );
            inheritedCache.set(match.variantId, inherited);
          }
        }
        // SATU aturan dengan tampilan (kartu & halaman produk): varian yang
        // dipilih dulu, lalu fallback ke varian perwakilan / harga produk.
        // Tanpa fallback ini, varian yang belum diberi harga sendiri menghasilkan
        // unit_price NULL → nota lahir Rp 0 padahal pembeli melihat ada harga.
        const selection = resolveSelectionPrice(
          match.pricing,
          {
            variations: variants.map((v) => v.pricing),
            priceNormal: productPriceNormal,
            priceAxis,
            inherited
          },
          group,
          promoContext
        );
        // HARGA MENYUSUL, BUKAN TOLAK (keputusan pemilik 2026-09-07). Varian
        // tanpa harga sendiri pada produk ber-harga varian CAMPURAN tak boleh
        // mengunci harga varian PERWAKILAN — di PROD itu menagih Rp 521.250
        // (Dress Style 1) untuk sepotong Blouse Rp 445.000, tanpa satu pun tanda
        // di layar (natasyashop #2095). Perbaikan 2026-09-05 menutupnya dengan
        // MELEMPAR, dan efek itu ditolak pemilik: pesanannya harus tetap masuk.
        // Jadi yang ditolak sekarang ANGKANYA, bukan pesanannya —
        // `sellableSelectionPrice` memulangkan `undefined` → `unit_price` NULL,
        // alur "harga menyusul" yang sudah dipegang board & nota (admin
        // mengisinya lewat Buat Nota, badge "Harga Belum Ditentukan").
        const { price, promo, tier, tier2, cuts, normalPrice, priceExtra } = sellableSelectionPrice(selection);
        return {
          variantId: match.variantId,
          variantName: match.variantName,
          unitPrice: price === undefined ? null : price,
          // mig 7900: potret tambahan yang TERMASUK di unit_price — ditulis
          // eksplisit supaya trigger DB tak perlu menebak varian hari ini.
          // Harga menyusul: tak ada harga yang memuatnya → NULL, trigger
          // memotret tambahan varian sendiri (bughunt D14-9).
          priceExtra: price === undefined ? null : priceExtra,
          promoCampaignId: promo?.campaignId ?? null,
          promoCampaignName: promo?.name ?? null,
          normalPrice: normalPrice === undefined ? null : normalPrice,
          tier,
          tier2,
          cuts
        };
      };
    }
    // DATA VARIAN TERBACA KOSONG (bughunt ketiga, 2026-09-30): produk
    // dinonaktifkan (mis. otomatis saat stok habis) atau semua variannya
    // dimatikan — RLS pembeli menuntut keduanya aktif — sementara baris
    // keranjang membawa id varian NYATA. Ditolak di sini, SEBELUM pesanan
    // dibuat. Produk yang memang tanpa varian (id keranjang bukan uuid)
    // dihargai dari harga PRODUK di DB lewat tangga yang sama (promo, level),
    // bukan dari angka keranjang (bughunt D16-16).
    const productOnly = sellableSelectionPrice(
      resolveSelectionPrice(
        undefined,
        { variations: [], priceNormal: productPriceNormal, priceAxis },
        group,
        promoContext
      )
    );
    return async (line) => {
      if (isUuid(line.variationId)) {
        throw new Error(unavailableVariantMessage(line.variationName));
      }
      const price = productOnly.price;
      return {
        variantId: null,
        variantName: line.variationName,
        unitPrice: price === undefined ? null : price,
        priceExtra: price === undefined ? null : productOnly.priceExtra,
        promoCampaignId: productOnly.promo?.campaignId ?? null,
        promoCampaignName: productOnly.promo?.name ?? null,
        normalPrice: productOnly.normalPrice === undefined ? null : productOnly.normalPrice,
        tier: productOnly.tier,
        tier2: productOnly.tier2,
        cuts: productOnly.cuts
      };
    };
  } catch (err) {
    // GAGAL MEMUAT varian / grup / harga (bughunt D16-16). Dulu jatuh ke harga
    // KERANJANG localStorage — angka basi (promo yang sudah berakhir, level
    // lama) yang bisa diedit siapa pun — dan terkunci tanpa jejak maupun log.
    // Belum ada yang ditulis pada titik ini, jadi checkout dihentikan dengan
    // pesan yang bisa diulang. (Error "variasi tidak tersedia" dilempar dari
    // resolver saat DIPANGGIL, bukan di sini, jadi tetap sampai ke pembeli.)
    console.warn("[checkout:price-load-failed] harga tak bisa dimuat — checkout dihentikan", productId, err);
    throw new Error(CHECKOUT_PRICE_LOAD_FAILED_MESSAGE);
  }
}

/** Pesan galat memuat harga checkout (bughunt D16-16) — bisa diulang, tanpa angka keranjang. */
export const CHECKOUT_PRICE_LOAD_FAILED_MESSAGE =
  "Harga produk belum bisa dimuat. Cek koneksi lalu coba checkout lagi.";

// ─── VOUCHER PRODUK (mig 5610) — pratinjau & checkout lewat RPC ─────────────

export type ResolvedCartLine = VoucherRpcLine & {
  /** Kunci baris di keranjang (`CartLine.variationId`, bisa basi) — untuk mencocokkan kembali ke UI. */
  variationId: string;
};

export type ResolvedCartPricing = {
  productId: string;
  productName: string;
  lines: ResolvedCartLine[];
};

/**
 * Harga umum / pra-voucher / tier tiap baris keranjang, dari RESOLVER YANG SAMA
 * dengan checkout (`buildCheckoutLineResolver`) — bahan `preview_my_voucher`
 * dan `checkout_orders_with_voucher`. Angka ini pratinjau: server membaca
 * ulang harga umum dari DB dan menolak bila berbeda (`VOUCHER_INVALID:
 * price_changed`).
 *
 * Kunci baris = `<indeks cart>:<indeks baris>` — RPC checkout menomori ulang
 * dengan pola yang sama, jadi hasil pratinjau bisa dicocokkan ke hasil akhir.
 */
export async function resolveCartsPricing(
  carts: readonly CartState[],
  customerId: string
): Promise<ResolvedCartPricing[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  const out: ResolvedCartPricing[] = [];
  for (const [ci, cart] of carts.entries()) {
    const resolve = await buildCheckoutLineResolver(db, cart.productId, customerId);
    const lines: ResolvedCartLine[] = [];
    for (const [li, line] of cart.lines.entries()) {
      if (line.qty <= 0) continue;
      const resolved = await resolve(line);
      lines.push({
        key: `${ci + 1}:${li + 1}`,
        variationId: line.variationId,
        productId: cart.productId,
        variantId: resolved.variantId,
        variantName: resolved.variantName,
        qty: line.qty,
        normalPrice: resolved.normalPrice,
        unitPrice: resolved.unitPrice,
        tier: resolved.tier,
        tier2: resolved.tier2,
        cuts: resolved.cuts,
        promoCampaignId: resolved.promoCampaignId,
        promoCampaignName: resolved.promoCampaignName,
        // mig 7900: tambahan yang termasuk di `unitPrice` — bahan pratinjau voucher
        // & "Harga Umum" ringkasan. Mesin voucher membaca DB; muatan checkout
        // mengirimnya untuk baris tak layak (`voucherCheckoutLinePayload`, mig 8080).
        priceExtra: resolved.priceExtra,
        note: line.note?.trim().slice(0, 20) || null
      });
    }
    out.push({ productId: cart.productId, productName: cart.productName, lines });
  }
  return out;
}

/**
 * Checkout SEMUA cart ber-VOUCHER dalam satu transaksi (RPC mig 5610). Dipakai
 * HANYA saat pembeli memilih voucher (keputusan pemilik Q2) — checkout tanpa
 * voucher tetap `createCheckoutOrder(s)` yang menulis `order_items` langsung.
 *
 * Server menghitung ulang harga umum, nilai voucher, alokasi, dan harga akhir
 * lalu menulis semuanya sendiri; klien hanya mengirim baris hasil resolver.
 * Gagal satu produk = seluruh checkout batal (minimum pembelian lintas produk).
 * `requestId` = kunci idempoten (`voucher_usages.client_request_id`).
 */
export async function createCheckoutOrdersWithVoucher(payload: {
  carts: readonly CartState[];
  customerId: string;
  voucherId: string;
  /** Kode voucher ber-kode (mig 6990); null = voucher daftar. */
  voucherCode?: string | null;
  requestId: string;
  note?: string | null;
  address?: string | null;
}): Promise<VoucherCheckoutResult> {
  // Gerbang dini yang sama dengan jalur tanpa voucher (`checkoutGuard.ts`).
  const guardDb = createBrowserSupabase();
  if (guardDb) await assertCartsOrderable(guardDb, payload.carts);
  const pricing = await resolveCartsPricing(payload.carts, payload.customerId);
  const rpcCarts: VoucherCheckoutCart[] = pricing
    .filter((c) => c.lines.length > 0)
    .map((c) => ({ productId: c.productId, lines: c.lines }));
  if (!rpcCarts.length) throw new Error("Keranjang kosong.");
  // Jalur voucher menulis lewat RPC ber-transaksi, jadi galat trigger tak
  // meninggalkan pesanan yatim — tapi pesannya sampai sebagai `PROMO_QUOTA:*`
  // mentah dari SQL. Pemeriksaan dini ini yang memberi kalimat & angkanya.
  const dbQuota = createBrowserSupabase();
  if (dbQuota) {
    await assertPromoQuotaAvailable(
      dbQuota,
      pricing.flatMap((c) =>
        c.lines.map((l) => ({ qty: l.qty, promoCampaignId: l.promoCampaignId }))
      )
    );
  }
  return checkoutOrdersWithVoucher({
    carts: rpcCarts,
    voucherId: payload.voucherId,
    code: payload.voucherCode ?? null,
    note: payload.note ?? null,
    address: payload.address ?? null,
    requestId: payload.requestId
  });
}

// ─── BUNDEL (mig 8040) — harga bundel & checkout satu transaksi ─────────────

export type ResolvedBundleLine = ResolvedCartLine & {
  componentId: string;
  position: number;
  /** Harga terpisah per pcs (termasuk tambahan) — `bundle_base_unit_price`. */
  baseUnitPrice: number | null;
};

export type ResolvedBundle = {
  cartKey: string;
  bundle: Bundle;
  qty: number;
  quote: BundleQuote;
  lines: ResolvedBundleLine[];
};

/**
 * Harga terpisah tiap komponen (RESOLVER YANG SAMA dengan checkout biasa —
 * promo, level, aturan merek, sumbu harga, tambahan) lalu `quoteBundle`.
 * Definisi bundel dibaca ulang STRICT; bundel yang berakhir / nonaktif / tak
 * berhak / komponennya berubah MENGHENTIKAN checkout sebelum menulis apa pun
 * (idiom `checkoutGuard.ts`) — tak pernah turun diam-diam ke harga satuan.
 */
export async function resolveBundlesPricing(
  bundles: readonly CartBundle[],
  customerId: string
): Promise<ResolvedBundle[]> {
  if (!bundles.length) return [];
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap");
  const defs = new Map(
    (await fetchBundlesForCheckout(bundles.map((b) => b.bundleId))).map((b) => [b.id, b] as const)
  );
  const group = await fetchCustomerGroup(db, customerId, {
    rules: () => loadBrandRulesForCheckout(db, customerId)
  });
  const now = new Date();
  const resolvers = new Map<string, (line: CheckoutLine) => Promise<ResolvedCheckoutLine>>();
  const out: ResolvedBundle[] = [];
  for (const [bi, entry] of bundles.entries()) {
    const def = defs.get(entry.bundleId);
    if (!def || !isBundleRunning(def, now)) {
      throw new Error(`Bundling "${entry.name}" sudah tidak tersedia. Hapus dari keranjang, lalu checkout lagi.`);
    }
    if (!bundleAllowsLevel(def, bundleCustomerSlot(group))) {
      throw new Error(`Bundling "${def.name}" tidak berlaku untuk akunmu. Hapus dari keranjang, lalu checkout lagi.`);
    }
    const comps = [...def.components].sort((a, b) => a.position - b.position);
    const byComponent = new Map(entry.choices.map((c) => [c.componentId, c] as const));
    if (comps.length !== entry.choices.length || comps.some((c) => !c.productId || !byComponent.has(c.id))) {
      throw new Error(`Isi bundling "${def.name}" sudah berubah. Buka lagi halaman bundlingnya dan pilih ulang.`);
    }
    const lines: ResolvedBundleLine[] = [];
    for (const comp of comps) {
      const choice = byComponent.get(comp.id)!;
      if (choice.productId !== comp.productId) {
        throw new Error(`Isi bundling "${def.name}" sudah berubah. Buka lagi halaman bundlingnya dan pilih ulang.`);
      }
      let resolve = resolvers.get(comp.productId as string);
      if (!resolve) {
        resolve = await buildCheckoutLineResolver(db, comp.productId as string, customerId);
        resolvers.set(comp.productId as string, resolve);
      }
      const resolved = await resolve({
        variationId: choice.variationId,
        variationName: choice.variationName,
        qty: entry.qty
      } as CheckoutLine);
      lines.push({
        key: `b${bi + 1}:${comp.position}`,
        componentId: comp.id,
        position: comp.position,
        variationId: choice.variationId,
        productId: comp.productId as string,
        variantId: resolved.variantId,
        variantName: resolved.variantName,
        qty: entry.qty,
        normalPrice: resolved.normalPrice,
        unitPrice: resolved.unitPrice,
        baseUnitPrice: resolved.unitPrice,
        tier: resolved.tier,
        tier2: resolved.tier2,
        cuts: resolved.cuts,
        promoCampaignId: resolved.promoCampaignId,
        promoCampaignName: resolved.promoCampaignName,
        priceExtra: resolved.priceExtra,
        note: null
      });
    }
    const quote = quoteBundle(
      def,
      group,
      lines.map((l) => ({
        componentId: l.componentId,
        price: l.unitPrice === null ? undefined : l.unitPrice,
        priceExtra: l.priceExtra ?? 0
      }))
    );
    if (quote.applies) {
      const byLine = new Map(quote.lines.map((l) => [l.componentId, l] as const));
      for (const l of lines) l.unitPrice = byLine.get(l.componentId)?.unitPrice ?? l.unitPrice;
    } else if (quote.reason === "harga_menyusul") {
      throw new Error(
        `Ada pilihan di bundling "${def.name}" yang harganya belum tersedia. Pilih varian lain di halaman bundlingnya.`
      );
    }
    out.push({ cartKey: entry.key, bundle: def, qty: entry.qty, quote, lines });
  }
  return out;
}

export type BundleCheckoutResult = {
  orders: Array<{ productId: string; orderId: string; orderCode: string; merged: boolean; productType: string | null }>;
  bundles: Array<{ orderBundleId: string; bundleId: string; name: string; qty: number }>;
  usageId: string | null;
  voucherName: string | null;
};

/**
 * Checkout SELURUH keranjang yang memuat ≥1 bundel dalam SATU transaksi (RPC
 * `checkout_orders_with_bundles`). Bundel yang "tidak berlaku" bagi pembeli
 * ini (aturan termurah) dikirim sebagai baris biasa. Bila tak satu bundel pun
 * berlaku, pemanggil memakai jalur lama (`bundlesOnlyAsLines` = true).
 *
 * `expected` (§6.4 "harga berubah → konfirmasi") = harga per 1 bundel yang
 * DIPAJANG kartu keranjang, per kunci bundel (`bundleExpectedTotals`). Harga
 * hitungan ulang yang NAIK darinya — atau bundel tanpa angka pembanding —
 * menghentikan checkout dengan `BUNDLE_PRICE_CHANGED` sebelum apa pun ditulis;
 * turun boleh lanjut. Tanpa `expected` = gerbang ini tak dijalankan.
 *
 * `keyOutstanding` = `requestId` ini SUDAH pernah dikirim pada percobaan yang
 * hasilnya tak diketahui (respons hilang, galat tak dikenal). Sebelum gerbang
 * klien APA PUN (harga berubah, tak satu bundel pun berlaku, ketersediaan,
 * kuota) replay diperiksa lebih dulu lewat PROBE: RPC yang sama dengan kunci
 * itu dan `p_bundles` kosong. Sudah tercatat → hasil lamanya dipulangkan
 * (pesanan TIDAK dibuat dua kali); terbukti belum (`BUNDLE_CHECKOUT_EMPTY_ERROR`)
 * → checkout berjalan seperti percobaan pertama; tak tahu → galatnya
 * dilempar dan kunci tetap disimpan pemanggil. Probe sengaja tak mengirim
 * bundelnya: percobaan yang belum tercatat lalu lolos validasi server akan
 * memesan harga baru TANPA konfirmasi §6.4.
 */
export async function createCheckoutOrdersWithBundles(payload: {
  carts: readonly CartState[];
  bundles: readonly CartBundle[];
  customerId: string;
  voucherId?: string | null;
  voucherCode?: string | null;
  requestId: string;
  note?: string | null;
  address?: string | null;
  expected?: ReadonlyMap<string, number>;
  keyOutstanding?: boolean;
  /**
   * Hadiah pembelian (mig 8680) — muatan `p_gifts` dari `giftCheckoutPayload`.
   * Server menghitung sendiri hak hadiahnya dari baris biasa checkout ini.
   */
  gifts?: ReadonlyArray<{ rule_id: string; lines: ReadonlyArray<{ variant_id: string; qty: number }> }>;
}): Promise<BundleCheckoutResult> {
  const gifts = payload.gifts ?? [];
  if (payload.keyOutstanding) {
    const probeDb = createBrowserSupabase();
    if (!probeDb) throw new Error("Supabase belum siap");
    const { data, error } = await probeDb.rpc("checkout_orders_with_bundles", {
      p_carts: [],
      p_bundles: [],
      p_voucher_id: null,
      p_code: null,
      p_note: null,
      p_order_address: null,
      p_request_id: payload.requestId
    });
    const verdict = bundleReplayProbeVerdict(data, error?.message ?? null);
    if (verdict === "replay") return parseBundleCheckoutResult(data);
    if (verdict === "tak_tahu") {
      throw new Error(error?.message || "Gagal memeriksa checkout sebelumnya. Coba lagi.");
    }
    // "bebas": kunci terbukti belum tercatat — lanjut seperti percobaan pertama.
  }
  const guardDb = createBrowserSupabase();
  if (guardDb) {
    await assertCartsOrderable(guardDb, [
      ...payload.carts,
      ...payload.bundles.flatMap((b) =>
        b.choices.map((c) => ({ productId: c.productId, productName: c.productName }))
      )
    ]);
  }
  const [pricing, bundlePricing] = await Promise.all([
    resolveCartsPricing(payload.carts, payload.customerId),
    resolveBundlesPricing(payload.bundles, payload.customerId)
  ]);
  // Gerbang konfirmasi §6.4 — SEBELUM cadangan BUNDLE_NONE_APPLIES, supaya
  // bundel yang dipesan harga satuan pun tak naik diam-diam dari yang dilihat.
  if (payload.expected && bundlePriceIncreases(bundlePricing, payload.expected).length > 0) {
    throw new Error("BUNDLE_PRICE_CHANGED");
  }
  const applied = bundlePricing.filter((b) => b.quote.applies);
  // Keranjang berhadiah tetap lewat RPC ini walau tak satu bundel pun berlaku:
  // bundelnya dipesan harga satuan (baris biasa), hadiahnya tetap diklaim.
  if (!applied.length && !gifts.length) {
    throw new Error("BUNDLE_NONE_APPLIES");
  }
  // Baris biasa + komponen bundel yang tak berlaku (dipesan harga satuan).
  const rpcCarts = [
    ...pricing.filter((c) => c.lines.length > 0).map((c) => ({ productId: c.productId, lines: c.lines })),
    ...bundlePricing
      .filter((b) => !b.quote.applies)
      .flatMap((b) => b.lines.map((l) => ({ productId: l.productId, lines: [l as ResolvedCartLine] })))
  ];
  const dbQuota = createBrowserSupabase();
  if (dbQuota) {
    await assertPromoQuotaAvailable(dbQuota, [
      ...rpcCarts.flatMap((c) => c.lines.map((l) => ({ qty: l.qty, promoCampaignId: l.promoCampaignId }))),
      ...applied.flatMap((b) => b.lines.map((l) => ({ qty: l.qty, promoCampaignId: l.promoCampaignId })))
    ]);
  }
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap");
  const { data, error } = await db.rpc("checkout_orders_with_bundles", {
    // mig 8070: baris biasa membawa tambahan yang diketahui (`bundleCartLinePayload`).
    p_carts: rpcCarts.map((c) => ({ product_id: c.productId, lines: c.lines.map(bundleCartLinePayload) })),
    p_bundles: applied.map((b) => bundleRpcPayload(b)),
    p_voucher_id: payload.voucherId ?? null,
    p_code: payload.voucherId ? normalizeVoucherCode(payload.voucherCode) : null,
    p_note: payload.note ?? null,
    p_order_address: payload.address ?? null,
    p_request_id: payload.requestId,
    p_gifts: gifts
  });
  if (error) {
    // Galat hadiah membawa nama aturannya di `details` — diteruskan setelah
    // `|` supaya `giftErrorMessage` bisa menyebut hadiahnya.
    if (/GIFT_INVALID:/.test(error.message) && error.details) {
      throw new Error(`${error.message}|${error.details}`);
    }
    throw new Error(error.message);
  }
  return parseBundleCheckoutResult(data);
}

/** Jawaban RPC `checkout_orders_with_bundles` (baru ATAU replay) → hasil checkout. */
function parseBundleCheckoutResult(data: unknown): BundleCheckoutResult {
  const r = (data ?? {}) as Record<string, unknown>;
  return {
    orders: Array.isArray(r.orders)
      ? (r.orders as Array<Record<string, unknown>>).map((o) => ({
          productId: String(o.productId ?? ""),
          orderId: String(o.orderId ?? ""),
          orderCode: String(o.orderCode ?? ""),
          merged: o.merged === true,
          productType: typeof o.productType === "string" ? o.productType : null
        }))
      : [],
    bundles: Array.isArray(r.bundles)
      ? (r.bundles as Array<Record<string, unknown>>).map((b) => ({
          orderBundleId: String(b.orderBundleId ?? ""),
          bundleId: String(b.bundleId ?? ""),
          name: String(b.name ?? ""),
          qty: Number(b.qty) || 0
        }))
      : [],
    usageId: typeof r.usageId === "string" ? r.usageId : null,
    voucherName: typeof r.voucherName === "string" ? r.voucherName : null
  };
}

/** Muatan satu bundel untuk RPC — kontrak `p_bundles` mig 8040. */
function bundleRpcPayload(b: ResolvedBundle): Record<string, unknown> {
  if (!b.quote.applies) throw new Error("bundling tak berlaku tak dikirim sebagai bundling");
  const quote = b.quote;
  const byLine = new Map(quote.lines.map((l) => [l.componentId, l] as const));
  return {
    bundle_id: b.bundle.id,
    qty: b.qty,
    bundle_price: quote.bundleBase,
    separate_price: quote.separateBase,
    lines: b.lines.map((l) => {
      const q = byLine.get(l.componentId);
      return {
        ...voucherRpcLinePayload(l),
        component_id: l.componentId,
        unit_price: q?.unitPrice ?? null,
        base_unit_price: q?.baseUnitPrice ?? null
      };
    })
  };
}

/**
 * Gerbang DINI kuota kampanye (mig 6020) — dijalankan SEBELUM satu baris pun
 * ditulis, untuk baris yang memang memakai kampanye.
 *
 * Penegak sebenarnya adalah trigger `enforce_promo_quota`: ia memegang lock
 * baris kampanye, jadi hanya ia yang benar bila dua pembeli berebut sisa
 * terakhir. Gunanya pemeriksaan ini adalah PESAN dan KEUTUHAN — pada checkout
 * tanpa voucher, `order_items` ditulis satu `insert` per pesanan, dan galat
 * trigger di tengahnya meninggalkan pesanan yang harus dibersihkan. Menolak
 * lebih awal membuat pembeli tahu baris mana yang melampaui batas tanpa
 * pesanan yatim sama sekali.
 *
 * FAIL-OPEN atas kegagalan pemanggilan: RPC-nya bisa gagal (jaringan, sesi
 * anon yang tak berhak) dan menutup checkout karena itu akan menghalangi
 * pembelian yang sah. Yang hilang hanyalah pesan yang lebih baik — triggernya
 * tetap menjaga angkanya.
 */
async function assertPromoQuotaAvailable(
  db: NonNullable<ReturnType<typeof createBrowserSupabase>>,
  lines: readonly { qty: number; promoCampaignId: string | null }[]
): Promise<void> {
  const wanted = new Map<string, number>();
  for (const l of lines) {
    if (!l.promoCampaignId || l.qty <= 0) continue;
    wanted.set(l.promoCampaignId, (wanted.get(l.promoCampaignId) ?? 0) + l.qty);
  }
  if (!wanted.size) return;

  for (const [campaignId, qty] of wanted) {
    let row: { remaining_total: number | null; remaining_for_me: number | null } | null = null;
    try {
      const { data, error } = await db.rpc("promo_campaign_my_quota", { p_campaign: campaignId });
      if (error) throw new Error(error.message);
      row = Array.isArray(data) ? (data[0] ?? null) : (data ?? null);
    } catch (err) {
      console.warn("[promo:quota-check-failed] kuota tak terbaca — diserahkan ke trigger", campaignId, err);
      continue;
    }
    if (!row) continue;

    const total = row.remaining_total;
    if (typeof total === "number" && total < qty) {
      throw new Error(
        total <= 0
          ? "Kuota promo untuk produk ini sudah habis. Muat ulang halaman — harganya kembali ke harga normal."
          : `Kuota promo tersisa ${total} pcs, sedangkan keranjang meminta ${qty} pcs. Kurangi jumlahnya.`
      );
    }
    const mine = row.remaining_for_me;
    if (typeof mine === "number" && mine < qty) {
      throw new Error(
        mine <= 0
          ? "Jatah promo ini sudah kamu pakai semua. Checkout lagi untuk memesan dengan harga normal."
          : `Jatah promo kamu tinggal ${mine} pcs, sedangkan keranjang meminta ${qty} pcs. Kurangi jumlahnya.`
      );
    }
  }
}

/**
 * Terjemahkan error insert order_items menjadi pesan yang bisa ditindaklanjuti
 * pembeli. Trigger `ensure_order_item_same_product` masih bisa RAISE lewat
 * jalur fallback (daftar varian gagal dimuat → id basi terkirim apa adanya);
 * pesan mentahnya berbahasa Inggris dan tidak menjelaskan harus apa.
 */
export function friendlyOrderItemInsertError(message: string): string {
  // Kuota PO habis (trigger `enforce_preorder_quota`, mig 3970). Pesannya sudah
  // berbahasa Indonesia dan menyebut sisa pcs, tapi ia tidak mengatakan harus
  // apa — dan keranjang di localStorage bisa membawa qty yang disusun sebelum
  // varian itu penuh, jadi pembeli perlu diarahkan.
  if (/Kuota PO varian/i.test(message)) {
    return `${message}. Kurangi jumlahnya atau pilih varian lain.`;
  }
  // Kuota kampanye promo (trigger `enforce_promo_quota`, mig 6020). Prefiks
  // `PROMO_QUOTA:<jenis>` adalah KODE untuk klien, bukan kalimat untuk pembeli —
  // ia dibuang di sini dan sisanya sudah berbahasa Indonesia + menyebut angka.
  // Dua jenis dibedakan karena tindakannya berbeda: kuota total habis berarti
  // promonya memang sudah tak tersedia, batas per pembeli berarti pembeli ini
  // sudah memakai jatahnya.
  const quota = /^PROMO_QUOTA:(total|per_customer)\s+([\s\S]*)$/i.exec(message.trim());
  if (quota) {
    const inti = quota[2].trim();
    return quota[1].toLowerCase() === "total"
      ? `Kuota promo habis — ${inti}. Kurangi jumlahnya; sisanya tetap bisa dipesan dengan harga normal.`
      : `Batas promo per pembeli tercapai — ${inti}. Kurangi jumlahnya untuk melanjutkan.`;
  }
  if (/variation must belong|order item product must match/i.test(message)) {
    // KALIMATNYA SENGAJA TIDAK menyuruh "tambahkan lagi" (bughunt ketiga,
    // 2026-09-30): sebab tersering di PROD adalah produk/varian yang sudah
    // DINONAKTIFKAN (stok habis) — RLS pembeli menyembunyikan variannya, jadi
    // menambahkannya lagi tak akan pernah berhasil, dan pembeli yang menuruti
    // saran lama memesan ulang produk LAIN yang sebenarnya sudah masuk.
    return (
      "Variasi di keranjang sudah tidak tersedia (produk dinonaktifkan, stoknya " +
      "habis, atau baru diperbarui toko). Hapus produk ini dari keranjang; bila " +
      "masih dijual, pilih ulang variasinya dari halaman produk."
    );
  }
  // Gerbang fail-closed `set_ready_stock_keep` (mig 4940, bughunt C1-4): baris
  // ready stock tanpa `variant_id` dulu LOLOS — keep terpasang tapi stok tak
  // pernah dipotong (oversell senyap). Yang membawanya ke sini adalah jalur
  // fallback `buildCheckoutLineResolver` saat daftar varian gagal dimuat dan
  // keranjang membawa `variationId` non-UUID; obatnya sama: muat ulang.
  if (/wajib menyebut varian/i.test(message)) {
    return (
      "Variasi produk ini tidak terbaca dari keranjang (kemungkinan keranjang lama). " +
      "Muat ulang halaman produk lalu pilih ulang variasinya."
    );
  }
  return message;
}

/**
 * Gerbang DINI checkout multi-produk (bughunt ketiga, 2026-09-30): SEMUA produk
 * keranjang dibaca SEKALI, sebelum satu pesanan pun dibuat. Produk yang tak
 * terbaca, nonaktif, atau tak dibuka untuk pemesanan → melempar kalimat yang
 * menyebut produk mana yang harus dihapus. Gagal membaca = melempar juga
 * (fail-closed): lebih baik checkout ditunda daripada sukses sebagian.
 */
async function assertCartsOrderable(
  db: NonNullable<ReturnType<typeof createBrowserSupabase>>,
  carts: readonly { productId: string; productName: string }[]
): Promise<void> {
  const valid = carts.filter((c) => isUuid(c.productId));
  const ids = Array.from(new Set(valid.map((c) => c.productId)));
  if (ids.length === 0) return;
  const { data, error } = await db
    .from("products")
    .select("id, name, status, product_type, is_active")
    .in("id", ids);
  if (error) {
    throw new Error("Gagal memeriksa produk di keranjang. Periksa koneksi lalu coba lagi.");
  }
  const message = unorderableCartsMessage(
    findUnorderableCarts(valid, (data ?? []) as CheckoutProductRow[])
  );
  if (message) throw new Error(message);
}

export async function createCheckoutOrder(payload: CheckoutPayload) {
  const db = createBrowserSupabase();

  if (!db || !isUuid(payload.cart.productId)) {
    return null;
  }

  // Cegah checkout keranjang basi: pastikan produk masih boleh dipesan SAAT INI
  // (bukan status yang tersimpan di keranjang). Mencerminkan trigger DB
  // enforce_orderable_product_on_order (mig 067) — ini untuk pesan error yang
  // ramah; DB tetap penjaga otoritatif bila status berubah saat race.
  const { data: productStatusRow, error: productStatusError } = await db
    .from("products")
    .select("status, product_type, is_active, name")
    .eq("id", payload.cart.productId)
    .single();

  if (productStatusError || !productStatusRow) {
    throw new Error(
      "Produk ini sudah tidak tersedia. Hapus dari keranjang untuk melanjutkan."
    );
  }

  // Produk NONAKTIF (mis. dinonaktifkan otomatis saat stok habis) tetap
  // terbaca oleh pembeli yang pernah memesannya (RLS "Customers read ordered
  // products"), tapi variannya TIDAK — tanpa gerbang ini pesanan sempat dibuat
  // lalu insert item gagal (bughunt ketiga, 2026-09-30).
  if (productStatusRow.is_active === false) {
    throw new Error(
      `Produk "${productStatusRow.name ?? payload.cart.productName}" sudah tidak dijual ` +
        "(stok habis atau dinonaktifkan toko). Hapus dari keranjang untuk melanjutkan."
    );
  }

  if (!isOrderableStatus(productStatusRow.status, productStatusRow.product_type)) {
    throw new Error(
      "PO untuk produk ini sudah ditutup. Pesanan tidak dapat dibuat."
    );
  }

  const {
    data: { user }
  } = await db.auth.getUser();

  // Alur baru: alamat dikumpulkan saat request kirim, bukan saat order, jadi
  // payload.address bisa kosong. Jangan timpa default_address tersimpan dengan
  // string kosong — hanya set bila alamat benar-benar diisi.
  const trimmedAddress = payload.address?.trim() ?? "";
  const customerRow: {
    auth_user_id: string | null;
    full_name: string;
    phone: string;
    default_address?: string;
  } = {
    auth_user_id: user?.id ?? null,
    full_name: payload.customerName,
    phone: payload.phone
  };
  if (trimmedAddress) {
    customerRow.default_address = trimmedAddress;
  }

  const { data: customer, error: customerError } = await db
    .from("customers")
    .upsert(customerRow, { onConflict: "store_id,phone" })
    .select("id")
    .single();

  if (customerError || !customer) {
    throw new Error(
      customerError ? terjemahkanErrorRpc(customerError).pesan : "Gagal menyimpan customer."
    );
  }

  // Snapshot harga jual OTORITATIF saat checkout via pricing.ts memakai grup
  // pembeli SAAT INI (tidak bergantung pada unitPrice keranjang yang bisa basi
  // atau belum terhitung — mis. grup belum termuat). Harga menyusul → null.
  // Gagal memuat varian/grup → checkout dihentikan, bukan harga keranjang
  // (bughunt D16-16). Sekaligus me-remap
  // variant_id basi (keranjang localStorage vs varian yang dibuat ulang admin)
  // ke id terkini — lihat buildCheckoutLineResolver.
  const resolveCheckoutLine = await buildCheckoutLineResolver(
    db,
    payload.cart.productId,
    customer.id
  );

  // Resolve SEMUA baris sebelum menulis apa pun ke DB: varian yang sudah hilang
  // melempar error ramah di sini — tidak meninggalkan order yatim.
  const resolvedLines = await Promise.all(
    payload.cart.lines.map(async (line) => ({
      line,
      resolved: await resolveCheckoutLine(line)
    }))
  );

  // Kuota kampanye (mig 6020) diperiksa SEBELUM pesanan dibuat — lihat
  // `assertPromoQuotaAvailable`. Triggernya tetap yang menentukan.
  await assertPromoQuotaAvailable(
    db,
    resolvedLines.map(({ line, resolved }) => ({
      qty: line.qty,
      promoCampaignId: resolved.promoCampaignId
    }))
  );

  // Cegah order GANDA untuk produk preorder ("1 order = 1 produk per pembeli",
  // lihat CLAUDE.md). Bila pelanggan SUDAH punya order aktif untuk produk ini
  // (mis. checkout lagi setelah memesan), gabungkan item ke order tersebut alih-
  // alih membuat order kedua — order terpecah membuat Detail PO / keranjang
  // menampilkan jumlah yang membingungkan (bug "jumlah ≠ yang dipesan"). Hanya
  // untuk preorder; item ready_stock sengaja tetap order baru agar stok &
  // tenggat keep per baris akurat. RLS pelanggan hanya mengizinkan INSERT
  // order_items (bukan UPDATE), jadi gabung = INSERT baris baru; tampilan admin
  // (rekap & Kelola Pesanan) sudah menggabung per varian.
  if (productStatusRow.product_type === "preorder") {
    const { data: existingOrder } = await db
      .from("orders")
      .select("id, order_code")
      .eq("product_id", payload.cart.productId)
      .eq("customer_id", customer.id)
      .neq("status", "Batal")
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (existingOrder) {
      const existing = existingOrder as { id: string; order_code: string };
      const mergeRows = resolvedLines.map(({ line, resolved }) => ({
        order_id: existing.id,
        product_id: payload.cart.productId,
        variant_id: resolved.variantId,
        variant_name: resolved.variantName,
        qty: line.qty,
        unit_price: resolved.unitPrice,
        unit_price_extra: resolved.priceExtra,
        promo_campaign_id: resolved.promoCampaignId,
        promo_campaign_name: resolved.promoCampaignName,
        // Jejak potongan (promo/level) — satu pembuat dengan checkout ber-voucher.
        ...discountTracePayload(resolved.cuts),
        item_note: line.note?.trim().slice(0, 20) || null
      }));
      const { error: mergeError } = await db
        .from("order_items")
        .insert(mergeRows);
      if (mergeError) {
        throw new Error(friendlyOrderItemInsertError(mergeError.message));
      }
      return existing.order_code;
    }
  }

  const { data: order, error: orderError } = await db
    .from("orders")
    .insert({
      customer_id: customer.id,
      product_id: payload.cart.productId,
      customer_name: payload.customerName,
      phone: payload.phone,
      address: trimmedAddress || null,
      note: payload.note,
      status: "Menunggu",
      distribution_status: "Menunggu"
    })
    .select("id, order_code")
    .single();

  if (orderError || !order) {
    throw new Error(orderError ? terjemahkanErrorRpc(orderError).pesan : "Gagal membuat order.");
  }

  const itemRows = resolvedLines.map(({ line, resolved }) => ({
    order_id: order.id,
    product_id: payload.cart.productId,
    variant_id: resolved.variantId,
    variant_name: resolved.variantName,
    qty: line.qty,
    unit_price: resolved.unitPrice,
    unit_price_extra: resolved.priceExtra,
    promo_campaign_id: resolved.promoCampaignId,
    promo_campaign_name: resolved.promoCampaignName,
    ...discountTracePayload(resolved.cuts),
    item_note: line.note?.trim().slice(0, 20) || null
  }));

  const { error: itemsError } = await db.from("order_items").insert(itemRows);

  if (itemsError) {
    // Hard-block stok ready_stock terjadi DI SINI: trigger set_ready_stock_keep
    // (mig 062) men-debit stok atomik saat INSERT dan RAISE bila stok kurang.
    // (RPC reserve_ready_stock lama dijadikan no-op di mig 070 karena membuat
    // dobel-debit.) Bersihkan order yang terlanjur dibuat (best-effort).
    //
    // ⚠️ Lewat RPC, bukan `db.from("orders").delete()` (bughunt G5-12).
    // `public.orders` tidak punya SATU PUN policy DELETE — RLS-nya cuma
    // create/read pembeli + update admin — jadi pembersihan langsung dari
    // browser selalu no-op yang MEMULANGKAN SUKSES: nol baris terpengaruh, nol
    // error. PROD 2026-09-07 menyimpan 57 order tanpa satu pun `order_items`
    // sebagai sisanya. RPC `delete_empty_checkout_order` (mig 5440) bergerbang
    // sempit: order KOSONG, milik pemanggil, < 15 menit, belum tersentuh uang.
    //
    // Tetap best-effort: kegagalan membersihkan tidak boleh menutupi error
    // ASLI (stok kurang) yang justru perlu dibaca pembeli.
    await db.rpc("delete_empty_checkout_order", { p_order_id: order.id });
    throw new Error(friendlyOrderItemInsertError(itemsError.message));
  }

  if (payload.cart.dpRequired) {
    // DP via NOTA (mig 098): nota DP TIDAK lagi terbit saat checkout. Karena
    // produk pre-order hanya bisa dipesan saat OPEN PO, nota DP baru
    // diterbitkan otomatis ketika PO ditutup (status produk → CLOSED PO) lewat
    // trigger issue_dp_invoices_on_po_close. Pembeli membayar DP di tab Tagihan
    // setelah PO ditutup. Tidak ada lagi baris payments DP maupun upload bukti
    // saat checkout.
    return order.order_code as string;
  }

  const { data: payment, error: paymentError } = await db
    .from("payments")
    .insert({
      order_id: order.id,
      // Tanpa DP → catat subtotal sebagai tagihan awal (riwayat lama).
      // (Cabang DP sudah return lebih awal: nota DP terbit via issue_dp_invoice.)
      amount: payload.subtotal ?? 0,
      status: "Menunggu",
      method: payload.paymentProof ? "Transfer" : null
    })
    .select("id")
    .single();

  if (paymentError) {
    throw new Error(terjemahkanErrorRpc(paymentError).pesan);
  }

  if (payment && payload.paymentProof) {
    // upsert WAJIB false: tidak ada policy UPDATE di storage.objects untuk
    // bucket payment-proofs — `upsert: true` (INSERT .. ON CONFLICT DO UPDATE)
    // ditolak RLS meski tanpa konflik. Nama file unik via Date.now().
    const proofPath = await withStorePrefix(
      db,
      `payments/${order.id}-${Date.now()}-${payload.paymentProof.name}`
    );
    const { error: proofError } = await db.storage
      .from("payment-proofs")
      .upload(proofPath, payload.paymentProof, { upsert: false });

    if (proofError) {
      throw new Error(terjemahkanErrorRpc(proofError).pesan);
    }

    // Bucket payment-proofs privat (mig 035): simpan STORAGE PATH, bukan public URL.
    // Admin membaca bukti via signed URL bila perlu.
    await db
      .from("payments")
      .update({ proof_url: proofPath })
      .eq("id", payment.id);
  }

  return order.order_code as string;
}

export type MultiCheckoutResult = {
  succeeded: { productId: string; productName: string; orderCode: string }[];
  failed: { productId: string; productName: string; error: string }[];
};

/**
 * Checkout keranjang MULTI-PRODUK: buat satu order TERPISAH per produk (tetap
 * 1 order = 1 produk di DB). Best-effort — produk yang gagal (mis. stok ready
 * kurang / PO sudah ditutup) dicatat di `failed`, produk lain tetap dibuat.
 * Tiap `createCheckoutOrder` sudah self-cleanup order yatim-nya bila insert item
 * gagal, jadi kegagalan satu produk tidak meninggalkan sampah.
 */
export async function createCheckoutOrders(payload: {
  carts: CartState[];
  customerName: string;
  phone: string;
  address: string;
  note?: string;
  paymentProof?: File | null;
}): Promise<MultiCheckoutResult> {
  const result: MultiCheckoutResult = { succeeded: [], failed: [] };

  // Gerbang DINI: produk nonaktif / tak dibuka menghentikan SELURUH checkout
  // sebelum satu pesanan pun dibuat — tak ada lagi sukses sebagian karena
  // sebab ini (lihat `checkoutGuard.ts`).
  const guardDb = createBrowserSupabase();
  if (guardDb) await assertCartsOrderable(guardDb, payload.carts);

  for (const cart of payload.carts) {
    try {
      const orderCode = await createCheckoutOrder({
        cart,
        customerName: payload.customerName,
        phone: payload.phone,
        address: payload.address,
        note: payload.note,
        // Subtotal PER PRODUK (bukan agregat) — dipakai baris payments bookkeeping.
        subtotal: cartSubtotal([cart]),
        paymentProof: payload.paymentProof ?? null,
      });
      if (!orderCode) {
        result.failed.push({
          productId: cart.productId,
          productName: cart.productName,
          error: "Produk tidak valid atau koneksi gagal.",
        });
        continue;
      }
      result.succeeded.push({
        productId: cart.productId,
        productName: cart.productName,
        orderCode,
      });
    } catch (error) {
      result.failed.push({
        productId: cart.productId,
        productName: cart.productName,
        error: error instanceof Error ? error.message : "Gagal membuat pesanan.",
      });
    }
  }

  return result;
}

/**
 * Map satu baris orders (select CUSTOMER_ORDERS_BOARD_SELECT) → LocalOrder.
 *
 * CATATAN PERFORMA (audit 2026-07): daftar pesanan TIDAK lagi meng-embed
 * `product_variants(*)` — produk ber-variasi ribuan (mis. 1.560 varian) membuat
 * payload /orders membengkak ~2 MB per pelanggan. Daftar varian untuk modal
 * Edit Pesanan dimuat LAZY oleh EditModal via `getPublicProductBySlug` (sudah
 * terpaginasi `fetchAllProductVariants` + kolom ramping, tahan >1000 varian);
 * `availableVariations` di sini sengaja dibiarkan kosong (fallback modal
 * memakai baris pesanan sendiri).
 */
function mapBoardOrderRow(order: any): LocalOrder {
  const items = order.order_items ?? [];
  const totalQty = items.reduce((sum: number, item: any) => sum + item.qty, 0);
  const voucherName = liveVoucherName(items);
  // URL dari project AKTIF via storage_path (mig 2300); fallback media_url.
  const productImage = pickProductCoverUrl(order.products?.product_media) ?? "";

  return {
    id: order.order_code ?? order.id,
    remoteId: order.id,
    productId: order.products?.id,
    productSlug: order.products?.slug,
    productName: order.products?.name ?? "Produk",
    productImage,
    warehouseId:
      typeof order.products?.warehouse_id === "string" && order.products.warehouse_id !== ""
        ? (order.products.warehouse_id as string)
        : null,
    productStatus: order.products?.status,
    // is_manual terbaca anon & authenticated (dicek PROD 2026-09-29) — tambahan
    // kolom ini tak membuat PostgREST menolak query board (bughunt D8-18).
    isManual: order.products?.is_manual === true,
    preorderDeadline: order.products?.preorder_deadline ?? undefined,
    productNote: order.product_note?.slice(0, 10) ?? undefined,
    customerName: order.customer_name,
    phone: order.phone,
    address: order.address,
    note: order.note,
    status: order.status,
    distributionStatus: order.distribution_status,
    estimatedReady: order.products?.estimated_ready ?? "sesuai update admin",
    price: order.products?.price_normal ?? undefined,
    totalQty,
    totalPrice: items.reduce(
      (sum: number, item: any) => sum + (item.unit_price ?? 0) * item.qty,
      0
    ),
    createdAt: order.created_at,
    // Pesanan ber-voucher (mig 5610). Sejak mig 6460 pembeli tetap bisa Edit
    // Pesanan (voucher dihitung ulang server); nama vouchernya disebut modal.
    // Baris yang sudah DIBATALKAN tidak dihitung — cermin gerbang server
    // (`keep_status <> 'cancelled'`, audit 2026-09-10).
    hasVoucher: voucherName !== null,
    voucherName: voucherName ?? undefined,
    // Harga diatur admin (mig 7060) → Edit Pesanan terkunci (gerbang HARGA_MANUAL).
    hasManualPrice: orderHasManualPrice(items),
    // Baris bundel hidup (mig 8040) → Edit Pesanan terkunci (gerbang BUNDEL).
    hasBundle: orderHasLiveBundle(items),
    lines: items.map((item: any) => ({
      variationId: item.variant_id ?? item.variant_name,
      variationName: item.variant_name,
      unitPrice: item.unit_price ?? undefined,
      qty: item.qty,
      note: item.item_note ?? undefined
    })),
    // Modal Edit Pesanan memuat HANYA baris yang ditulis ulang RPC — baris
    // batal yang ikut terkirim dulu hidup lagi sebagai pcs tertagih.
    editLines: orderEditLinesFromItems(items)
  };
}

export async function updateCustomerOpenPreorderOrderInSupabase(
  order: LocalOrder,
  lines: CartState["lines"]
) {
  const db = createBrowserSupabase();
  // Lebur per varian + CATATAN (`orderEditPayloadLines`) — dulu per varian saja,
  // dan catatan baris kembar yang kalah hilang permanen di setiap Simpan.
  const targetLines = orderEditPayloadLines(lines);

  if (!db || !targetLines.length) {
    return false;
  }

  // Re-snapshot harga satuan via pricing.ts memakai grup pembeli SAAT INI.
  // Selama OPEN PO harga belum dikomit — ini sekaligus mengisi unit_price yang
  // masih NULL begitu admin memasukkan harga, dan menyinkron diskon grup. Bila
  // varian/grup gagal dimuat, kirim TANPA key unitPrice → RPC (mig 128) memakai
  // snapshot lama varian yang sama; varian BARU (tanpa snapshot) membuat Simpan
  // DITOLAK, bukan jatuh ke price_normal (bughunt D13-3).
  //
  // B6 (2026-09-17): dulu `resolveSnapshotUnitPrice(…, group)` — TANPA promo —
  // sehingga pembeli yang mengubah jumlah pesanan promonya justru ditagih
  // harga level (63.000 → 70.000) dan jejak kampanye/potongan hilang. Kini
  // rumus & jejaknya SAMA dengan checkout (`resolveSelectionPrice` + kampanye
  // via `loadPromosForCheckout`), dan RPC `update_open_preorder_order_items`
  // (mig 5990) menulis jejak dari muatan ini. Varian yang tak teridentifikasi
  // di konteks → key `unitPrice` TIDAK dikirim (RPC mempertahankan snapshot
  // lama), bukan `null` yang menghapus harganya (kelas bughunt E2-01).
  //
  // mig 6460: muatan juga membawa harga umum + tier (bahan mesin voucher,
  // `orderEditPricedLine`) — pesanan ber-voucher dihitung ulang server dengan
  // rumus checkout. Baris tanpa kunci harga pada pesanan ber-voucher DITOLAK
  // server ("muat ulang halaman"), tak pernah diam-diam kehilangan potongan.
  let payloadLines: Array<Record<string, unknown>> = targetLines;
  let priceContextError: unknown = null;
  try {
    if (order.productId) {
      const productId = order.productId;
      const [variants, myGroup, promos, productPriceNormal, priceAxis] = await Promise.all([
        fetchProductVariantPricing(db, productId),
        // Diskon merek per level (mig 7070) lewat pemuat ber-cadangan checkout
        // (pola A5, bughunt D13-3): galat RPC aturan → aturan yang sedang
        // dipajang storefront pembeli ini, bukan "tanpa harga".
        fetchMyCustomerGroup(db, {
          rulesFor: (customerId) => loadBrandRulesForCheckout(db, customerId)
        }),
        loadPromosForCheckout(productId),
        fetchProductPriceNormal(db, productId).catch(() => undefined),
        // SUMBU HARGA + warisan slot — SAMA dengan resolver checkout (bughunt
        // D15-33): tanpanya baris bersumbu yang dihargai checkout lewat warisan
        // tertulis ulang harga menyusul, atau meminjam harga sumbu lain. Pemuat
        // KETAT: gagal = konteks harga gagal utuh (snapshot lama), bukan null.
        loadPriceAxisForOrderEdit(productId)
      ]);
      // Merek produk HANYA dibaca bila level pembeli punya aturan — pelanggan
      // umum & level tanpa aturan tak mendapat mode gagal baru. Pemuatnya
      // ber-cadangan (dua kali, lalu tanpa aturan merek + peringatan).
      const productMeta = myGroup?.brandDiscounts?.size
        ? await loadProductBrandForCheckout(db, productId)
        : null;
      const group = levelForProduct(myGroup, productMeta);
      if (variants.length) {
        const inheritedCache = new Map<
          string,
          Awaited<ReturnType<typeof resolvePublicInheritedPricing>>
        >();
        const priced: Array<Record<string, unknown>> = [];
        for (const l of targetLines) {
          const match = matchVariant(
            {
              variantId: isUuid(l.variationId) ? l.variationId : null,
              variantName: l.variationName
            },
            variants
          );
          if (!match) {
            priced.push(l);
            continue;
          }
          let inherited: Awaited<ReturnType<typeof resolvePublicInheritedPricing>> | undefined;
          if (orderEditNeedsInherited(match.pricing, priceAxis)) {
            if (!inheritedCache.has(match.variantId)) {
              inheritedCache.set(
                match.variantId,
                await loadInheritedPricingForOrderEdit(productId, match.pricing.optionIds ?? [])
              );
            }
            inherited = inheritedCache.get(match.variantId);
          }
          priced.push(
            orderEditPricedLine(
              l,
              orderEditSelectionPrice(
                match.pricing,
                {
                  variations: variants.map((v) => v.pricing),
                  priceNormal: productPriceNormal,
                  priceAxis,
                  inherited
                },
                group,
                promos
              )
            )
          );
        }
        payloadLines = priced;
      }
    }
  } catch (err) {
    // Kirim tanpa unitPrice — server mempertahankan snapshot lama varian yang
    // sama. Varian BARU tak punya snapshot: dicegat di bawah.
    priceContextError = err;
  }

  // Bughunt D13-3: varian baru tanpa harga → server memakai `price_normal`
  // (harga UMUM, tanpa diskon level/merek/promo) dan pembeli kelebihan tagih
  // tanpa tanda. Tolak Simpan; pembeli tinggal mencoba lagi.
  const unpriced = orderEditUnpricedNewVariants(payloadLines, order.lines ?? []);
  if (unpriced.length) {
    console.warn("[edit-pesanan:harga-gagal] varian baru tanpa harga", unpriced, priceContextError);
    throw new Error(
      "Harga untuk variasi yang baru ditambahkan gagal dimuat. Periksa koneksi, lalu tekan Simpan lagi."
    );
  }

  const { error } = await db.rpc("update_open_preorder_order_items", {
    target_order_id: isUuid(order.remoteId ?? "") ? order.remoteId : null,
    target_order_code: order.id,
    target_lines: payloadLines,
    target_product_note: null
  });

  if (error) {
    throw new Error(orderEditErrorMessage(error));
  }

  return true;
}

/**
 * Update catatan (item_note) per-varian untuk order — tanpa cek OPEN PO.
 * Bisa dipakai admin atau pemilik order, hanya mengubah kolom item_note,
 * tidak menyentuh qty/harga.
 */
export async function updateOrderItemNotesInSupabase(
  orderId: string,
  lines: Array<{ variationId: string; note: string | null }>
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase client unavailable");

  const { error } = await db.rpc("update_order_item_notes", {
    target_order_id: orderId,
    target_lines: lines.map((l) => ({
      variationId: l.variationId,
      note: l.note?.trim().slice(0, 20) || null
    }))
  });

  if (error) throw new Error(error.message);
}

/**
 * Fetch individual order_items with customer name + note untuk keperluan
 * admin edit catatan di Closed POPanel.
 */
export async function fetchOrderItemNotesForAdmin(
  productId: string,
  orderStatuses?: string[]
): Promise<
  Array<{
    orderItemId: string;
    orderId: string;
    orderCode: string;
    customerName: string;
    variantName: string;
    qty: number;
    note: string | null;
  }>
> {
  const db = createBrowserSupabase();
  if (!db) return [];

  let query = db
    .from("order_items")
    .select(
      "id, variant_name, qty, item_note, order_id, orders!inner(order_code, customer_name, status)"
    )
    .eq("orders.product_id", productId);

  if (orderStatuses?.length) {
    query = query.in("orders.status", orderStatuses);
  } else {
    query = query.neq("orders.status", "Batal");
  }

  query = query.order("variant_name", { ascending: true });

  const { data, error } = await query;
  if (error || !data) return [];

  return (data as any[]).map((i) => ({
    orderItemId: i.id,
    orderId: i.order_id,
    orderCode: i.orders?.order_code ?? "",
    customerName: i.orders?.customer_name ?? "—",
    variantName: i.variant_name,
    qty: i.qty,
    note: i.item_note ?? null
  }));
}

// ─── Kelola pesanan CLOSED PO (per-pembeli + manual) ─────────────────────────

/** Varian produk lengkap dengan field harga, untuk hitung harga klien-side. */
export type ClosedPOEditVariant = {
  variantId: string;
  variantName: string;
  sortOrder: number;
  isActive: boolean;
  /** Bentuk yang dipahami getVariantPrice (pricing.ts). */
  pricing: import("@/lib/products").ProductVariation;
};

export type ClosedPOEditGroup = import("@/lib/reseller-groups").ResellerGroup;

/**
 * Kolom varian yang dibutuhkan editor pesanan. SATU sumber untuk pembacaan
 * massal (`fetchClosedPOEditContext`) dan pembacaan satu baris
 * (`fetchOneEditVariant`) — kolom diskon yang terlewat di salah satunya berarti
 * harga yang dihitung layar berbeda dari harga yang tersimpan.
 */
// `stock` (bughunt ketiga, 2026-09-30): modal Tambah Produk di Keranjang
// Pelanggan dulu tak tahu stok ready stock — admin baru tahu varian habis dari
// penolakan `admin_create_order` ("Stok tidak cukup … sisa 0 pcs"), 5× di PROD
// sehari, 3× berturut-turut untuk varian yang sama.
const EDIT_VARIANT_COLUMNS =
  "id, name, sku, option_ids, sort_order, is_active, price_normal, price_extra, diskon_mode, diskon_reseller, diskon_agen, diskon_slot3, diskon_slot4, diskon_slot5, diskon_slot6, diskon_slot7, diskon_slot8, diskon_slot9, diskon_slot10, diskon_slot11, diskon_slot12, diskon_slot13, diskon_slot14, diskon_slot15, stock";

/** Baris `product_variants` → bentuk yang dipahami editor + `getVariantPrice`. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapEditVariantRow(v: Record<string, any>): ClosedPOEditVariant {
  return {
    variantId: v.id as string,
    variantName: v.name as string,
    sortOrder: v.sort_order ?? 0,
    isActive: v.is_active !== false,
    pricing: {
      id: v.id,
      name: v.name,
      sku: v.sku ?? v.name,
      optionIds: (v.option_ids as string[] | null) ?? [],
      price_normal: v.price_normal ?? undefined,
      price_extra: v.price_extra ?? undefined,
      diskonMode:
        v.diskon_mode === "nominal" || v.diskon_mode === "persen"
          ? v.diskon_mode
          : undefined,
      diskonReseller: v.diskon_reseller ?? undefined,
      diskonAgen: v.diskon_agen ?? undefined,
      diskonSlot3: v.diskon_slot3 ?? undefined,
      diskonSlot4: v.diskon_slot4 ?? undefined,
      diskonSlot5: v.diskon_slot5 ?? undefined,
      diskonSlot6: v.diskon_slot6 ?? undefined,
      diskonSlot7: v.diskon_slot7 ?? undefined,
      diskonSlot8: v.diskon_slot8 ?? undefined,
      diskonSlot9: v.diskon_slot9 ?? undefined,
      diskonSlot10: v.diskon_slot10 ?? undefined,
      diskonSlot11: v.diskon_slot11 ?? undefined,
      diskonSlot12: v.diskon_slot12 ?? undefined,
      diskonSlot13: v.diskon_slot13 ?? undefined,
      diskonSlot14: v.diskon_slot14 ?? undefined,
      diskonSlot15: v.diskon_slot15 ?? undefined,
      // Stok hidup varian — hanya bermakna untuk ready stock (preorder tak
      // melacak stok; editor mengabaikannya bila `stockTracked` mati).
      stockQty: v.stock != null ? Number(v.stock) : undefined
    }
  };
}

/**
 * Satu baris varian, dibaca SESUDAH `resolve_or_create_preorder_variant`
 * membuatnya (materialisasi malas A — keranjang admin, `adminPickerIsLazy`).
 *
 * ⚠️ Ini kolom uang, dan barisnya TIDAK lahir kosong: RPC itu mewarisi
 * `price_normal` + seluruh slot diskon (15 sejak mig 7300) lewat `preorder_inherited_pricing`
 * (mig 3640/4640). Menebak baris barunya di klien — mis. menyusunnya dari
 * `option_ids` dengan harga `undefined` — akan membuat `buildAdminOrderLines`
 * menghitung `unitPrice: null` lalu menyimpan "harga menyusul" untuk kombinasi
 * yang sebenarnya SUDAH berharga. Karena itu harganya dibaca kembali dari baris
 * yang benar-benar tertulis, bukan diasumsikan.
 *
 * MELEMPAR bila query-nya gagal (idiom fail-closed `fetchClosedPOEditContext`):
 * `null` di sini hanya berarti "barisnya tak terbaca", dan pemanggil menahan
 * penambahan alih-alih menyimpan harga yang tak pernah ia lihat.
 */
export async function fetchOneEditVariant(
  variantId: string
): Promise<ClosedPOEditVariant | null> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const { data, error } = await db
    .from("product_variants")
    .select(EDIT_VARIANT_COLUMNS)
    .eq("id", variantId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return mapEditVariantRow(data as Record<string, any>);
}

/**
 * Konteks untuk editor kelola pesanan: daftar varian (dengan harga) + grup
 * reseller + struktur grup variasi (Warna/Ukuran dst). Dipakai untuk menghitung
 * harga otomatis per pembeli (grup + override varian) memakai getVariantPrice,
 * dan untuk picker varian 3-level (VariantGroupSelector) ala halaman produk.
 */
export async function fetchClosedPOEditContext(productId: string): Promise<{
  variants: ClosedPOEditVariant[];
  groups: ClosedPOEditGroup[];
  variantGroups: import("@/lib/products").VariantGroup[];
  /**
   * Tipe produk — dibutuhkan picker untuk memutuskan boleh-tidaknya
   * materialisasi kombinasi malas (`variantPickerIsLazy`). `undefined` = tak
   * terbaca, dan itu FAIL-CLOSED ke perilaku lama: picker tidak pernah
   * memanggil RPC materialisasi untuk produk yang tipenya tak ia ketahui.
   */
  productType?: "preorder" | "ready_stock";
  /**
   * `option_ids` kombinasi yang DIMATIKAN admin (`is_active = false`).
   * `variants` di atas sengaja membuangnya, jadi tanpa daftar ini picker mode
   * malas menganggapnya "belum bermateri" dan membiarkannya diketuk — lalu
   * RPC materialisasi menolaknya. Lihat `blockedCombosFromRows`.
   */
  blockedOptionCombos: string[][];
  /**
   * Mode daftar kombinasi (B1, `products.absent_combos_sellable`) + PO
   * terbatas — dibaca picker lewat `pickerIsLazy`. `undefined`/`null` = aturan
   * lama (dihitung dari jumlah kombinasi).
   */
  absentCombosSellable?: boolean | null;
  poLimited?: boolean;
}> {
  const db = createBrowserSupabase();
  if (!db) return { variants: [], groups: [], variantGroups: [], blockedOptionCombos: [] };

  // Varian diambil TERPAGINASI: produk >1000 kombinasi (batas PostgREST
  // max-rows) tak boleh terpotong, kalau tidak admin tak bisa memilih sebagian
  // varian saat menambah/mengedit pesanan.
  //
  // ⚠️ FAIL-CLOSED, dan ini kolom uang (bughunt E2-01). Sampai 2026-09-07
  // kegagalan fetch varian ditelan jadi `variants: []` (`.catch(() => [])`) dan
  // `groupRes.error` tak pernah dibaca — konteks kosong yang TAK TERBEDAKAN
  // dari "produk ini memang tak punya varian". Layar edit tetap hidup di atas
  // konteks itu, dan setiap baris yang disimpan lalu menghitung harganya dari
  // daftar kosong → `unitPrice: null` → harga SELURUH baris pesanan itu
  // terhapus jadi "harga menyusul". Yang berada di jangkauan keadaan itu saat
  // audit: 473 order_items di 222 pesanan, Rp 125.936.290.
  //
  // Gangguan jaringan sesaat tidak boleh berakhir sebagai harga yang salah
  // tersimpan: lempar, biar pemanggil menampilkan error dan MEMATIKAN Simpan.
  const [variantRows, groupRes, variantGroupRes, productRes, brandRulesRes] = await Promise.all([
    fetchAllProductVariants<Record<string, any>>(db, productId, EDIT_VARIANT_COLUMNS),
    db
      .from("reseller_groups")
      .select("id, slot, name, discount_persen, is_active")
      .order("slot"),
    db
      .from("variant_groups")
      .select(
        "id, name, sort_order, use_as_image_primary, variant_options(id, name, image_url, sort_order)"
      )
      .eq("product_id", productId)
      .order("sort_order", { ascending: true }),
    // Tipe produk hanya memengaruhi MODE picker, bukan satu angka pun, jadi
    // kegagalannya TIDAK dilempar — beda dari ketiga query di atas. Yang hilang
    // cuma kemampuan memateriakan kombinasi baru, yaitu perilaku sebelum
    // perbaikan ini; menggugurkan seluruh layar karenanya jauh lebih mahal.
    db
      .from("products")
      .select("product_type, brand, is_manual, po_limited, absent_combos_sellable")
      .eq("id", productId)
      .maybeSingle(),
    // Diskon merek per level (mig 7070) ikut menentukan ANGKA. Galatnya
    // DITANGKAP di sini lalu diputuskan sesudah produknya terbaca (bughunt
    // D13-15): produk Non Web tak pernah kena aturan merek, jadi gagal memuat
    // aturan tak boleh menjatuhkan layarnya; produk lain tetap dilempar.
    fetchGroupBrandDiscountRules(db).then(
      (rules) => ({ rules, error: null as unknown }),
      (error: unknown) => ({ rules: [] as GroupBrandRule[], error })
    )
  ]);

  // Grup reseller ikut menentukan ANGKA (diskon level), jadi kegagalannya sama
  // mahalnya dengan kegagalan varian: tanpa grup, harga dihitung seolah pembeli
  // tak punya level dan reseller ditagih harga umum.
  if (groupRes.error) {
    throw new Error(
      `Gagal memuat grup harga pelanggan: ${groupRes.error.message}`
    );
  }
  // Struktur grup variasi hanya memengaruhi PICKER, bukan angka — tapi picker
  // yang kosong membuat admin memilih varian yang salah, jadi tetap dilempar.
  if (variantGroupRes.error) {
    throw new Error(
      `Gagal memuat struktur variasi produk: ${variantGroupRes.error.message}`
    );
  }

  const variants: ClosedPOEditVariant[] = (variantRows ?? [])
    .filter((v) => v.is_active !== false)
    .map(mapEditVariantRow);
  // Baris yang dibuang di atas tetap berguna: ia daftar kombinasi yang TIDAK
  // dijual, dan picker wajib mencoretnya (bukan menawarkannya untuk dibuat).
  const blockedOptionCombos = blockedCombosFromRows(
    (variantRows ?? []).map((v) => ({
      isActive: v.is_active !== false,
      optionIds: (v.option_ids as string[] | null) ?? null
    }))
  );

  // Merek produk = kunci aturan diskon merek per level. Bila toko PUNYA aturan
  // dan mereknya tak terbaca, harga tak bisa dipastikan → lempar (fail-closed),
  // bukan menghitung harga level tanpa aturan. Toko tanpa aturan: perilaku lama
  // (kegagalan baris produk hanya mematikan mode picker malas).
  const productRow = (productRes.data ?? null) as
    | {
        product_type?: string;
        brand?: string | null;
        is_manual?: boolean | null;
        po_limited?: boolean | null;
        absent_combos_sellable?: boolean | null;
      }
    | null;
  const isNonWeb = !productRes.error && productRow?.is_manual === true;
  // Aturan gagal dimuat: produk Non Web jalan terus tanpa aturan (cermin
  // NON_WEB_NO_BRAND_RULES); produk lain → lempar (fail-closed, harga tak bisa
  // dipastikan). Pesannya dikenali layar sebagai galat KONTEKS HARGA.
  if (brandRulesRes.error && !isNonWeb) {
    throw new Error(
      `Gagal memuat diskon merek per level — ${
        brandRulesRes.error instanceof Error ? brandRulesRes.error.message : "muat ulang halaman"
      }`
    );
  }
  const brandRules = brandRulesRes.rules;
  if (brandRules.length > 0 && (productRes.error || !productRow)) {
    throw new Error("Gagal memuat merek produk untuk diskon merek per level — muat ulang halaman.");
  }
  const productBrand = { brand: productRow?.brand ?? null, isManual: productRow?.is_manual === true };

  // Konteks ini SATU produk: tiap level langsung diselesaikan untuk produk ini
  // (`levelForProduct`), jadi editor yang memilih grup pembeli dari `groups`
  // otomatis memakai aturan merek levelnya.
  const groups: ClosedPOEditGroup[] = ((groupRes.data as any[]) ?? []).map((g) => {
    const group: ClosedPOEditGroup = {
      id: g.id as string,
      slot: g.slot as number,
      name: g.name as string,
      discountPersen: Number(g.discount_persen),
      isActive: g.is_active as boolean
    };
    return levelForProduct(
      withBrandDiscounts(group, brandDiscountsForGroup(brandRules, group.id)),
      productBrand
    ) as ClosedPOEditGroup;
  });

  // Struktur grup variasi (mirror public-data.ts) — sort grup + opsi tiap grup.
  const variantGroups: import("@/lib/products").VariantGroup[] = (
    (variantGroupRes.data as any[]) ?? []
  )
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((g) => ({
      id: g.id as string,
      name: g.name as string,
      sortOrder: g.sort_order ?? 0,
      useAsImagePrimary: !!g.use_as_image_primary,
      options: ((g.variant_options as any[]) ?? [])
        .slice()
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((o) => ({
          id: o.id as string,
          name: o.name as string,
          imageUrl: o.image_url ?? undefined,
          sortOrder: o.sort_order ?? 0
        }))
    }));

  const rawType = productRow?.product_type;
  const productType =
    rawType === "preorder" || rawType === "ready_stock" ? rawType : undefined;

  return {
    variants,
    groups,
    variantGroups,
    productType,
    blockedOptionCombos,
    absentCombosSellable: productRow?.absent_combos_sellable ?? null,
    poLimited: productRow?.po_limited === true,
  };
}

export type ClosedPOEditableItem = {
  variantId: string | null;
  variantName: string;
  qty: number;
  note: string | null;
  /** true = sudah di Produksi / keep → terkunci, tak bisa diubah dari sini. */
  locked: boolean;
};

/**
 * Ambil order satu pembeli pada produk CLOSED PO untuk diedit: order id, grup,
 * serta item-itemnya (editable vs terkunci).
 *
 * Satu pembeli BISA punya >1 order aktif untuk satu produk (mis. checkout ganda
 * sebelum guard merge — lihat `createCheckoutOrder`). Editor lama hanya membaca
 * satu order (`limit(1)`) sehingga jumlah yang ditampilkan ≠ yang dipesan. Di
 * sini SEMUA order aktif digabung: item editable (Menunggu, keep null) dijumlah
 * per varian, item terkunci ditampilkan read-only. `orderId` = order tertua
 * (target konsolidasi saat simpan), `extraOrderIds` = order duplikat yang punya
 * item editable untuk dikosongkan saat simpan.
 */
export async function fetchClosedPOOrderForEdit(
  productId: string,
  customerId: string
): Promise<{
  orderId: string;
  extraOrderIds: string[];
  customerName: string;
  groupId: string | null;
  items: ClosedPOEditableItem[];
  /**
   * Baris HADIAH pembelian (mig 8680) — keterangan saja, tak pernah jadi baris
   * editor (RENCANA hadiah §17.4): hadiah ikut jumlah barang syaratnya.
   */
  gifts: AggregatedGiftItem[];
  /**
   * Alasan seluruh edit terkunci (bughunt D4-3), `null` = bebas diedit.
   * Cermin guard nota `admin_edit_closed_po_order`. Layar ini dulu tak
   * menanyakan `paid_qty` maupun nota sama sekali, jadi admin diundang
   * mengedit sesuatu yang pasti ditolak — dan penolakan itu datang SESUDAH
   * order duplikat terlanjur dikosongkan.
   */
  lockedReason: string | null;
} | null> {
  const db = createBrowserSupabase();
  if (!db) return null;

  const { data: orders, error } = await db
    .from("orders")
    .select(
      "id, customer_name, customer_id, created_at, customers(group_id), order_items(id, variant_id, variant_name, qty, paid_qty, item_note, item_status, keep_status, manual_price_normal, manual_discount_mode, gift_role, order_gift_id)"
    )
    .eq("product_id", productId)
    .eq("customer_id", customerId)
    .neq("status", "Batal")
    .order("created_at", { ascending: true });

  if (error || !orders || orders.length === 0) return null;

  const orderRows = orders as any[];
  const primary = orderRows[0];

  const itemIds = orderRows.flatMap((o) =>
    ((o.order_items as any[]) ?? []).map((it) => it.id as string)
  );

  // Nota AKTIF yang memuat baris-baris ini. Definisi statusnya diambil dari
  // `CART_INVOICE_ACTIVE_STATUSES` — satu daftar yang sama dengan keranjang DAN
  // dengan SQL-nya; menyalin daftarnya di sini akan melahirkan cermin kedua
  // yang bisa menyimpang dari guard yang sebenarnya berlaku.
  const inActiveInvoice = new Set<string>();
  if (itemIds.length) {
    const { data: lineRows, error: lineErr } = await db
      .from("invoice_lines")
      .select("order_item_id, invoices!inner(status)")
      .in("order_item_id", itemIds)
      .is("cancelled_at", null)
      .in("invoices.status", CART_INVOICE_ACTIVE_STATUSES);

    // FAIL-CLOSED. Gagal membaca nota BUKAN "tidak ada nota": meneruskan
    // set kosong akan membuka layar edit untuk pesanan yang sebenarnya
    // terkunci, yaitu keadaan yang justru dihindari perbaikan ini.
    if (lineErr) throw new Error(lineErr.message);
    for (const r of (lineRows as any[]) ?? []) {
      if (r.order_item_id) inActiveInvoice.add(r.order_item_id as string);
    }
  }

  // Nama aturan baris hadiah (keterangan editor). Gagal dibaca = tanpa nama,
  // bukan gagal memuat: barisnya tetap tersaring sebagai hadiah.
  const giftIds = orderRows.flatMap((o) =>
    ((o.order_items as any[]) ?? [])
      .filter((it) => it.gift_role === "hadiah" && it.order_gift_id)
      .map((it) => String(it.order_gift_id))
  );
  const giftNames = giftIds.length
    ? await fetchOrderGiftNames(giftIds, "admin").catch(() => new Map<string, string>())
    : new Map<string, string>();

  const rawRows = orderRows.flatMap((o) =>
    ((o.order_items as any[]) ?? []).map((it) => ({
      orderId: o.id as string,
      variantId: (it.variant_id ?? null) as string | null,
      variantName: (it.variant_name as string) ?? "",
      qty: (it.qty ?? 0) as number,
      note: (it.item_note ?? null) as string | null,
      itemStatus: (it.item_status ?? null) as string | null,
      keepStatus: (it.keep_status ?? null) as string | null,
      paidQty: Number(it.paid_qty ?? 0),
      inActiveInvoice: inActiveInvoice.has(it.id as string),
      giftRole: (it.gift_role ?? null) as string | null,
      orderGiftId: (it.order_gift_id ?? null) as string | null,
      giftRuleName: it.order_gift_id ? (giftNames.get(String(it.order_gift_id)) ?? null) : null
    }))
  );

  // Gabung item lintas order jadi baris editor. Aturan penggabungannya (varian
  // + CATATAN, bukan varian saja) hidup di `aggregateOrderItemsForEdit` supaya
  // bisa diuji unit tanpa Supabase — di situlah dulu catatan hilang.
  const { items, extraOrderIds, gifts } = aggregateOrderItemsForEdit(
    rawRows,
    primary.id as string
  );

  return {
    orderId: primary.id as string,
    extraOrderIds,
    customerName: primary.customer_name ?? "—",
    groupId: primary.customers?.group_id ?? null,
    items,
    gifts,
    lockedReason: closedPoEditLockedRows(rawRows).length
      ? "Pesanan sudah masuk tagihan / ada pcs terbayar — selesaikan atau void tagihannya dulu sebelum edit."
      : // Cermin gerbang HARGA_MANUAL `admin_edit_closed_po_order` (mig 7060):
        // tulis-ulang membuang Harga Jual & diskon manual baris `Menunggu`.
        orderRows.some((o) =>
            ((o.order_items as any[]) ?? []).some(
              (it) =>
                it.item_status === "Menunggu" &&
                manualPriceLocksRewrite({
                  keepStatus: (it.keep_status ?? null) as string | null,
                  manualPriceNormal:
                    it.manual_price_normal == null ? null : Number(it.manual_price_normal),
                  manualDiscountMode: (it.manual_discount_mode ?? null) as string | null
                })
            )
          )
        ? "Ada harga manual di pesanan ini — Edit Pesanan tidak tersedia. Kembalikan ke harga otomatis dulu di keranjang admin (Ubah Harga / Ubah Diskon)."
        : // Duplikat berhadiah boleh digabung sejak mig 8890 (H60): pembeliannya
          // utuh, hadiah digeser sekali di server (pratinjau di editor).
          null
  };
}

export type ManualOrderCustomer = {
  id: string;
  name: string;
  phone: string;
  groupId: string | null;
  /**
   * Sudah punya pesanan non-Batal untuk produk ini. Kriterianya SENGAJA sama
   * persis dengan gerbang di dalam `admin_create_order` /
   * `admin_create_closed_po_order` (`status <> 'Batal'`) — kalau berbeda, UI
   * akan menjanjikan sesuatu yang lalu ditolak RPC.
   */
  hasOrder: boolean;
};

/**
 * SELURUH pelanggan aktif toko + penanda siapa yang sudah memesan produk ini.
 * Dipakai kotak cari modal "Pesanan Manual" di Detail PO dan Detail Produksi
 * (sejak mig 7320 — dulu Detail Produksi membuang yang sudah memesan).
 *
 * Dulu pelanggan ber-`hasOrder` DIBUANG dari hasil, sehingga begitu seseorang
 * memesan namanya lenyap dari pencarian dan admin mengira datanya hilang.
 * Sekarang ia tetap muncul; modalnya yang mengarahkan ke Edit Pesanan alih-alih
 * memanggil RPC pembuatan yang pasti ditolak.
 */
export async function fetchManualOrderCustomers(
  productId: string
): Promise<ManualOrderCustomer[]> {
  const db = createBrowserSupabase();
  if (!db) return [];

  const [custRes, orderRes] = await Promise.all([
    db
      .from("customers")
      .select("id, full_name, phone, group_id, status")
      .eq("status", "active")
      .order("full_name", { ascending: true }),
    db
      .from("orders")
      .select("customer_id")
      .eq("product_id", productId)
      .neq("status", "Batal")
  ]);

  const taken = new Set(
    ((orderRes.data as any[]) ?? []).map((o) => o.customer_id as string)
  );

  return ((custRes.data as any[]) ?? []).map((c) => ({
    id: c.id as string,
    name: (c.full_name as string) ?? "—",
    phone: (c.phone as string) ?? "",
    groupId: (c.group_id as string) ?? null,
    hasOrder: taken.has(c.id)
  }));
}

export type ClosedPOOrderLine = {
  variantId: string | null;
  variantName: string;
  qty: number;
  unitPrice: number | null;
  note: string | null;
  /**
   * Jejak kampanye + potongan yang membentuk `unitPrice` (mig 5920) — dari
   * `explainVariantPrice().cuts` lewat `buildAdminOrderLines`. Opsional: hanya
   * `admin_add_order_lines` yang membacanya; RPC lain mengabaikan kunci asing.
   * Absen/null = tak tercatat (idiom 3650), bukan "tanpa potongan".
   */
  promoCampaignId?: string | null;
  promoCampaignName?: string | null;
  promoDiscountMode?: "persen" | "nominal" | null;
  promoDiscountValue?: number | null;
  levelDiscountMode?: "persen" | "nominal" | null;
  levelDiscountValue?: number | null;
  /**
   * Tambahan (mig 7900) yang TERMASUK di `unitPrice` — dikirim sebagai kunci
   * `priceExtra` hanya bila `unitPrice` berharga (`orderLinePriceExtraKey`).
   * Server (mig 8090, bughunt D15-30) mencocokkannya dengan tambahan varian
   * hari ini: beda = harga dihitung dari tambahan lama → simpan ditolak "muat
   * ulang". Absen = perilaku lama (trigger memotret tambahan varian).
   */
  priceExtra?: number;
};

/**
 * Simpan perubahan item pesanan satu pembeli (CLOSED PO).
 *
 * `allowOverQuota` memakai RPC pembungkus `…_over_quota` (mig 3970) yang
 * menyalakan flag transaksi-lokal sebelum mendelegasikan ke RPC yang sama.
 * Pembungkus, bukan parameter tambahan: menambah argumen pada RPC yang sudah
 * hidup menuntut drop+create seluruh badannya — dua salinan aturan pesanan yang
 * pasti menyimpang.
 */
export async function adminEditClosedPOOrder(
  orderId: string,
  lines: ClosedPOOrderLine[],
  allowOverQuota = false
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase client unavailable");

  const { error } = await db.rpc(allowOverQuota
    ? "admin_edit_closed_po_order_over_quota"
    : "admin_edit_closed_po_order", {
    p_order_id: orderId,
    p_lines: lines.map((l) => ({
      variantId: l.variantId,
      variantName: l.variantName,
      qty: l.qty,
      unitPrice: l.unitPrice,
      note: l.note,
      ...orderLinePriceExtraKey(l)
    }))
  });

  if (error) throw new Error(error.message);
}

/**
 * Konsolidasi order duplikat + simpan gabungannya dalam SATU transaksi
 * (mig 5290, bughunt D4-2).
 *
 * Versi lama melakukannya dari sini: `adminEditClosedPOOrder(extraId, [])` untuk
 * tiap duplikat, lalu `adminEditClosedPOOrder(orderId, lines)`. Tiap panggilan
 * transaksi tersendiri, jadi kegagalan di langkah terakhir — guard nota pada
 * order utama, admin menekan Batal pada konfirmasi kuota, jaringan putus —
 * meninggalkan item order duplikat TERHAPUS PERMANEN sementara gabungannya tak
 * sampai ke mana pun. RPC ini menjadikannya satu transaksi; kegagalan apa pun
 * mengembalikan seluruhnya.
 *
 * `allowOverQuota` jadi PARAMETER di sini, bukan RPC pembungkus terpisah:
 * fungsinya memang baru, jadi tak ada badan hidup yang harus di-drop+create
 * (alasan yang melahirkan pembungkus `…_over_quota` pada mig 3970 tidak berlaku).
 */
export async function adminConsolidateClosedPOOrder(
  targetOrderId: string,
  extraOrderIds: string[],
  lines: ClosedPOOrderLine[],
  allowOverQuota = false
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase client unavailable");

  const { error } = await db.rpc("admin_consolidate_closed_po_order", {
    p_target_order_id: targetOrderId,
    p_extra_order_ids: extraOrderIds,
    p_lines: lines.map((l) => ({
      variantId: l.variantId,
      variantName: l.variantName,
      qty: l.qty,
      unitPrice: l.unitPrice,
      note: l.note,
      ...orderLinePriceExtraKey(l)
    })),
    p_allow_over_quota: allowOverQuota
  });

  if (error) throw new Error(error.message);
}

/** Buat pesanan manual untuk pelanggan terdaftar (CLOSED PO). */
export async function adminCreateClosedPOOrder(
  productId: string,
  customerId: string,
  lines: ClosedPOOrderLine[],
  allowOverQuota = false
): Promise<string> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase client unavailable");

  const { data, error } = await db.rpc(allowOverQuota
    ? "admin_create_closed_po_order_over_quota"
    : "admin_create_closed_po_order", {
    p_product_id: productId,
    p_customer_id: customerId,
    p_lines: lines.map((l) => ({
      variantId: l.variantId,
      variantName: l.variantName,
      qty: l.qty,
      unitPrice: l.unitPrice,
      note: l.note,
      ...orderLinePriceExtraKey(l)
    }))
  });

  if (error) throw new Error(error.message);
  return data as string;
}

/**
 * Buat pesanan manual saat produk SEDANG DIPRODUKSI (mig 4110).
 *
 * Pintu ketiga di samping `actionAdminCreateOrder` (OPEN PO / ready stock) dan
 * `adminCreateClosedPOOrder` (CLOSED PO): keduanya menolak status
 * `PROSES PRODUKSI`, sehingga sebelum mig 4110 pesanan susulan hanya bisa
 * dibuat dengan memundurkan SELURUH batch lewat "Batal Submit ke Produksi".
 *
 * Hanya FASE A (target qty per varian belum terkunci). Di fase B RPC menolak
 * dengan pesan yang menyuruh "Batal Mulai Dikirim" dulu; sisi UI mematikan
 * tombolnya lewat `canAddProductionManualOrder` — ini gerbang otoritatifnya.
 *
 * Sejak mig 6410 RPC-nya (dan pembungkus over-quota, yang mendelegasikan ke
 * sana) juga menuntut izin `pesanan.manual-produksi`: admin tanpa izin itu
 * ditolak "Butuh izin …" walau memegang `pesanan.kelola`.
 */
export async function adminCreateProductionOrder(
  productId: string,
  customerId: string,
  lines: ClosedPOOrderLine[],
  allowOverQuota = false
): Promise<string> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase client unavailable");

  const { data, error } = await db.rpc(allowOverQuota
    ? "admin_create_production_order_over_quota"
    : "admin_create_production_order", {
    p_product_id: productId,
    p_customer_id: customerId,
    // Jejak kampanye & potongan ikut (mig 7570): tanpanya pesanan manual
    // berharga promo tak memakan kuota kampanye dan badge diskonnya hilang.
    p_lines: lines.map((l) => ({
      variantId: l.variantId,
      variantName: l.variantName,
      qty: l.qty,
      unitPrice: l.unitPrice,
      note: l.note,
      promoCampaignId: l.promoCampaignId ?? null,
      promoCampaignName: l.promoCampaignName ?? null,
      promoDiscountMode: l.promoDiscountMode ?? null,
      promoDiscountValue: l.promoDiscountValue ?? null,
      levelDiscountMode: l.levelDiscountMode ?? null,
      levelDiscountValue: l.levelDiscountValue ?? null,
      ...orderLinePriceExtraKey(l)
    }))
  });

  if (error) throw new Error(error.message);
  return data as string;
}

/** Pesanan satu pembeli yang siap dibuka editor Edit Pesanan Detail Produksi. */
export type ProductionOrderForEdit = {
  orderId: string;
  customerName: string;
  groupId: string | null;
  /** Baris yang ditulis ulang RPC (`item_status='Produksi'`, belum keep). */
  items: {
    variantId: string | null;
    variantName: string;
    qty: number;
    note: string | null;
    /** Harga tercatat — dipertahankan RPC untuk varian yang sudah nonaktif. */
    unitPrice: number | null;
  }[];
  /** Kalimat gembok siap tampil — null = boleh disimpan. */
  lockedReason: string | null;
};

/**
 * Muat pesanan satu pembeli untuk Edit Pesanan Detail Produksi (mig 7320).
 * Gemboknya dinilai `productionOrderEditLockedReason` — cermin gembok RPC —
 * supaya layar tak menjanjikan Simpan yang pasti ditolak. FAIL-CLOSED: gagal
 * membaca nota/DP = galat, bukan "tak ada nota". `null` = tak ada pesanan aktif.
 */
export async function fetchProductionOrderForEdit(
  productId: string,
  customerId: string
): Promise<ProductionOrderForEdit | null> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase client unavailable");

  const { data: orders, error } = await db
    .from("orders")
    .select(
      "id, status, customer_name, created_at, customers(group_id), order_items(id, variant_id, variant_name, qty, unit_price, paid_qty, item_note, item_status, keep_status, voucher_usage_id, manual_price_normal, manual_discount_mode)"
    )
    .eq("product_id", productId)
    .eq("customer_id", customerId)
    .neq("status", "Batal")
    .order("created_at", { ascending: true });

  if (error) throw new Error(error.message);
  if (!orders || orders.length === 0) return null;

  const primary = orders[0] as any;
  const itemRows = ((primary.order_items as any[]) ?? []);
  const itemIds = itemRows.map((it) => it.id as string);

  const inActiveInvoice = new Set<string>();
  if (itemIds.length) {
    const { data: lineRows, error: lineErr } = await db
      .from("invoice_lines")
      .select("order_item_id, invoices!inner(status)")
      .in("order_item_id", itemIds)
      .is("cancelled_at", null)
      .in("invoices.status", CART_INVOICE_ACTIVE_STATUSES);
    if (lineErr) throw new Error(lineErr.message);
    for (const r of (lineRows as any[]) ?? []) {
      if (r.order_item_id) inActiveInvoice.add(r.order_item_id as string);
    }
  }

  const { data: dpRows, error: dpErr } = await db
    .from("invoices")
    .select("status, amount_paid, balance_applied, refunded_amount, invoice_payments(status)")
    .eq("kind", "dp")
    .eq("source_order_id", primary.id as string)
    .in("status", ["pending_acc", "open", "partial", "paid"]);
  if (dpErr) throw new Error(dpErr.message);

  const rows: ProductionEditItemRow[] = itemRows.map((it) => ({
    itemStatus: (it.item_status ?? null) as string | null,
    keepStatus: (it.keep_status ?? null) as string | null,
    paidQty: Number(it.paid_qty ?? 0),
    inActiveInvoice: inActiveInvoice.has(it.id as string),
    voucherUsageId: (it.voucher_usage_id ?? null) as string | null,
    manualPriceNormal:
      it.manual_price_normal == null ? null : Number(it.manual_price_normal),
    manualDiscountMode: (it.manual_discount_mode ?? null) as string | null
  }));

  return {
    orderId: primary.id as string,
    customerName: (primary.customer_name as string) ?? "—",
    groupId: primary.customers?.group_id ?? null,
    items: itemRows
      .filter((it) =>
        isProductionEditableRow({
          itemStatus: (it.item_status ?? null) as string | null,
          keepStatus: (it.keep_status ?? null) as string | null
        })
      )
      .map((it) => ({
        variantId: (it.variant_id ?? null) as string | null,
        variantName: (it.variant_name as string) ?? "",
        qty: Number(it.qty ?? 0),
        note: (it.item_note ?? null) as string | null,
        unitPrice: it.unit_price == null ? null : Number(it.unit_price)
      })),
    lockedReason: productionOrderEditLockedReason({
      orderCount: orders.length,
      orderStatus: (primary.status ?? null) as string | null,
      rows,
      dpLocked: productionDpInvoiceLocked(
        ((dpRows as any[]) ?? []).map((d) => ({
          status: d.status as string,
          amountPaid: Number(d.amount_paid ?? 0),
          balanceApplied: Number(d.balance_applied ?? 0),
          refundedAmount: Number(d.refunded_amount ?? 0),
          hasPendingPayment: ((d.invoice_payments as any[]) ?? []).some(
            (p) => p.status === "pending"
          )
        }))
      )
    })
  };
}

/**
 * Simpan Edit Pesanan Detail Produksi (mig 7320) — `admin_edit_production_order`
 * menulis ulang baris `item_status='Produksi'` satu order dalam satu transaksi.
 * Izin `pesanan.edit-produksi` (bawaan Super Admin saja), PROSES PRODUKSI fase A,
 * minimal 1 pcs. `allowOverQuota` → pembungkus `…_over_quota` (idiom mig 3970),
 * dipakai HANYA setelah admin mengonfirmasi penolakan kuota.
 */
export async function adminEditProductionOrder(
  orderId: string,
  lines: ProductionEditRpcLine[],
  allowOverQuota = false
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase client unavailable");

  // Muatan dikirim APA ADANYA (`productionEditRpcLines`): kunci `unitPrice` &
  // jejak yang absen untuk varian nonaktif adalah sinyal ke RPC (mig 7570)
  // untuk mempertahankan harga + jejak baris lama — jangan diisi null di sini.
  const { error } = await db.rpc(allowOverQuota
    ? "admin_edit_production_order_over_quota"
    : "admin_edit_production_order", {
    p_order_id: orderId,
    p_lines: lines
  });

  if (error) throw new Error(error.message);
}

/**
 * Ringkasan katalog SANGAT ringan untuk halaman master Merek/Kategori/Segmen
 * admin — hanya kolom yang dipakai menghitung pemakaian per master
 * (`annotateProductCounts`) & Tag Segmen Massal (`groupProductsForSegmentTagging`).
 * Dulu ketiga halaman memakai `loadAdminProductsFromSupabase` (SELURUH katalog:
 * semua kolom termasuk `search_text`/`description` + semua foto + semua varian)
 * hanya demi angka hitungan — transfer membengkak drastis di katalog besar
 * (audit performa 2026-07).
 */
export type AdminProductLite = {
  id: string;
  brand: string | null;
  category: string | null;
  segment: string | null;
};

export async function loadAdminProductLitesFromSupabase(): Promise<
  AdminProductLite[] | null
> {
  const db = createBrowserSupabase();
  if (!db) return null;

  const { data, error } = await db
    .from("products")
    .select("id, brand, category, segment");

  if (error) throw new Error(error.message);
  return ((data ?? []) as any[]).map((r) => ({
    id: r.id as string,
    brand: (r.brand as string | null) ?? null,
    category: (r.category as string | null) ?? null,
    segment: (r.segment as string | null) ?? null
  }));
}

/** Petakan satu baris view `customer_summary` ke tipe domain AdminCustomer. */
export function mapCustomerSummaryRow(row: any): AdminCustomer { // eslint-disable-line @typescript-eslint/no-explicit-any
  return {
    id: row.customer_id,
    name: row.full_name,
    storeName: row.store_name ?? undefined,
    phone: row.phone,
    email: row.email ?? null,
    level: row.customer_level,
    status: (row.status ?? "active") as AdminCustomer["status"],
    groupId: row.group_id ?? null,
    groupName: row.group_name ?? null,
    assignedAdminId: row.assigned_admin_id ?? null,
    assignedAdminName: row.assigned_admin_name ?? null,
    totalOrder: Number(row.total_order ?? 0),
    // `total_belanja` bertipe bigint sejak mig 3740 (anti `integer out of range`
    // saat belanja seumur hidup lewat Rp 2,1 M). Koersi eksplisit — cermin
    // `mapCustomer` di AdminCustomerEdit — supaya `formatAdminMoney` tak pernah
    // menerima string seandainya serializer memulangkan int8 sebagai teks.
    totalSpend: Number(row.total_belanja ?? 0),
    createdAt: row.created_at ?? undefined,
    // mig 3800 — null = belum pernah order sama sekali (bukan "tanggalnya
    // hilang"). JANGAN di-coalesce ke string kosong: layar membedakan
    // "Belum pernah order" dari sebuah tanggal.
    lastOrderAt: row.last_order_at ?? null
  };
}

/**
 * Ambil SATU halaman pelanggan (paginasi server, infinite scroll). Filter tab +
 * pencarian diterapkan di server lewat `applyCustomerListFilters`. Urutan
 * deterministik (created_at lalu customer_id) agar paginasi `.range()` tidak
 * melompati / menggandakan baris saat created_at sama.
 */
export async function fetchCustomersPage(opts: {
  filter: CustomerFilterKey;
  search: string;
  from: number;
  to: number;
}): Promise<AdminCustomer[] | null> {
  const db = createBrowserSupabase("admin");
  if (!db) return null;

  const query = applyCustomerListFilters(
    db.from("customer_summary").select(CUSTOMER_SUMMARY_COLUMNS),
    opts
  );
  const { data, error } = await query
    .order("created_at", { ascending: false })
    .order("customer_id", { ascending: false })
    .range(opts.from, opts.to);

  if (error) throw new Error(error.message);
  return (data as any[]).map(mapCustomerSummaryRow);
}

/**
 * Hitung jumlah pelanggan per filter tab (head/count, tanpa menarik baris).
 * Pencarian ikut diperhitungkan agar badge tab konsisten dengan daftar.
 *
 * `filterKeys` datang dari pemanggil (bukan konstanta tetap) karena tab
 * sekarang dinamis — satu tab per grup pelanggan aktif (`buildCustomerFilterTabs`).
 */
export async function fetchCustomerCounts(
  search: string,
  filterKeys: CustomerFilterKey[]
): Promise<CustomerCounts | null> {
  const db = createBrowserSupabase("admin");
  if (!db) return null;

  const results = await Promise.all(
    filterKeys.map((filter) =>
      applyCustomerListFilters(
        db.from("customer_summary").select("customer_id", { count: "exact", head: true }),
        { filter, search }
      )
    )
  );

  const counts: CustomerCounts = {};
  filterKeys.forEach((key, i) => {
    counts[key] = results[i].count ?? 0;
  });
  return counts;
}

/**
 * Ambil SEMUA customer_id yang cocok dengan filter+pencarian saat ini (tanpa
 * paginasi) — dipakai aksi "Pilih semua N pelanggan" agar aksi masal tetap bisa
 * menjangkau pelanggan yang belum dimuat ke layar.
 */
export async function fetchCustomerIds(opts: {
  filter: CustomerFilterKey;
  search: string;
}): Promise<string[] | null> {
  const db = createBrowserSupabase("admin");
  if (!db) return null;

  const query = applyCustomerListFilters(
    db.from("customer_summary").select("customer_id"),
    opts
  );
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data as any[]).map((r) => r.customer_id as string);
}

/**
 * Ambil semua grup pelanggan (untuk dropdown & tab filter di admin).
 */
export async function fetchResellerGroupsFromSupabase() {
  const db = createBrowserSupabase();
  if (!db) return null;

  const { data, error } = await db
    .from("reseller_groups")
    .select("id, slot, name, discount_persen, is_active")
    .order("slot");

  if (error) return null;

  return (data as any[]).map((g) => ({
    id: g.id as string,
    slot: g.slot as number,
    name: g.name as string,
    discountPersen: Number(g.discount_persen),
    isActive: g.is_active as boolean
  }));
}

/**
 * Admin membuat akun pelanggan manual via API route (service role).
 * Membuat auth account + profiles + customers (status langsung 'active').
 */
export async function createResellerAccount(payload: {
  name: string;
  storeName: string;
  phone: string;
  email?: string | null;
  password: string;
  groupId?: string | null;
  handlerId?: string | null;
}) {
  const response = await fetch("/api/admin/customers", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });

  const result = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(result.error ?? "Gagal membuat akun pelanggan.");
  }

  return true;
}

/**
 * Update status (dan opsional level) customer via RPC admin_update_customer_status.
 * Atomic + tercatat di customer_activity_log (migration 026).
 */
export async function updateCustomerStatusInSupabase(
  customerId: string,
  status: AdminCustomer["status"],
  level?: CustomerLevel,
  note?: string,
  groupId?: string | null
) {
  const db = createBrowserSupabase();

  if (!db || !isUuid(customerId)) {
    return false;
  }

  const { error } = await db.rpc("admin_update_customer_status", {
    target_customer_id: customerId,
    new_status: status,
    new_level: level ?? null,
    admin_note: note ?? null,
    new_group_id: groupId !== undefined ? (groupId || null) : null
  });

  if (error) {
    throw new Error(
      `Gagal memperbarui status customer. Jalankan migration 026 terlebih dahulu. Detail: ${error.message}`
    );
  }

  return true;
}

/** Kolom merek TANPA `telegram_url` — dipakai sebagai jalur mundur, lihat di bawah. */
const BRAND_COLUMNS_BASE = "id, name, note, icon_url, created_at";
const BRAND_COLUMNS = `${BRAND_COLUMNS_BASE}, telegram_url`;

/**
 * `telegram_url` (mig 3440) baru ada setelah migrasi diterapkan lewat jendela
 * runbook, sementara kode tayang lebih dulu lewat auto-deploy. Di sela itu
 * PostgREST membalas 42703 (kolom tak dikenal) dan daftar merek akan mati
 * total — padahal yang hilang cuma satu kolom opsional. Jadi: coba kolom
 * lengkap dulu, mundur ke kolom lama HANYA untuk error kolom-tak-ada.
 */
function isMissingBrandColumnError(error: unknown): boolean {
  if (!error) return false;
  const e = error as { code?: string; message?: string };
  // 42703 = undefined_column. Pesan disaring ke nama kolomnya supaya error
  // kolom LAIN tidak ikut "disembuhkan" diam-diam oleh jalur mundur.
  if (e.code === "42703") return true;
  return (e.message ?? "").includes("telegram_url");
}

function mapAdminBrandRow(brand: any): AdminBrand {
  return {
    id: brand.id,
    name: brand.name,
    note: brand.note ?? undefined,
    iconUrl: brand.icon_url ?? undefined,
    telegramUrl: brand.telegram_url ?? undefined,
    totalProducts: 0,
    createdAt: brand.created_at
  };
}

export async function fetchAdminBrandsFromSupabase() {
  const db = createBrowserSupabase();

  if (!db) {
    return null;
  }

  // Master `product_brands` adalah satu-satunya sumber kebenaran. JANGAN derive
  // dari products.brand sebagai fallback — merek yang sudah dihapus (is_active
  // false) tidak boleh "nyangkut" hanya karena masih ada snapshot teks di
  // products. Master kosong → [] (dropdown/ daftar memang kosong sampai admin
  // membuat merek lagi; nilai brand produk yang ada tetap tampil sendiri).
  const run = (columns: string) =>
    db
      .from("product_brands")
      .select(columns)
      .eq("is_active", true)
      .order("name", { ascending: true });

  let result: { data: unknown; error: unknown } = await run(BRAND_COLUMNS);
  if (isMissingBrandColumnError(result.error)) {
    result = await run(BRAND_COLUMNS_BASE);
  }

  if (result.error || !result.data) {
    return [];
  }

  return (result.data as any[]).map(mapAdminBrandRow);
}

/**
 * Satu merek untuk halaman form `/admin/brands/[id]`. Merek NONAKTIF ikut
 * terbaca (policy `product_brands` mengizinkan admin membaca semuanya) supaya
 * tautan lama tidak berujung 404 yang membingungkan. `null` = id tak dikenal.
 */
export async function fetchAdminBrandByIdFromSupabase(
  id: string
): Promise<AdminBrand | null> {
  const db = createBrowserSupabase();
  if (!db) return null;

  const run = (columns: string) =>
    db.from("product_brands").select(columns).eq("id", id).maybeSingle();

  let result: { data: unknown; error: unknown } = await run(BRAND_COLUMNS);
  if (isMissingBrandColumnError(result.error)) {
    result = await run(BRAND_COLUMNS_BASE);
  }

  if (result.error) {
    throw new Error((result.error as { message?: string }).message ?? "Gagal memuat merek.");
  }
  if (!result.data) return null;
  return mapAdminBrandRow(result.data);
}

export async function deleteAdminBrandFromSupabase(id: string) {
  const db = createBrowserSupabase();
  if (!db) return;
  const { error } = await db
    .from("product_brands")
    .update({ is_active: false })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * Saldo dompet merek (mig 154) untuk ditampilkan di form merek. 0 bila merek
 * itu belum pernah punya mutasi. Dibaca langsung dari `brand_balance` (policy
 * admin-select) alih-alih lewat `fetchBrandBalances()` yang menarik SEMUA
 * merek — di sini yang dibutuhkan cuma satu.
 */
export async function fetchAdminBrandBalanceFromSupabase(
  brandId: string
): Promise<number> {
  const db = createBrowserSupabase();
  if (!db) return 0;

  const { data, error } = await db
    .from("brand_balance")
    .select("balance")
    .eq("brand_id", brandId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return Number(data?.balance ?? 0) || 0;
}

export async function saveAdminBrandToSupabase(payload: AdminBrandPayload) {
  const db = createBrowserSupabase();

  if (!db) {
    return null;
  }

  const brandRow: Record<string, unknown> = {
    name: payload.name.trim(),
    note: payload.note?.trim() || null,
    is_active: true
  };
  if (payload.iconUrl !== undefined) {
    brandRow.icon_url = payload.iconUrl || null;
  }
  if (payload.telegramUrl !== undefined) {
    brandRow.telegram_url = payload.telegramUrl.trim() || null;
  }
  const query = payload.id
    ? db
        .from("product_brands")
        .update(brandRow)
        .eq("id", payload.id)
        .select("id")
        .single()
    : db
        .from("product_brands")
        .upsert(brandRow, { onConflict: "store_id,name" })
        .select("id")
        .single();
  const { data, error } = await query;

  if (error || !data) {
    throw new Error(error?.message ?? "Gagal menyimpan merek.");
  }

  return data.id as string;
}

/**
 * GANTI NAMA merek lewat RPC `admin_rename_brand` (mig 3440) — BUKAN update
 * kolom `name` biasa.
 *
 * Alasannya: `products.brand` adalah snapshot TEKS, dan Saldo Merek / Retur
 * Produksi / target program poin me-resolve merek sebuah produk BY NAMA.
 * Meng-update nama merek sendirian akan memutus tautan itu diam-diam. RPC
 * menyinkronkan snapshot-snapshot itu dalam SATU transaksi, sementara
 * `product_brands.id` — jangkar `brand_balance` + ledgernya — tidak berubah
 * sama sekali.
 */
export async function renameAdminBrandInSupabase(
  id: string,
  name: string
): Promise<BrandRenameResult> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Koneksi database tidak tersedia.");

  const { data, error } = await db.rpc("admin_rename_brand", {
    p_brand_id: id,
    p_name: name.trim()
  });

  if (error) {
    // RPC belum ada = migrasi 3440 belum diterapkan. SENGAJA tidak mundur ke
    // update polos: rename tanpa sinkronisasi snapshot justru meninggalkan
    // produk yatim dari mereknya — persis yang dicegah RPC ini.
    if (error.code === "42883" || /admin_rename_brand/.test(error.message ?? "")) {
      throw new Error(
        "Ganti nama merek belum aktif di database ini (migrasi 3440 belum diterapkan). Perubahan lain tetap bisa disimpan."
      );
    }
    throw new Error(error.message);
  }

  return mapBrandRenameResult(data);
}

export async function fetchAdminCategoriesFromSupabase() {
  const db = createBrowserSupabase();

  if (!db) {
    return null;
  }

  const { data, error } = await db
    .from("product_categories")
    .select("id, name, note, icon_url, created_at")
    .eq("is_active", true)
    .order("name", { ascending: true });

  if (error) {
    return null;
  }

  return (data as any[]).map(
    (category): AdminCategory => ({
      id: category.id,
      name: category.name,
      note: category.note ?? undefined,
      iconUrl: category.icon_url ?? undefined,
      totalProducts: 0,
      createdAt: category.created_at
    })
  );
}

export async function saveAdminCategoryToSupabase(
  payload: AdminCategoryPayload
) {
  const db = createBrowserSupabase();

  if (!db) {
    return null;
  }

  const categoryRow: Record<string, unknown> = {
    note: payload.note?.trim() || null,
    is_active: true
  };
  // Nama hanya ditulis saat MEMBUAT. Memperbarui kategori yang sudah ada tak
  // pernah menyentuh `name` — itu jalur `admin_rename_category` (lihat tipe
  // payload); update biasa di sini akan meninggalkan produk dengan nama lama.
  if (!payload.id) {
    categoryRow.name = (payload.name ?? "").trim();
  }
  if (payload.iconUrl !== undefined) {
    categoryRow.icon_url = payload.iconUrl || null;
  }
  const query = payload.id
    ? db
        .from("product_categories")
        .update(categoryRow)
        .eq("id", payload.id)
        .select("id")
        .single()
    : db
        .from("product_categories")
        .upsert(categoryRow, { onConflict: "store_id,name" })
        .select("id")
        .single();
  const { data, error } = await query;

  if (error || !data) {
    throw new Error(error?.message ?? "Gagal menyimpan kategori.");
  }

  return data.id as string;
}

function categoryRenameError(
  error: { code?: string; message?: string },
  forPreview = false
): Error {
  // RPC belum ada = migrasinya belum diterapkan. SENGAJA tidak mundur ke update
  // biasa: itulah yang meninggalkan produk dengan nama lama.
  if (
    error.code === "42883" ||
    error.code === "PGRST202" ||
    /admin_rename_category/.test(error.message ?? "")
  ) {
    return new Error(CATEGORY_RENAME_UNAVAILABLE);
  }
  // Pesan RAISE EXCEPTION milik RPC sudah berbahasa Indonesia dan lolos apa
  // adanya (tabrakan nama, tanpa izin, dst.). Selebihnya (jaringan, batas waktu,
  // galat tak dikenal) diganti kalimat yang bisa ditindaklanjuti.
  const t = terjemahkanErrorRpc(error);
  if (forPreview && (t.kind === "jaringan" || t.kind === "waktu_habis" || t.kind === "tak_dikenal")) {
    return new Error(CATEGORY_RENAME_CHECK_FAILED);
  }
  return new Error(t.pesan);
}

/**
 * Hitung dampak ganti nama TANPA menulis (dry-run RPC `admin_rename_category`):
 * berapa produk yang teks kategorinya ikut berubah, berapa target poin/rak/
 * promo/voucher, dan apakah peta TikTok ikut pindah. Angkanya dari SQL (kunci
 * `category_key`), bukan dari daftar produk di layar yang bisa terpotong batas
 * baris PostgREST. Gagal = lempar galat; pemanggil WAJIB memblokir simpan.
 */
export async function previewAdminCategoryRename(
  id: string,
  name: string
): Promise<CategoryRenameResult> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Koneksi database tidak tersedia.");

  const { data, error } = await db.rpc("admin_rename_category", {
    p_category_id: id,
    p_name: name.trim(),
    p_dry_run: true
  });
  if (error) throw categoryRenameError(error, true);
  return mapCategoryRenameResult(data);
}

/**
 * GANTI NAMA kategori lewat RPC `admin_rename_category` — BUKAN update kolom
 * `name`. Satu panggilan biasanya tuntas dalam satu transaksi; bila produk yang
 * cocok lebih banyak dari batch database, RPC memperbarui sebagian lalu
 * memulangkan `done=false` dan fungsi ini memanggilnya lagi (argumen sama,
 * nama master baru berganti di putaran terakhir) sampai tuntas.
 */
export async function renameAdminCategoryInSupabase(
  id: string,
  name: string,
  onProgress?: (done: number, total: number) => void
): Promise<CategoryRenameResult> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Koneksi database tidak tersedia.");

  let updated = 0;
  let total = 0;
  for (let round = 0; round < CATEGORY_RENAME_MAX_ROUNDS; round += 1) {
    const { data, error } = await db.rpc("admin_rename_category", {
      p_category_id: id,
      p_name: name.trim(),
      p_dry_run: false,
      p_products_before: updated
    });
    if (error) {
      const failure = categoryRenameError(error);
      // Terputus di tengah batch: produk yang sudah diganti tidak dibatalkan;
      // simpan ulang melanjutkan karena pencocokan masih memakai nama lama master.
      throw updated > 0 && total > 0
        ? new Error(`${categoryRenameInterruptedMessage(updated, total)} (${failure.message})`)
        : failure;
    }
    const result = mapCategoryRenameResult(data);
    if (round === 0) total = result.productsTotal;
    updated += result.productsUpdated;
    if (result.done) {
      return { ...result, productsTotal: total, productsUpdated: updated };
    }
    onProgress?.(updated, total);
  }
  throw new Error(categoryRenameInterruptedMessage(updated, total));
}

export async function deleteAdminCategoryFromSupabase(id: string) {
  const db = createBrowserSupabase();
  if (!db) return;
  const { error } = await db
    .from("product_categories")
    .update({ is_active: false })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

// ─── Segmen (mig 159) — cermin pola Kategori, master text snapshot ──────────────

export async function fetchAdminSegmentsFromSupabase() {
  const db = createBrowserSupabase();

  if (!db) {
    return null;
  }

  const { data, error } = await db
    .from("product_segments")
    .select("id, name, created_at")
    .eq("is_active", true)
    .order("name", { ascending: true });

  if (error) {
    return null;
  }

  return (data as any[]).map( // eslint-disable-line @typescript-eslint/no-explicit-any
    (segment): AdminSegment => ({
      id: segment.id,
      name: segment.name,
      totalProducts: 0,
      createdAt: segment.created_at
    })
  );
}

export async function saveAdminSegmentToSupabase(payload: AdminSegmentPayload) {
  const db = createBrowserSupabase();

  if (!db) {
    return null;
  }

  const segmentRow: Record<string, unknown> = {
    name: payload.name.trim(),
    is_active: true
  };
  const query = payload.id
    ? db
        .from("product_segments")
        .update(segmentRow)
        .eq("id", payload.id)
        .select("id")
        .single()
    : db
        .from("product_segments")
        .upsert(segmentRow, { onConflict: "store_id,name" })
        .select("id")
        .single();
  const { data, error } = await query;

  if (error || !data) {
    throw new Error(error?.message ?? "Gagal menyimpan segmen.");
  }

  return data.id as string;
}

export async function deleteAdminSegmentFromSupabase(id: string) {
  const db = createBrowserSupabase();
  if (!db) return;
  const { error } = await db
    .from("product_segments")
    .update({ is_active: false })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * Tag segmen massal ke sekumpulan produk (mig 159) — dipakai "Tag Segmen Massal"
 * di Daftar Segmen untuk menutup gap produk dewasa yang belum bersegmen.
 * Update langsung `products.segment` (sesi admin / RLS is_admin). `segment` kosong
 * → null (lepas segmen). Idempoten; caller yang menyaring produk yang relevan.
 */
export async function bulkSetProductSegmentInSupabase(
  productIds: string[],
  segment: string | null
) {
  const db = createBrowserSupabase();
  if (!db || !productIds.length) return;
  const { error } = await db
    .from("products")
    .update({ segment: segment && segment.trim() ? segment.trim() : null })
    .in("id", productIds);
  if (error) throw new Error(error.message);
}

export async function uploadCategoryIconToSupabase(file: File): Promise<string> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Tidak dapat terhubung.");
  const ext = file.name.split(".").pop() ?? "jpg";
  const path = await withStorePrefix(db, `category-icons/${Date.now()}.${ext}`);
  const { error } = await db.storage
    .from("product-media")
    .upload(path, file, {
      upsert: false,
      contentType: file.type,
      cacheControl: "31536000"
    });
  if (error) throw new Error(error.message);
  const { data } = db.storage.from("product-media").getPublicUrl(path);
  return data.publicUrl;
}

/** Upload logo merek ke bucket publik `product-media` → URL publik (mirror ikon kategori). */
export async function uploadBrandIconToSupabase(file: File): Promise<string> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Tidak dapat terhubung.");
  const ext = file.name.split(".").pop() ?? "jpg";
  const path = await withStorePrefix(db, `brand-icons/${Date.now()}.${ext}`);
  const { error } = await db.storage
    .from("product-media")
    .upload(path, file, {
      upsert: false,
      contentType: file.type,
      cacheControl: "31536000"
    });
  if (error) throw new Error(error.message);
  const { data } = db.storage.from("product-media").getPublicUrl(path);
  return data.publicUrl;
}

function isUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value
  );
}

// ─── Closed PO ────────────────────────────────────────────────────────────────

/** Baris mentah RPC `get_closed_po_summary` (SATU baris per varian). */
type ClosedPOSummaryRow = {
  product_id: string;
  product_name: string;
  brand: string;
  closed_at: string | null;
  deadline: string | null;
  estimated_ready: string;
  variant_id: string;
  variant_name: string;
  pending_qty: number;
  reseller_count: number;
  submitted_qty: number;
  submitted_at: string | null;
  production_status: "pending" | "partial" | "submitted";
};

/**
 * Ambil SEMUA baris `get_closed_po_summary` secara TERPAGINASI (menembus batas
 * `max-rows` PostgREST = 1000 — BERLAKU JUGA untuk RPC set-returning).
 *
 * RPC ini mengembalikan SATU BARIS PER VARIAN untuk SEMUA produk CLOSED PO
 * sekaligus (termasuk varian TANPA pesanan). Saat total varian closed PO > 1000
 * — mis. saat sebuah produk RIBUAN kombinasi baru ditutup (batch auto-close
 * harian) — pemanggilan TUNGGAL hanya menerima 1000 baris PERTAMA. Karena RPC
 * diurutkan `closed_at desc`, 1000 baris itu bisa HABIS dipakai oleh SATU produk
 * raksasa, sehingga SEMUA produk closed PO lain LENYAP dari board (bug live
 * 2026-07: mis. GAMILA & 18 produk lain hilang karena kalah urut dgn produk
 * 2.484 varian). Ambil terpaginasi 1000/hal, diurut `variant_id` (unik global →
 * offset stabil, tanpa lompatan/duplikat); pemanggil tetap mengelompokkan per
 * produk (urutan baris tak memengaruhi kebenaran). `productId` opsional
 * memfilter satu produk (Detail PO — produk raksasa pun termuat penuh).
 */
async function fetchAllClosedPOSummaryRows(
  db: NonNullable<ReturnType<typeof createBrowserSupabase>>,
  productId?: string
): Promise<ClosedPOSummaryRow[]> {
  const PAGE = 1000; // = max-rows PostgREST
  const all: ClosedPOSummaryRow[] = [];
  for (let from = 0; ; from += PAGE) {
    let query = db.rpc("get_closed_po_summary");
    if (productId) query = query.eq("product_id", productId);
    const { data, error } = await query
      .order("variant_id", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as unknown as ClosedPOSummaryRow[];
    all.push(...rows);
    if (rows.length < PAGE) break;
  }
  return all;
}

/**
 * Mengambil rekap produk Closed PO dari Supabase via RPC get_closed_po_summary
 * (migration 020). Mengembalikan data yang sudah dikelompokkan per produk.
 *
 * Setiap produk berisi daftar varian dengan:
 *   pendingQty    — qty belum disubmit (item_status='Menunggu')
 *   resellerCount — jumlah pelanggan unik yang masih menunggu
 *   submittedQty  — qty yang sudah disubmit ke produksi (akumulatif)
 *   productionStatus — 'pending' | 'partial' | 'submitted'
 */
export async function fetchClosedPOSummaryFromSupabase(): Promise<
  ClosedPOProduct[] | null
> {
  const db = createBrowserSupabase();

  if (!db) {
    return null;
  }

  let rows: ClosedPOSummaryRow[];
  try {
    rows = await fetchAllClosedPOSummaryRows(db);
  } catch (e) {
    throw new Error(
      `Gagal memuat data Closed PO. Jalankan migration 020 terlebih dahulu. Detail: ${
        e instanceof Error ? e.message : String(e)
      }`
    );
  }

  if (!rows.length) {
    return [];
  }

  // Flat rows → group by product
  const productMap = new Map<string, ClosedPOProduct>();

  for (const row of rows) {
    const pid = row.product_id;

    if (!productMap.has(pid)) {
      productMap.set(pid, {
        productId: pid,
        productName: row.product_name,
        brand: row.brand,
        closedAt: row.closed_at,
        deadline: row.deadline,
        estimatedReady: row.estimated_ready,
        hasAnySubmitted: false,
        allSubmitted: true, // will be set false if any variant isn't submitted
        variants: []
      });
    }

    const product = productMap.get(pid)!;

    const variant: ClosedPOVariant = {
      variantId: row.variant_id,
      variantName: row.variant_name,
      pendingQty: row.pending_qty,
      resellerCount: row.reseller_count,
      submittedQty: row.submitted_qty,
      productionStatus: row.production_status,
      submittedAt: row.submitted_at
    };

    product.variants.push(variant);

    if (variant.productionStatus !== "pending") {
      product.hasAnySubmitted = true;
    }
    if (variant.productionStatus !== "submitted") {
      product.allSubmitted = false;
    }
  }

  let result = [...productMap.values()];

  // Total pcs dipesan per produk = Σ (pending + submitted) tiap varian.
  for (const p of result) {
    p.totalPcs = p.variants.reduce(
      (sum, v) => sum + v.pendingQty + v.submittedQty,
      0
    );
  }

  // PO yang ditutup TANPA pesanan (jatuh tempo & tak ada yang order) tidak
  // ditampilkan di kolom Closed PO — admin cukup diberi tahu lewat notif
  // `po_closed_empty` (mig 116). Saring di sini (totalPcs 0 = tanpa pesanan).
  result = result.filter((p) => (p.totalPcs ?? 0) > 0);

  // Lengkapi foto produk (product_media) + jumlah pembeli unik.
  // Best-effort: kegagalan query tambahan tidak boleh menggagalkan panel.
  const productIds = result.map((p) => p.productId);
  try {
    const [mediaRes, buyerRes] = await Promise.all([
      db
        .from("products")
        .select(
          "id, product_media(media_url, storage_path, is_cover, media_type, sort_order)"
        )
        .in("id", productIds),
      db
        .from("orders")
        .select("product_id, customer_id, order_items(qty, keep_status)")
        .in("product_id", productIds)
        // Sertakan 'Produksi': submit ke produksi bisa PARSIAL sehingga order
        // pindah ke 'Produksi' walau produk masih CLOSED PO & sebagian itemnya
        // masih 'Menunggu'. Tanpa ini, pembeli & pcs pada order tsb tak terhitung
        // → kartu board tampil "0 pembeli" (bug PO setengah-submit).
        .in("status", ["Menunggu", "Diproses", "Produksi"])
    ]);

    const imageMap = new Map<string, string | null>();
    for (const row of (mediaRes.data ?? []) as any[]) {
      const media = (row.product_media ?? []) as any[];
      const cover =
        media.find((m) => m.is_cover && m.media_type === "image") ??
        media
          .filter((m) => m.media_type === "image")
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0] ??
        null;
      // URL dari project AKTIF via storage_path (mig 2300); fallback media_url.
      imageMap.set(row.id, cover ? resolveProductMediaUrl(cover) : null);
    }

    // Pembeli unik + pcs AKTIF (non-cancelled) per produk. `submitted_qty` pada
    // RPC ikut menghitung pcs yang sudah DIBATALKAN setelah submit (mesin cancel
    // mig 074 tak menurunkannya), sehingga Σ(pending+submitted) meng-overcount.
    // Hitung ulang dari order_items aktif agar cocok dengan Detail Pre-order
    // (aggregatePODetail meng-skip item cancelled).
    const buyerMap = new Map<string, Set<string>>();
    const activePcsMap = new Map<string, number>();
    for (const row of (buyerRes.data ?? []) as any[]) {
      if (row.customer_id) {
        const set = buyerMap.get(row.product_id) ?? new Set<string>();
        set.add(row.customer_id);
        buyerMap.set(row.product_id, set);
      }
      const activePcs = ((row.order_items ?? []) as any[]).reduce(
        (s: number, it: any) =>
          s + (it.keep_status === "cancelled" ? 0 : (it.qty ?? 0)),
        0
      );
      activePcsMap.set(
        row.product_id,
        (activePcsMap.get(row.product_id) ?? 0) + activePcs
      );
    }

    for (const p of result) {
      p.imageUrl = imageMap.get(p.productId) ?? null;
      p.totalBuyers = buyerMap.get(p.productId)?.size ?? 0;
      // Override totalPcs dgn hitungan aktif; fallback ke nilai RPC bila produk
      // tak ada di hasil query (best-effort — jangan nol-kan kartu).
      const activePcs = activePcsMap.get(p.productId);
      if (activePcs !== undefined) p.totalPcs = activePcs;
    }
  } catch {
    // Abaikan — kartu tetap tampil tanpa foto / jumlah pembeli.
  }

  return result;
}

/**
 * Ambil data Closed PO (production status per varian) untuk satu produk.
 * Memanggil get_closed_po_summary dan filter by product_id.
 * Mengembalikan null jika produk tidak ditemukan (bukan CLOSED PO atau belum ada data).
 */
export async function fetchClosedPOProductFromSupabase(
  productId: string
): Promise<ClosedPOProduct | null> {
  const db = createBrowserSupabase();
  if (!db) return null;

  let rows: ClosedPOSummaryRow[];
  try {
    rows = await fetchAllClosedPOSummaryRows(db, productId);
  } catch {
    return null;
  }

  if (!rows.length) return null;

  const product: ClosedPOProduct = {
    productId,
    productName: rows[0].product_name,
    brand: rows[0].brand,
    closedAt: rows[0].closed_at,
    deadline: rows[0].deadline,
    estimatedReady: rows[0].estimated_ready,
    hasAnySubmitted: false,
    allSubmitted: true,
    variants: []
  };

  for (const row of rows) {
    const variant: ClosedPOVariant = {
      variantId: row.variant_id,
      variantName: row.variant_name,
      pendingQty: row.pending_qty,
      resellerCount: row.reseller_count,
      submittedQty: row.submitted_qty,
      productionStatus: row.production_status,
      submittedAt: row.submitted_at
    };
    product.variants.push(variant);
    if (variant.productionStatus !== "pending") product.hasAnySubmitted = true;
    if (variant.productionStatus !== "submitted") product.allSubmitted = false;
  }

  return product;
}

/**
 * Pesan galat `submit_closed_po_to_production`. Dulu SEMUA galat ditempeli
 * "Jalankan migration 019 terlebih dahulu" — sisa masa RPC-nya baru lahir.
 * Admin urfimutiyaro (2026-10-09) membaca kalimat itu padahal sebabnya
 * statement timeout 8 detik (57014) pada produk 585 varian / 4.279 baris
 * (ditutup mig 7120). Gerbang toko mig 7120 memakai 42501 dengan kalimat
 * kurasi — diteruskan apa adanya (idiom `silentInvoiceErrorMessage`).
 */
export function submitProduksiErrorMessage(error: { code?: string; message?: string }): string {
  if (error.code === "57014") {
    return (
      "Server kehabisan waktu memproses submit (pesanan produk ini sangat banyak). " +
      "Belum ada yang tersimpan — coba lagi, atau submit sebagian varian dulu."
    );
  }
  const raw = (error.message ?? "").trim();
  if (error.code === "42501" && raw && !isRawPermissionMessage(raw)) return raw;
  return terjemahkanErrorRpc(error).pesan;
}

/**
 * Submit varian Closed PO ke produksi secara atomic (mig 0190 → 0610 → 5190;
 * set-based sejak mig 7120).
 *
 * variantSubmissions: [{ variantId, variantName, qty }]
 *   — variantId digunakan untuk update product_variants
 *   — variantName digunakan untuk match order_items
 */
export async function submitClosedPOToProductionInSupabase(
  productId: string,
  variantSubmissions: Array<{
    variantId: string;
    variantName: string;
    qty: number;
  }>
) {
  const db = createBrowserSupabase();

  if (!db || !isUuid(productId) || !variantSubmissions.length) {
    return false;
  }

  const { error } = await db.rpc("submit_closed_po_to_production", {
    target_product_id: productId,
    target_variants: variantSubmissions.map((v) => ({
      variantId: v.variantId,
      variantName: v.variantName,
      qty: v.qty
    }))
  });

  if (!error) {
    return true;
  }

  console.error("submit_closed_po_to_production gagal:", error);
  throw new Error(submitProduksiErrorMessage(error));
}

/**
 * Membuka kembali produk Closed PO ke OPEN PO dengan deadline baru.
 * Gagal jika ada varian yang sudah disubmit (atomic, mig 0190).
 *
 * newDeadline format: "YYYY-MM-DD"
 */
export async function reopenClosedPOInSupabase(
  productId: string,
  newDeadline: string
) {
  const db = createBrowserSupabase();

  if (!db || !isUuid(productId)) {
    return false;
  }

  const { error } = await db.rpc("reopen_closed_po", {
    target_product_id: productId,
    new_deadline: newDeadline
  });

  if (!error) {
    return true;
  }

  throw new Error(error.message);
}

// ─── Customer Keep & Shipment ─────────────────────────────────────────────────

export type CustomerKeepItem = {
  itemId: string;
  orderId: string;
  orderCode: string;
  productName: string;
  productImage: string;
  variantName: string;
  qty: number;
  unitPrice?: number;
  keepStatus:
    | "unpaid_keep"
    | "paid_keep"
    | "hold"
    | "requested_shipment"
    | "shipped"
    | "cancelled";
  keepExpiryAt?: string;
  shipmentRequestId?: string;
};

// CustomerKeepItem (tipe di atas) masih dipakai oleh CustomerCartItem.keepStatus.
// Fungsi fetch lama dihapus — digantikan fetchCustomerOrdersBoard (orders +
// keranjang dalam satu query, audit performa 2026-07).

// ─── Keranjang Pesanan (sisi pembeli) ─────────────────────────────────────────

export type CustomerCartItem = {
  itemId: string;
  orderId: string;
  orderCode: string;
  productName: string;
  productImage: string;
  /**
   * Gudang produk SAAT INI (Multigudang, mig 7200; `null` = Gudang Utama) —
   * badge gudang kartu keranjang. Hanya dibaca saat saklar Multigudang ON.
   */
  warehouseId?: string | null;
  /** `order_items.variant_id` — kunci lookup harga normal varian (baris harga). */
  variantId: string | null;
  variantName: string;
  qty: number;
  /** Pcs yang sudah tertutup pelunasan nota (alokasi server, mig 072). */
  paidQty: number;
  unitPrice?: number;
  /**
   * Potret tambahan baris (`order_items.unit_price_extra`, mig 7900) yang
   * termasuk di `unitPrice` dan di luar diskon — bahan coretan/badge keranjang
   * (`cartItemPriceView`). `null`/absen = 0 (baris pra-7900).
   */
  unitPriceExtra?: number | null;
  /**
   * JEJAK kampanye promo saat memesan (mig 3650). `null` = TAK TERCATAT
   * (pesanan lama / dibuat admin / impor) — bukan "tanpa promo".
   */
  promoCampaignName: string | null;
  /** JEJAK voucher produk saat memesan (mig 5610; snapshot nama). `null` = tanpa voucher. */
  voucherName: string | null;
  /** Potongan voucher per pcs yang sudah TERLIPAT ke `unitPrice` (display saja). */
  voucherDiscountPerPcs: number | null;
  /**
   * JEJAK potongan promo/level yang membentuk `unitPrice` (mig promo_harga_coret)
   * — bahan pil "🔥 10% + 30%" & coretan di keranjang. `null` = tak tercatat.
   */
  discountTrace: DiscountTraceRow | null;
  /**
   * Harga Jual baris (mig 6390/7060) — coret keranjang = harga ini bila terisi,
   * sama dengan nota (`resolve_order_item_normal_price` lapis 0) dan keranjang
   * admin (bughunt D12-3). `null` = ikut harga normal varian.
   */
  manualPriceNormal?: number | null;
  /** Diskon manual baris (mig 7060) — pil keranjang menyebut diskon ini (D12-3). */
  manualDiscount?: ItemManualDiscount | null;
  /**
   * Pembelian BUNDEL baris ini (`order_items.order_bundle_id`, mig 8040) +
   * nama bundelnya (`order_bundles.bundle_name`). Absen/null = baris biasa.
   * Bahan label "Bundel: …", Batalkan Bundel, dan nota utuh K4.
   */
  orderBundleId?: string | null;
  bundleName?: string | null;
  /**
   * Hadiah pembelian (mig 8680): pembelian berhadiah baris ini, perannya
   * (`hadiah` / `syarat`), dan nama aturannya (`order_gifts.rule_name`).
   * Absen = baris biasa. Bahan pil Hadiah / Syarat hadiah dan hak hidup (mig 8850).
   */
  orderGiftId?: string | null;
  giftRole?: string | null;
  giftRuleName?: string | null;
  /**
   * `order_items.bundle_base_unit_price` (mig 8040) — harga terpisah per pcs
   * sebelum langkah bundel; bahan badge "… + Bundel" (`cartItemPriceView`).
   * NULL pada baris bundel yang harganya diubah admin (Ubah Harga).
   */
  bundleBaseUnitPrice?: number | null;
  /**
   * `order_items.bundle_base_unit_price_after_voucher` (mig 8040) — harga
   * terpisah per pcs SESUDAH voucher; NULL pada baris tanpa voucher.
   */
  bundleBaseUnitPriceAfterVoucher?: number | null;
  /**
   * `order_items.voucher_usage_id` (mig 5610) — buku pemakaian voucher baris
   * ini. Dibaca untuk cermin gerbang `VOUCHER_EDIT:shared` Batalkan Bundel:
   * voucher bundel yang juga menutup barang lain tak bisa dibatalkan sendiri.
   */
  voucherUsageId?: string | null;
  /** Catatan pembeli per varian (item_note). */
  note?: string;
  productType: "preorder" | "ready_stock";
  bucket: CartBucket;
  badge: CartBadge;
  keepStatus: CustomerKeepItem["keepStatus"] | null;
  /**
   * `order_items.item_status` — 'Menunggu' = belum disubmit ke produksi.
   * Sejak mig 7890 baris itu pun boleh diminta kirim sebelum ready
   * (`isAwaitingGoodsItem` tak lagi memandangnya); kolom ini tetap dibaca
   * untuk label/aksi lain. null = tak terbaca.
   */
  itemStatus: string | null;
  /** Alasan pembatalan (gagal_produksi / expired / admin / manual). */
  cancelReason?: string | null;
  keepExpiryAt?: string;
  /** `ready_stock` / `preorder` — sapuan tempo nota hanya menyapu ready stock. */
  keepSource?: string | null;
  shipmentRequestId?: string;
  /** Item punya baris aktif di nota pending_acc/open/partial. */
  inActiveInvoice: boolean;
  /**
   * Pcs yang sudah DIPESAN baris nota aktif tapi belum tertutup pembayaran —
   * cermin suku ketiga `order_item_free_pcs` (mig 4810). Gerbang nota memakai
   * `itemFreePcs` (qty − paidQty − ini), bukan `inActiveInvoice`.
   */
  reservedQty: number;
  /**
   * Nota yang memuat item ini (aktif + yang sudah lunas), untuk menampilkan
   * NOMOR tagihannya. Kosong = belum pernah dinotakan / notanya void. Nota
   * lunas hanya ikut bila baris ini masih memegang pcs lunas
   * (`cartInvoiceRefsForRow` — pecah-baris kiriman).
   */
  invoices: CartInvoiceRef[];
  orderStatus: string;
  productStatus: string;
  /** DP (uang muka) — info per-order, diduplikasi ke tiap item order. */
  dpRequired: boolean;
  dpAmount?: number;
  /** Status nota DP order ini (kind='dp'): paid = DP lunas. */
  dpInvoiceStatus?: InvoiceStatus | null;
  /** Id nota DP order ini — untuk link "Bayar DP di Tagihan". */
  dpInvoiceId?: string | null;
  /** Nomor nota DP untuk tampilan. */
  dpInvoiceNo?: number | null;
  /** Kode nomor kustom nota DP (Penomoran Dokumen, mig 198). */
  dpInvoiceCode?: string | null;
  /**
   * Uang BERSIH yang diterima nota DP (`dpInvoiceSettledAmount`) — nominal
   * badge "DP Lunas Rp …". Hanya terisi bila nota DP-nya `paid`.
   */
  dpPaidAmount?: number | null;
  /**
   * Bagian baris INI dari uang DP lunas pesanannya (`allocateDpPaidByRow`) —
   * nominal badge "DP Lunas Rp …". null = DP belum lunas / baris tak ber-DP
   * (batal, hadiah) → badge tanpa nominal.
   */
  dpRowAmount?: number | null;
  /**
   * DP pesanan ini ikut ditagih di tagihan barang yang MASIH menagihnya
   * (mig 6590; penyebab dinilai sejak bughunt G7-12).
   */
  dpHangusBy?: DpHangusInfo | null;
  /**
   * DP hangus yang penyebabnya tak lagi menagih pesanan ini (G7-12/13) —
   * pembeli tak disuruh membayar di tagihan barang itu.
   */
  dpHangusOrphan?: DpHangusOrphanInfo | null;
  /**
   * Nota DP pesanan ini KEDALUWARSA — semua barangnya sudah ready sebelum DP
   * dibayar (mig 7130). DP tak berlaku; yang dibayar pelunasan barangnya.
   */
  dpExpired?: boolean;
  /**
   * SEMUA barang pesanan ini sudah ready SAAT INI (`orderGoodsAllReady` atas
   * seluruh barisnya). Kotak "DP kedaluwarsa" hanya tampil bila ini benar —
   * pesanan yang kembali punya baris Belum Ready dibuka notanya oleh server
   * (mig 7140).
   */
  goodsAllReady?: boolean;
  /**
   * DP LUNAS pesanan ini yang belum tercermin sebagai pcs lunas (Rupiah,
   * diduplikasi per item seperti `dpAmount`) — dipotong dari "total belum
   * lunas" kotak ringkasan (bughunt E7-1, `cartDpCreditByOrder`).
   */
  dpCreditLeft?: number;
  /**
   * Uang bayar nota barang yang sudah menempel pcs belum lunas item ini tapi
   * belum genap satu pcs (`cartPaidResidual`, pemilik 2026-10-24) — dipotong
   * dari "total belum lunas" kotak ringkasan.
   */
  paidResidual?: number;
};

export type CustomerCart = {
  belumReady: CustomerCartItem[];
  ready: CustomerCartItem[];
  requested: CustomerCartItem[];
  shipped: CustomerCartItem[];
};

/** Info nota DP per order (untuk badge/link "Bayar DP di Tagihan"). */
type DpInvoiceInfo = {
  id: string;
  status: InvoiceStatus;
  invoiceNo: number;
  invoiceCode: string | null;
  /** Uang bersih diterima; null bila nota DP belum `paid`. */
  paidAmount: number | null;
};

/**
 * SATU select untuk halaman /orders: gabungan kolom yang dibutuhkan daftar
 * pesanan (LocalOrder) DAN keranjang (CustomerCart) — dulu dua query terpisah
 * memindai orders+order_items+products dua kali (audit performa 2026-07).
 * `product_variants` SENGAJA tidak di-embed (lihat catatan mapBoardOrderRow).
 */
const CUSTOMER_ORDERS_BOARD_SELECT =
  // `customer_id` (pemilik 2026-10-24): kunci baca bayar sebagian nota barang
  // (`fetchCartPaidResidual`); terbaca `authenticated` (dicek PROD 2026-10-24).
  "id, order_code, customer_id, customer_name, phone, address, product_note, note, status, distribution_status, created_at, " +
  // `warehouse_id` (mig 7200) — badge gudang kartu Sudah/Belum Ready; terbaca
  // `authenticated` (dicek PROD 2026-10-14). Papan ini hanya dimuat pembeli
  // yang login — anon memang tak di-grant kolom ini.
  "products(id, slug, name, status, product_type, is_manual, preorder_deadline, estimated_ready, price_normal, dp_required, dp_amount, dp_mode, warehouse_id, " +
  "product_media(media_url, storage_path, media_type, is_cover)), " +
  "order_items(id, variant_id, variant_name, qty, paid_qty, unit_price, promo_campaign_name, voucher_name, voucher_discount_per_pcs, " +
  // mig 8340: `source_item_id` + `created_at` (kolom lama, sudah di-grant ke
  // pembeli) = keluarga pecah-baris untuk pcs tertagih & pcs bebas per keluarga
  // (`itemFamilyOpen.ts`, cermin `order_item_family_open_pcs`).
  "source_item_id, created_at, " +
  "promo_discount_mode, promo_discount_value, level_discount_mode, level_discount_value, " +
  // mig 7900 (sudah di PROD): potret tambahan baris — coretan & badge keranjang
  // menilai diskon atas harga tanpa tambahan.
  "unit_price_extra, " +
  // mig 7060: harga yang diatur admin mengunci Edit Pesanan (`hasManualPrice`)
  // dan jadi dasar coret/pil keranjang pembeli (bughunt D12-3).
  "manual_price_normal, manual_discount_mode, manual_discount_value, " +
  // mig 7760: `item_status` = gerbang Request Kirim / Nota + Kirim barang Belum
  // Ready (`isAwaitingGoodsItem` — hanya yang sudah disubmit ke produksi);
  // `ready_at` = kesiapan barang baris paket untuk `orderGoodsAllReady`.
  // Keduanya kolom lama (mig 0010 / 7040) yang sudah dibaca sesi pembeli.
  // mig 8040 (`order_bundle_id`, kolom BARU — migrasinya WAJIB mendahului
  // merge): label bundel, Batalkan Bundel, kunci Edit Pesanan, nota utuh K4;
  // harga terpisah tanpa/sesudah voucher = bagian bundel badge harga.
  // `voucher_usage_id` (mig 5610, kolom lama) = cermin gerbang voucher
  // bersama Batalkan Bundel.
  "item_note, keep_status, keep_source, cancel_reason, keep_expiry_at, shipment_request_id, item_status, ready_at, voucher_usage_id, order_bundle_id, bundle_base_unit_price, bundle_base_unit_price_after_voucher, " +
  // mig 8680 (SUDAH DI-APPLY sebelum merge): jejak hadiah pembelian — pil
  // Hadiah / Syarat hadiah; baris hadiah tak dimuat Edit Pesanan (mig 8850).
  "order_gift_id, gift_role)";

/**
 * Info nota per item & nota DP per order — dua query ringan yang tak saling
 * bergantung, dijalankan PARALEL (dulu berurutan = 1 round-trip ekstra di
 * jalur panas /orders yang juga di-refetch tiap event Realtime; audit
 * performa 2026-07).
 */
async function fetchCartInvoiceInfo(
  db: NonNullable<ReturnType<typeof createBrowserSupabase>>,
  orderRows: any[]
): Promise<{
  inActiveInvoice: Set<string>;
  reservedByItem: Map<string, number>;
  /** Pcs per item yang sudah ditagih di tagihan barang hidup (mig 8000). */
  billedByItem: Map<string, number>;
  invoiceRefsByItem: Map<string, CartInvoiceRef[]>;
  dpInvoiceByOrder: Map<string, DpInvoiceInfo>;
  dpHangusByOrder: Map<string, DpHangusInfo>;
  dpOrphanByOrder: Map<string, DpHangusOrphanInfo>;
  dpCreditByOrder: Map<string, number>;
  /** Pesanan ber-nota DP kedaluwarsa (mig 7130). */
  dpExpiredOrders: Set<string>;
  /** Bayar sebagian nota barang yang belum genap satu pcs, per item. */
  paidResidualByItem: Map<string, number>;
}> {
  const itemIds: string[] = [];
  const orderIds: string[] = [];
  const customerIds = new Set<string>();
  for (const order of orderRows) {
    orderIds.push(order.id as string);
    if (order.customer_id) customerIds.add(order.customer_id as string);
    for (const item of (order.order_items ?? []) as any[]) {
      itemIds.push(item.id as string);
    }
  }

  // DIPECAH + FAIL-CLOSED (bughunt ketiga, 2026-09-30). Halaman ini memuat
  // SEMUA pesanan pembeli sepanjang masa, jadi daftar id tumbuh tanpa batas:
  // pembeli ber-683 item dulu mengirim satu GET ±26,5 KB yang ditolak gateway
  // (400) — dan galatnya ditelan, sehingga Keranjang tampil seolah tak ada item
  // yang sudah masuk nota. `selectInChunks` memecah id per 150 (paralel) dan
  // MELEMPAR galat potongan mana pun: gagal membaca nota BUKAN "tidak ada nota".
  // Bayar sebagian / Kredit DP yang belum genap 1 pcs (pemilik 2026-10-24) —
  // hanya butuh id pelanggan, jadi ikut gelombang paralel ini.
  const [lineRows, dpRows, paidResidual] = await Promise.all([
    selectInChunks(itemIds, (chunk) =>
      db
        .from("invoice_lines")
        .select(
          // `kind, qty, paid_qty` ditambahkan mig 4810 — pcs yang DIPESAN
          // baris nota aktif ikut dihitung (`reservedByItem`). Kolomnya saja
          // yang bertambah, bukan round-trip baru (audit performa mig 210).
          //
          // `invoice_payments(status)`: bukti transfer yang menunggu admin
          // membuat sapuan tempo MELEWATI notanya, jadi tanpa embed ini pil
          // tenggat di /orders menjanjikan pembatalan otomatis kepada pembeli
          // yang justru sudah membayar. Pembeli boleh membacanya — RLS
          // "Customers read own invoice payments" (mig 0920). Satu literal
          // utuh, jangan dipecah dengan `+`: hasil `.select()` hanya terurai
          // tipenya untuk literal tunggal.
          "order_item_id, kind, qty, paid_qty, cancelled_at, invoices!inner(id, invoice_no, invoice_code, status, kind, due_at, invoice_payments(status))"
        )
        .in("order_item_id", chunk)
        .is("cancelled_at", null)
        .in("invoices.status", CART_INVOICE_VISIBLE_STATUSES)
    ),
    selectInChunks(orderIds, (chunk) =>
      db
        .from("invoices")
        // mig 6590 (SUDAH DI-APPLY): nota DP hangus + nomor tagihan barang penyebabnya.
        // Buku penerimaan (amount_paid/balance_applied/refunded_amount) =
        // bahan potongan DP di ringkasan keranjang (bughunt E7-1).
        // Bughunt G7-12: `status` + rantai `merged_into` penyebab (bersarang,
        // kolom lama semua, tanpa migrasi) — pembeli tak lagi disuruh membayar
        // DP di tagihan barang yang sudah batal. Satu literal utuh.
        // mig 7130 (`dp_expired_at`, kolom BARU — migrasinya mendahului
        // merge): DP kedaluwarsa karena barang sudah ready → badge bukan
        // "DP Belum Lunas".
        .select(
          "id, invoice_no, invoice_code, status, source_order_id, amount_paid, balance_applied, refunded_amount, hangus_by_invoice_id, dp_expired_at, hangus_by:hangus_by_invoice_id(invoice_no, invoice_code, status, merged_into_invoice_id, merged_into:merged_into_invoice_id(invoice_no, invoice_code, status, merged_into_invoice_id, merged_into:merged_into_invoice_id(invoice_no, invoice_code, status, merged_into_invoice_id)))"
        )
        .eq("kind", "dp")
        .in("source_order_id", chunk)
        .or("status.in.(pending_acc,open,partial,paid),hangus_by_invoice_id.not.is.null,dp_expired_at.not.is.null")
    ),
    fetchCartPaidResidual(db, [...customerIds])
  ]);

  // `inActiveInvoice` (gerbang aksi) & daftar nota (tampilan) lahir dari baris
  // yang SAMA — lihat buildCartInvoiceIndex: nota `paid` ikut ditampilkan tapi
  // TIDAK pernah menggerbang apa pun.
  const {
    refsByItem: invoiceRefsByItem,
    activeItemIds: inActiveInvoice,
    reservedByItem,
    billedByItem
  } = buildCartInvoiceIndex(lineRows as any[]);

  const dpInvoiceByOrder = new Map<string, DpInvoiceInfo>();
  // DP hangus hanya "ditagih di tagihan barang" selama penyebabnya masih
  // menagih (G7-12/13) — dinilai dari `lineRows` yang sama.
  const orderIdByItem = new Map<string, string>();
  for (const order of orderRows) {
    for (const item of (order.order_items ?? []) as any[]) {
      orderIdByItem.set(item.id as string, order.id as string);
    }
  }
  const {
    active: activeDpRows,
    hangus: dpHangusByOrder,
    orphaned: dpOrphanByOrder,
    expired: dpExpiredOrders
  } = pickDpInvoicesByOrder(
    dpRows as any[],
    formatInvoiceNo,
    cartBillingInvoicesByOrder(lineRows as any[], orderIdByItem)
  );
  for (const r of activeDpRows.values() as Iterable<any>) {
    if (r.source_order_id) {
      dpInvoiceByOrder.set(r.source_order_id, {
        id: r.id,
        status: r.status as InvoiceStatus,
        invoiceNo: Number(r.invoice_no ?? 0),
        invoiceCode: r.invoice_code ?? null,
        paidAmount: r.status === "paid" ? dpInvoiceSettledAmount(r) : null
      });
    }
  }

  // Hanya pesanan ber-DP lunas yang memicu query (kebanyakan pembeli: nol).
  const dpCreditByOrder = await fetchCartDpCredit(
    db,
    dpRows as any[],
    paidResidual.dpByOrder
  );

  return {
    inActiveInvoice,
    reservedByItem,
    billedByItem,
    invoiceRefsByItem,
    dpInvoiceByOrder,
    dpHangusByOrder,
    dpOrphanByOrder,
    dpCreditByOrder,
    dpExpiredOrders,
    paidResidualByItem: paidResidual.moneyByItem
  };
}

/**
 * Susun keranjang pesanan dari baris orders (CUSTOMER_ORDERS_BOARD_SELECT):
 *   belumReady : item PO masih proses (keep_status NULL, order aktif) — plus
 *                item GAGAL PRODUKSI (cancelled + cancel_reason) agar pembeli
 *                melihat badge-nya.
 *   ready      : item siap dikirim/dititip (unpaid_keep / paid_keep / hold)
 *   requested  : sudah diminta kirim (requested_shipment)
 *   shipped    : sudah dikirim (shipped)
 * Item cancelled lain (hangus/batal admin) tidak ditampilkan.
 */
function buildCustomerCart(
  orderRows: any[],
  inActiveInvoice: Set<string>,
  reservedByItem: Map<string, number>,
  invoiceRefsByItem: Map<string, CartInvoiceRef[]>,
  dpInvoiceByOrder: Map<string, DpInvoiceInfo>,
  dpHangusByOrder: Map<string, DpHangusInfo> = new Map(),
  dpCreditByOrder: Map<string, number> = new Map(),
  dpOrphanByOrder: Map<string, DpHangusOrphanInfo> = new Map(),
  dpExpiredOrders: ReadonlySet<string> = new Set(),
  billedByItem: ReadonlyMap<string, number> = new Map(),
  bundleNames: ReadonlyMap<string, string> = new Map(),
  paidResidualByItem: ReadonlyMap<string, number> = new Map(),
  giftNames: ReadonlyMap<string, string> = new Map()
): CustomerCart {
  const cart: CustomerCart = {
    belumReady: [],
    ready: [],
    requested: [],
    shipped: []
  };

  for (const order of orderRows) {
    const product = order.products ?? {};
    const productType = (product.product_type ?? "preorder") as
      | "preorder"
      | "ready_stock";
    const orderStatus = (order.status ?? "") as string;
    const productStatus = (product.status ?? "") as string;

    // URL dari project AKTIF via storage_path (mig 2300); fallback media_url.
    const productImage = pickProductCoverUrl(product.product_media) ?? "";

    const dpRequired = Boolean(product.dp_required);
    // mig 8340: pcs tertagih (DP) & pcs bebas dihitung PER KELUARGA pecah-baris —
    // klaim yang tertinggal di baris sumber menutup pcs pecahannya (cermin
    // `order_item_family_open_pcs`, basis 'dp' & 'free').
    const familyItems = ((order.order_items ?? []) as any[]).map((i) =>
      familyItemFromRow(i, order.id as string)
    );
    const familyBilled = dpBilledQtyByItem(familyItems, billedByItem);
    const familyReserved = familyReservedQtyByItem(familyItems, reservedByItem);
    // DP menurut mode produk (Rp/order, Rp/pcs, % harga — mig 070) atas item yang
    // tidak cancelled DAN pcs yang belum ditagih di tagihan barang hidup (mig 8000,
    // selaras compute_order_dp_due — nominal yang sama dengan nota DP yang menagih).
    const dpAmount =
      product.dp_amount != null
        ? computeOrderDpDue(
            (product.dp_mode ?? "order") as DpMode,
            Number(product.dp_amount),
            ((order.order_items ?? []) as any[])
              .filter((i) => i.keep_status !== "cancelled")
              .map((i) => ({
                qty: Number(i.qty ?? 0),
                unitPrice: i.unit_price != null ? Number(i.unit_price) : null,
                billedQty: familyBilled.get(i.id as string) ?? 0
              }))
          )
        : undefined;
    const dpInvoice = dpInvoiceByOrder.get(order.id as string) ?? null;
    // Nilai DP lunas per baris — badge "DP Lunas Rp …" (putusan pemilik
    // 2026-10-29): nota DP hanya menyimpan total pesanan.
    const dpRowShares = allocateDpPaidByRow(
      (product.dp_mode ?? "order") as DpMode,
      dpInvoice?.paidAmount ?? null,
      ((order.order_items ?? []) as any[]).map((i) => ({
        id: i.id as string,
        qty: Number(i.qty ?? 0),
        unitPrice: i.unit_price != null ? Number(i.unit_price) : null,
        keepStatus: (i.keep_status ?? null) as string | null,
        giftRole: (i.gift_role ?? null) as string | null
      }))
    );
    // Cermin `order_goods_all_ready` atas SELURUH baris pesanan (mig 7130).
    const goodsAllReady = orderGoodsAllReady(
      ((order.order_items ?? []) as any[]).map((i) => ({
        keepStatus: (i.keep_status ?? null) as string | null,
        // mig 7760: baris paket yang barangnya belum datang = Belum Ready.
        readyAt: (i.ready_at ?? null) as string | null
      }))
    );

    for (const item of (order.order_items ?? []) as any[]) {
      const ks = (item.keep_status ?? null) as
        | CustomerCartItem["keepStatus"]
        | null;
      const cancelReason = (item.cancel_reason ?? null) as string | null;
      const paidQty = Number(item.paid_qty ?? 0);

      const cartItem: CustomerCartItem = {
        itemId: item.id as string,
        orderId: order.id as string,
        orderCode: (order.order_code ?? order.id) as string,
        productName: (product.name ?? "") as string,
        productImage,
        warehouseId:
          typeof product.warehouse_id === "string" && product.warehouse_id !== ""
            ? (product.warehouse_id as string)
            : null,
        variantId: (item.variant_id as string | null) ?? null,
        variantName: item.variant_name as string,
        qty: item.qty as number,
        paidQty,
        unitPrice: item.unit_price ?? undefined,
        unitPriceExtra: item.unit_price_extra == null ? null : Number(item.unit_price_extra),
        promoCampaignName: (item.promo_campaign_name as string | null) ?? null,
        voucherName: (item.voucher_name as string | null) ?? null,
        voucherDiscountPerPcs:
          item.voucher_discount_per_pcs == null ? null : Number(item.voucher_discount_per_pcs),
        discountTrace: discountTraceFromRow(item),
        manualPriceNormal:
          item.manual_price_normal == null ? null : Number(item.manual_price_normal),
        manualDiscount: manualDiscountFromRow(item),
        // Jejak bundel (mig 8040) — kunci hanya ada pada baris bundel, jadi
        // baris biasa terpetakan persis seperti sebelumnya.
        ...(item.order_bundle_id
          ? {
              orderBundleId: item.order_bundle_id as string,
              bundleName: bundleNames.get(item.order_bundle_id as string) ?? null,
              bundleBaseUnitPrice:
                item.bundle_base_unit_price == null ? null : Number(item.bundle_base_unit_price),
              bundleBaseUnitPriceAfterVoucher:
                item.bundle_base_unit_price_after_voucher == null
                  ? null
                  : Number(item.bundle_base_unit_price_after_voucher)
            }
          : {}),
        // Kunci hanya pada baris ber-voucher — baris biasa terpetakan seperti dulu.
        ...(item.voucher_usage_id ? { voucherUsageId: item.voucher_usage_id as string } : {}),
        // Jejak hadiah pembelian (mig 8680) — kunci hanya pada baris berhadiah.
        ...(item.order_gift_id
          ? {
              orderGiftId: item.order_gift_id as string,
              giftRole: (item.gift_role as string | null) ?? null,
              giftRuleName: giftNames.get(item.order_gift_id as string) ?? null
            }
          : {}),
        note: item.item_note ?? undefined,
        productType,
        bucket: ks && ks !== "cancelled" ? "ready" : "belum_ready",
        badge: deriveCartBadge({
          keepStatus: ks,
          cancelReason,
          paidQty,
          qty: item.qty as number,
          dpRequired,
          dpInvoiceStatus: dpInvoice?.status ?? null,
          dpHangus: dpHangusByOrder.has(order.id),
          dpExpired: dpExpiredOrders.has(order.id),
          billedQty: familyBilled.get(item.id as string) ?? 0
        }),
        keepStatus: ks,
        itemStatus: (item.item_status ?? null) as string | null,
        cancelReason,
        keepExpiryAt: item.keep_expiry_at ?? undefined,
        // Sumber keep — label tempo nota hanya menjanjikan pembatalan
        // otomatis untuk baris ready stock (`keepDeadlineView`).
        keepSource: item.keep_source ?? null,
        shipmentRequestId: item.shipment_request_id ?? undefined,
        inActiveInvoice: inActiveInvoice.has(item.id as string),
        reservedQty: familyReserved.get(item.id as string) ?? 0,
        invoices: cartInvoiceRefsForRow(
          invoiceRefsByItem.get(item.id as string),
          paidQty
        ),
        orderStatus,
        productStatus,
        dpRequired,
        dpAmount,
        dpInvoiceStatus: dpInvoice?.status ?? null,
        dpInvoiceId: dpInvoice?.id ?? null,
        dpInvoiceNo: dpInvoice?.invoiceNo ?? null,
        dpInvoiceCode: dpInvoice?.invoiceCode ?? null,
        dpPaidAmount: dpInvoice?.paidAmount ?? null,
        dpRowAmount: dpRowShares.get(item.id as string) ?? null,
        dpHangusBy: dpHangusByOrder.get(order.id) ?? null,
        dpHangusOrphan: dpOrphanByOrder.get(order.id) ?? null,
        dpExpired: dpExpiredOrders.has(order.id),
        goodsAllReady,
        dpCreditLeft: dpCreditByOrder.get(order.id) ?? 0,
        paidResidual: paidResidualByItem.get(item.id as string) ?? 0
      };

      if (ks === "unpaid_keep" || ks === "paid_keep" || ks === "hold") {
        cart.ready.push(cartItem);
      } else if (ks === "requested_shipment") {
        cart.requested.push(cartItem);
      } else if (ks === "shipped") {
        cart.shipped.push(cartItem);
      } else if (ks === "cancelled") {
        // Hanya item GAGAL PRODUKSI yang tetap ditampilkan (badge merah);
        // cancelled lain (hangus/batal) disembunyikan seperti sebelumnya.
        if (cancelReason === "gagal_produksi") {
          cart.belumReady.push(cartItem);
        }
      } else if (
        productType === "preorder" &&
        CART_BELUM_READY_ORDER_STATUSES.includes(orderStatus)
      ) {
        cart.belumReady.push(cartItem);
      }
    }
  }

  return cart;
}

/**
 * Nama pembelian BUNDEL (mig 8040) per `order_bundle_id` baris keranjang —
 * hanya label, jadi BEST-EFFORT: gagal dibaca = label "Bundel" tanpa nama,
 * bukan keranjang yang gagal tampil. Pembeli tanpa baris bundel: nol query.
 */
async function fetchCartBundleNames(orderRows: any[]): Promise<Map<string, string>> {
  const ids = new Set<string>();
  for (const order of orderRows) {
    for (const item of (order.order_items ?? []) as any[]) {
      if (item.order_bundle_id) ids.add(item.order_bundle_id as string);
    }
  }
  const out = new Map<string, string>();
  if (!ids.size) return out;
  try {
    for (const [id, b] of await fetchOrderBundles([...ids])) out.set(id, b.bundleName);
  } catch {
    // label saja — lihat kepala fungsi
  }
  return out;
}

/**
 * Pembelian BERHADIAH (mig 8680) per `order_gift_id`: nama aturan (label pil),
 * snapshot aturan & catatan penggeseran terakhir (mig 8850 — pratinjau Edit
 * Pesanan & keterangan hadiah yang kurang). BEST-EFFORT seperti nama bundel:
 * gagal dibaca = pil tanpa nama, tanpa pratinjau. Pembeli tanpa baris
 * berhadiah: nol query.
 */
async function fetchCartGiftInfo(orderRows: any[]): Promise<Map<string, OrderGiftInfo>> {
  const ids = new Set<string>();
  for (const order of orderRows) {
    for (const item of (order.order_items ?? []) as any[]) {
      if (item.order_gift_id) ids.add(item.order_gift_id as string);
    }
  }
  if (!ids.size) return new Map();
  try {
    return await fetchOrderGiftInfo([...ids]);
  } catch {
    return new Map();
  }
}

/** Hasil gabungan halaman /orders: daftar pesanan + keranjang dari SATU query. */
export type CustomerOrdersBoard = {
  orders: LocalOrder[];
  cart: CustomerCart;
  /** Pembelian berhadiah pembeli ini per `order_gift_id` (mig 8680/8850). */
  gifts: ReadonlyMap<string, OrderGiftInfo>;
};

/**
 * Papan pesanan pembeli — daftar pesanan (LocalOrder[]) DAN keranjang
 * (CustomerCart) dari SATU round-trip orders (+2 query paralel info nota).
 * Menggantikan pasangan fetchCustomerOrdersFromSupabase +
 * fetchCustomerCartFromSupabase yang memindai tabel yang sama dua kali
 * (audit performa 2026-07). RLS men-scope ke pembeli yang login; tanpa sesi
 * pembeli hasilnya kosong. `null` hanya bila Supabase belum dikonfigurasi.
 */
export async function fetchCustomerOrdersBoard(): Promise<CustomerOrdersBoard | null> {
  const db = createBrowserSupabase();
  if (!db) return null;

  const { data, error } = await db
    .from("orders")
    .select(CUSTOMER_ORDERS_BOARD_SELECT)
    .order("created_at", { ascending: false });

  if (error) throw new Error(error.message);

  const orderRows = (data ?? []) as any[];
  const {
    inActiveInvoice,
    reservedByItem,
    billedByItem,
    invoiceRefsByItem,
    dpInvoiceByOrder,
    dpHangusByOrder,
    dpOrphanByOrder,
    dpCreditByOrder,
    dpExpiredOrders,
    paidResidualByItem
  } = await fetchCartInvoiceInfo(db, orderRows);
  const [bundleNames, gifts] = await Promise.all([
    fetchCartBundleNames(orderRows),
    fetchCartGiftInfo(orderRows)
  ]);
  const giftNames = new Map([...gifts].map(([id, g]) => [id, g.name] as const));

  return {
    gifts,
    orders: orderRows.map((o) => mapBoardOrderRow(o)),
    cart: buildCustomerCart(
      orderRows,
      inActiveInvoice,
      reservedByItem,
      invoiceRefsByItem,
      dpInvoiceByOrder,
      dpHangusByOrder,
      dpCreditByOrder,
      dpOrphanByOrder,
      dpExpiredOrders,
      billedByItem,
      bundleNames,
      paidResidualByItem,
      giftNames
    )
  };
}

export type ShipmentItemInput = { itemId: string; shipQty: number };

/**
 * Request kirim dari pembeli (atomic via RPC, mendukung kirim sebagian qty).
 * @param items  daftar { itemId, shipQty }. shipQty boleh < qty item.
 */
/**
 * Alamat tujuan request kirim (mig 073) — di-SNAPSHOT server-side ke
 * shipment_requests. Pilih salah satu: `addressId` (alamat tersimpan) ATAU
 * alamat manual (recipientName + phone + addressLine wajib). Dropship: wajib
 * nama + HP pengirim (dicetak di label menggantikan identitas toko).
 */
export type ShipmentAddressInput = {
  addressId?: string | null;
  recipientName?: string;
  phone?: string;
  addressLine?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  /** Nama kecamatan (mig 3720/3750). */
  district?: string;
  /** Jangkar tarif Biteship; `null`/kosong = alamat lama, ongkir manual. */
  biteshipAreaId?: string | null;
  biteshipDistrictKey?: string | null;
  isDropship?: boolean;
  dropshipSenderName?: string;
  dropshipSenderPhone?: string;
  /** Preferensi ekspedisi pembeli (opsional); admin tetap bisa menggantinya. */
  expedition?: string | null;
};

/**
 * `ShipmentAddressInput` → `p_address` jsonb. SATU pembangun untuk SEMUA RPC
 * pengiriman.
 *
 * Sebelumnya bentuk ini disalin di LIMA tempat (dua di berkas ini, satu di
 * `supabase/invoices.ts`, dua di `boardActions.ts`) — dan itu persis jebakan
 * yang dicatat rencana §8a-bis untuk sisi SQL: menambah satu field alamat lalu
 * lupa di salah satu salinan membuat field-nya hilang DIAM-DIAM di sebagian
 * alur, tanpa error di mana pun. Kecamatan adalah field pertama yang menguji
 * itu; dengan satu pembangun, "lupa di satu tempat" tak lagi mungkin.
 *
 * Kunci yang nilainya null tetap DIKIRIM (bukan dihilangkan): sisi SQL membaca
 * `p_address->>'…'` yang memulangkan null untuk kunci absen maupun kunci
 * ber-null, jadi keduanya setara — kecuali di `update_shipment_intent`, yang
 * memakai operator `?` untuk `weight_gram` dan sengaja TIDAK dipakai untuk
 * kolom alamat (lihat mig 3750 §6).
 */
export function buildShipmentAddressPayload(address: ShipmentAddressInput) {
  return {
    address_id: address.addressId ?? null,
    recipient_name: address.recipientName ?? null,
    phone: address.phone ?? null,
    address_line: address.addressLine ?? null,
    city: address.city ?? null,
    province: address.province ?? null,
    postal_code: address.postalCode ?? null,
    district: address.district ?? null,
    biteship_area_id: address.biteshipAreaId ?? null,
    biteship_district_key: address.biteshipDistrictKey ?? null,
    is_dropship: address.isDropship ?? false,
    dropship_sender_name: address.dropshipSenderName ?? null,
    dropship_sender_phone: address.dropshipSenderPhone ?? null
  };
}

/**
 * Saldo terkini pelanggan (RLS: pembeli baca miliknya). 0 bila belum ada baris.
 * Error QUERY (bukan "belum ada baris") → THROW, bukan 0 (bughunt H2-9: 0 tak
 * bisa dibedakan dari "gagal", dan di halaman Bayar itu berarti opsi "Bayar
 * pakai Saldo" hilang total tanpa pesan). Semua pemanggil lain sudah punya
 * `.catch()` yang aman diberi promise reject (jatuh ke 0 seperti sebelumnya).
 */
export async function fetchCustomerBalance(customerId: string): Promise<number> {
  const db = createBrowserSupabase();
  if (!db) return 0;
  const { data, error } = await db
    .from("customer_balance")
    .select("balance")
    .eq("customer_id", customerId)
    .maybeSingle();
  if (error) throw new Error(terjemahkanErrorRpc(error).pesan);
  return Number((data as { balance?: number } | null)?.balance ?? 0);
}

export type CustomerShipmentItem = {
  /**
   * Id baris `order_items` — dipakai me-resolve TAGIHAN paket ini (kartu
   * Detail Pembayaran). Tanpa ini pembeli tak punya jangkar apa pun ke
   * notanya selain nota ongkir.
   */
  orderItemId: string;
  productName: string;
  /**
   * Foto cover produk. HANYA diisi fetcher DETAIL
   * (`fetchCustomerShipmentByIdFromSupabase`) — daftar tab Pengiriman sengaja
   * tak memuatnya demi payload, dan kartunya juga tak merender gambar. null =
   * produk tak punya foto ATAU dimuat lewat jalur daftar; keduanya sama-sama
   * berarti "jangan render bingkai gambar".
   */
  imageUrl: string | null;
  variantName: string;
  qty: number;
  /** Catatan variasi (order_items.note) — dipakai pencarian tab Pengiriman. */
  note: string | null;
  /**
   * Kesiapan barang baris ini di dalam paket (mig 7760): `order_items.ready_at`
   * — NULL = barangnya belum datang (paket menunggu di Dikemas).
   */
  readyAt?: string | null;
  keepStatus?: string | null;
  /**
   * Berat SATUAN varian (gram) — bahan hitung berat paket saat
   * `shipment_requests.weight_gram` belum diisi admin (mig 221). null =
   * berat produknya memang kosong.
   */
  weightGram: number | null;
  /**
   * Gudang produk SAAT INI (Multigudang, mig 7200; `null` = Gudang Utama) —
   * bahan "Dikirim dari …". Kiriman tak menyimpan gudangnya sendiri.
   */
  warehouseId: string | null;
  // ── Bahan SURAT JALAN unduhan pembeli (2026-09) ──
  // Hanya diisi fetcher DETAIL; daftar tab Pengiriman sengaja tak memuatnya
  // (alasan payload yang sama dengan `imageUrl`, lihat komentar select).
  /** Kode SKU varian — kolom sub-baris nama produk di surat jalan. */
  sku: string | null;
  /** Merek produk — badge di sub-baris nama produk. */
  brand: string | null;
  /** Harga satuan yang ditagihkan (snapshot `order_items.unit_price`). */
  unitPrice: number | null;
  /** Snapshot tambahan per pcs (`order_items.unit_price_extra`, mig 7900); jalur DETAIL saja. */
  unitPriceExtra?: number | null;
  /** Baris ASAL bila item ini pecahan kirim-sebagian (mig 4600). */
  sourceItemId: string | null;
  /** Jejak voucher produk (mig 5610; snapshot nama) — sub-baris surat jalan pembeli. */
  voucherName: string | null;
  /**
   * Potongan voucher produk per pcs (mig 5610; sudah terlipat ke `unitPrice`) —
   * kolom Voucher & seksi "Potongan voucher" surat jalan pembeli. Hanya jalur
   * DETAIL yang memuatnya.
   */
  voucherDiscountPerPcs?: number | null;
  /** Jejak potongan (mig 5920) — persen "10% + 30%" di kolom Diskon surat jalan. */
  discountTrace?: DiscountTraceRow | null;
};

export type CustomerShipment = {
  id: string;
  status: string; // draft | waiting_payment | paid | shipped | done
  paymentStatus: string; // pending | paid | verified
  ongkir: number;
  /** Biaya dropship/packing dari baris manual nota ongkir (mig 105). 0 bila tidak ada. */
  dropshipFee: number;
  resiNumber: string | null;
  expedition: string | null;
  /**
   * Berat total kiriman (gram) yang diisi admin saat Set Ongkir / Buat Nota +
   * Kirim (mig 221). null = belum diisi → layar jatuh ke hitung otomatis dari
   * berat produk (`resolveShipmentWeight`).
   */
  weightGram: number | null;
  /** Resi mandiri pembeli (mig 163/164) — bisa diedit selama pra-kirim. */
  selfManaged: boolean;
  /**
   * Ongkir dibayar SENDIRI oleh pembeli ke kurirnya (mig 4040) ⇒ tak ditagih.
   * Bedakan dari ongkir 0 "Gratis", yang berarti TOKO menanggung biayanya.
   */
  ongkirPaidByBuyer: boolean;
  createdAt: string;
  shippedAt: string | null;
  /** Kapan pembeli konfirmasi "Pesanan Diterima" (mig 082). */
  receivedAt: string | null;
  items: CustomerShipmentItem[];
  /** Kesiapan barang paket (mig 7760) — chip kartu/detail: Belum Ready / Ready Sebagian. */
  goodsReadiness: GoodsReadinessSummary;
  /** Snapshot tujuan (mig 073) — fallback "—" untuk request lama. */
  recipientName: string | null;
  recipientPhone: string | null;
  shipAddressLine: string | null;
  shipDistrict: string | null;
  shipCity: string | null;
  shipProvince: string | null;
  shipPostalCode: string | null;
  isDropship: boolean;
  dropshipSenderName: string | null;
  dropshipSenderPhone: string | null;
  /** Catatan request kirim (shipment_requests.notes). */
  notes: string | null;
  /** Nota ongkir (kind='ongkir') — pembayaran pindah ke tab Tagihan. */
  ongkirInvoiceId: string | null;
  ongkirInvoiceNo: number | null;
  /** Kode nomor kustom nota ongkir (Penomoran Dokumen, mig 198). */
  ongkirInvoiceCode: string | null;
  ongkirInvoiceStatus: InvoiceStatus | null;
  /**
   * VOUCHER ONGKIR (mig 5620). `voucherName` = snapshot nama di paket (null =
   * tanpa voucher). `voucherOngkirDiscount` = Σ baris potongan voucher yang
   * hidup di nota ongkir paket ini; null = voucher terpasang tapi ongkirnya
   * belum ditetapkan admin (potongan menunggu). `ongkir` TETAP BRUTO.
   */
  voucherName: string | null;
  voucherOngkirDiscount: number | null;
  /** Kode nomor kustom pengiriman (Penomoran Dokumen, mig 198). */
  shipmentCode: string | null;
};

// Berat satuan varian di-embed lewat FK `variant_id` (SATU baris per item),
// BUKAN `products(product_variants(...))` seperti Detail Pengiriman admin:
// fetcher ini menarik SELURUH riwayat pengiriman pembeli sekaligus, dan embed
// by-name akan menyeret setiap kombinasi varian produknya (produk preorder
// terbesar 4.644 baris) — persis payload yang dibuang dari /orders pada audit
// performa mig 210. Item tanpa `variant_id` karena itu berakhir "belum diisi"
// alih-alih dicocokkan by-name; PROD 2026-08: 0 dari 433 item terkirim.
const CUSTOMER_SHIPMENT_BASE_SELECT =
  "id, shipment_code, status, payment_status, ongkir, weight_gram, resi_number, expedition, self_managed, ongkir_paid_by_buyer, created_at, shipped_at, received_at, notes, " +
  "recipient_name, recipient_phone, ship_address_line, ship_district, ship_city, ship_province, ship_postal_code, " +
  "is_dropship, dropship_sender_name, dropship_sender_phone, ongkir_invoice_id, voucher_name, " +
  // VOUCHER ONGKIR (mig 5620): usage paket — RLS voucher_usages meloloskan
  // pembeli ke barisnya sendiri. `amount_granted` = potongan atas SEMUA nota
  // paket (nota ongkir, nota "Ongkir — selisih", maupun nota produk combo),
  // jadi tak bergantung pada nota mana yang memegang barisnya.
  "voucher_usage:voucher_usages!shipment_requests_voucher_usage_id_fkey(amount_granted, status), " +
  "ongkir_invoice:invoices!ongkir_invoice_id(id, invoice_no, invoice_code, status, invoice_lines(kind, amount, cancelled_at))";

const CUSTOMER_SHIPMENT_ITEM_SELECT =
  // mig 7760: `ready_at` + `keep_status` → badge Ready/Belum Ready per baris &
  // chip kesiapan paket (RLS pembeli membaca barisnya sendiri).
  "id, qty, variant_name, note:item_note, ready_at, keep_status, " +
  "variant:product_variants!order_items_variant_id_fkey(weight)";

/**
 * Tambahan kolom yang HANYA dibutuhkan surat jalan unduhan pembeli — harga
 * satuan, SKU, merek, baris asal. SENGAJA terpisah dan hanya ditempel ke
 * select DETAIL: fetcher DAFTAR menarik SELURUH riwayat pengiriman pembeli
 * tanpa limit, dan kartunya tak mencetak dokumen apa pun (kelas regresi yang
 * dibersihkan audit performa mig 210).
 */
const CUSTOMER_SHIPMENT_DOC_ITEM_SELECT =
  // `voucher_discount_per_pcs` (mig 5610) — sudah dibaca pembeli di /orders
  // (`CUSTOMER_ORDERS_BOARD_SELECT`), jadi kolom & RLS-nya sudah teruji di PROD.
  "unit_price, unit_price_extra, source_item_id, voucher_name, voucher_discount_per_pcs, " +
  // mig 5920 — jejak potongan (sudah ter-apply di PROD sebelum merge).
  "promo_discount_mode, promo_discount_value, level_discount_mode, level_discount_value, " +
  "doc_variant:product_variants!order_items_variant_id_fkey(sku)";

/**
 * Daftar tab Pengiriman — SEMUA pengiriman pembeli, jadi tanpa foto produk.
 * `products.warehouse_id` (mig 7200) = bahan "Dikirim dari …"; policy
 * "Customers read ordered products" meloloskan pembeli ke produk pesanannya.
 */
const CUSTOMER_SHIPMENT_SELECT =
  `${CUSTOMER_SHIPMENT_BASE_SELECT}, ` +
  `order_items!shipment_request_id(${CUSTOMER_SHIPMENT_ITEM_SELECT}, products(name, warehouse_id))`;

/**
 * Detail SATU pengiriman — sama persis + foto cover produk untuk kartu Isi
 * Paket. Sengaja select TERPISAH: `fetchCustomerShipmentsFromSupabase`
 * menarik SELURUH pengiriman pembeli tanpa limit, dan sebuah produk bisa punya
 * belasan baris `product_media`; menempelkannya di sana = payload berlipat
 * demi gambar yang kartunya memang tak render (kelas regresi yang justru
 * dibersihkan audit performa mig 210).
 */
const CUSTOMER_SHIPMENT_DETAIL_SELECT =
  `${CUSTOMER_SHIPMENT_BASE_SELECT}, ` +
  `order_items!shipment_request_id(${CUSTOMER_SHIPMENT_ITEM_SELECT}, ` +
  `${CUSTOMER_SHIPMENT_DOC_ITEM_SELECT}, ` +
  `products(name, brand, warehouse_id, product_media(media_url, storage_path, media_type, is_cover)))`;

function mapCustomerShipmentRow(sr: any): CustomerShipment {
  const inv = sr.ongkir_invoice ?? null;
  const invLines: any[] = inv?.invoice_lines ?? [];
  const dropshipFee = invLines
    .filter((l: any) => l.kind === "manual" && !l.cancelled_at)
    .reduce((s: number, l: any) => s + Number(l.amount ?? 0), 0);
  return {
    id: sr.id,
    status: sr.status,
    paymentStatus: sr.payment_status,
    ongkir: Number(sr.ongkir ?? 0),
    dropshipFee,
    resiNumber: sr.resi_number ?? null,
    expedition: sr.expedition ?? null,
    weightGram: sr.weight_gram != null ? Number(sr.weight_gram) : null,
    selfManaged: Boolean(sr.self_managed),
    ongkirPaidByBuyer: Boolean(sr.ongkir_paid_by_buyer),
    createdAt: sr.created_at,
    shippedAt: sr.shipped_at ?? null,
    receivedAt: sr.received_at ?? null,
    items: ((sr.order_items ?? []) as any[]).map((i) => {
      const w = i.variant?.weight;
      return {
        orderItemId: i.id as string,
        productName: i.products?.name ?? "",
        imageUrl: pickProductCoverUrl(i.products?.product_media),
        variantName: i.variant_name,
        qty: i.qty,
        note: i.note ?? null,
        readyAt: (i.ready_at as string | null | undefined) ?? null,
        keepStatus: (i.keep_status as string | null | undefined) ?? null,
        weightGram: w != null ? Number(w) : null,
        warehouseId:
          typeof i.products?.warehouse_id === "string" && i.products.warehouse_id !== ""
            ? (i.products.warehouse_id as string)
            : null,
        // Kolom dokumen hanya ada pada jalur DETAIL; fetcher DAFTAR
        // memulangkan undefined → null, dan kartunya memang tak memakainya.
        sku: i.doc_variant?.sku ?? null,
        brand: i.products?.brand ?? null,
        unitPrice: i.unit_price != null ? Number(i.unit_price) : null,
        unitPriceExtra: i.unit_price_extra != null ? Number(i.unit_price_extra) : null,
        sourceItemId: i.source_item_id ?? null,
        voucherName: (i.voucher_name as string | null) ?? null,
        voucherDiscountPerPcs:
          i.voucher_discount_per_pcs == null ? null : Number(i.voucher_discount_per_pcs),
        discountTrace: discountTraceFromRow(i)
      };
    }),
    goodsReadiness: shipmentGoodsReadiness(
      ((sr.order_items ?? []) as any[]).map((i) => ({
        qty: Number(i.qty ?? 0),
        readyAt: (i.ready_at as string | null | undefined) ?? null,
        keepStatus: (i.keep_status as string | null | undefined) ?? null
      }))
    ),
    recipientName: sr.recipient_name ?? null,
    recipientPhone: sr.recipient_phone ?? null,
    shipAddressLine: sr.ship_address_line ?? null,
    shipDistrict: sr.ship_district ?? null,
    shipCity: sr.ship_city ?? null,
    shipProvince: sr.ship_province ?? null,
    shipPostalCode: sr.ship_postal_code ?? null,
    isDropship: Boolean(sr.is_dropship),
    dropshipSenderName: sr.dropship_sender_name ?? null,
    dropshipSenderPhone: sr.dropship_sender_phone ?? null,
    notes: sr.notes ?? null,
    ongkirInvoiceId: inv?.id ?? sr.ongkir_invoice_id ?? null,
    ongkirInvoiceNo: inv?.invoice_no != null ? Number(inv.invoice_no) : null,
    ongkirInvoiceCode: inv?.invoice_code ?? null,
    ongkirInvoiceStatus: (inv?.status ?? null) as InvoiceStatus | null,
    // Usage yang sudah DILEPAS (gratis/batal/dilepas admin) = tanpa voucher,
    // walau snapshot namanya masih tersimpan di paket — cermin `board.ts`.
    ...(() => {
      const usage = Array.isArray(sr.voucher_usage) ? sr.voucher_usage[0] : sr.voucher_usage;
      if (!usage || usage.status !== "applied") {
        return { voucherName: null, voucherOngkirDiscount: null };
      }
      return {
        voucherName: (sr.voucher_name as string | null) ?? null,
        voucherOngkirDiscount:
          usage.amount_granted == null ? null : Number(usage.amount_granted)
      };
    })(),
    shipmentCode: sr.shipment_code ?? null
  };
}

/** Daftar shipment request milik pembeli yang login (RLS: reseller_id = auth.uid()). */
export async function fetchCustomerShipmentsFromSupabase(): Promise<
  CustomerShipment[]
> {
  const db = createBrowserSupabase();
  if (!db) return [];

  const { data, error } = await db
    .from("shipment_requests")
    .select(CUSTOMER_SHIPMENT_SELECT)
    .neq("status", "cancelled")
    .order("created_at", { ascending: false })
    // bughunt J3-11: batas jaga-jaga (aman hari ini, cegah truncation diam-
    // diam PostgREST kalau riwayat pengiriman satu pelanggan kelak membesar).
    .limit(500);

  if (error || !data) return [];

  return (data as any[]).map(mapCustomerShipmentRow);
}

/**
 * Jumlah pengiriman pembeli berstatus tertentu — `count: exact` + `head: true`,
 * jadi TIDAK mengunduh satu baris pun.
 *
 * Kartu ringkasan Akun ("Sedang Dikirim") dulu memakai
 * `fetchCustomerShipmentsFromSupabase()` lalu `.filter(status==='shipped').length`
 * — artinya SELURUH riwayat pengiriman (beserta isi paket, alamat, & nota
 * ongkir ter-embed) diunduh hanya untuk menghasilkan satu angka, dan biayanya
 * tumbuh seumur akun. Angkanya identik; payload-nya kini tetap.
 *
 * Melempar bila query gagal — `count` null berarti GAGAL maupun KOSONG di
 * supabase-js, jadi menelannya akan menampilkan "0 sedang dikirim" pada akun
 * yang paketnya sebenarnya sedang jalan.
 */
export async function fetchCustomerShipmentCount(
  status: string
): Promise<number> {
  const db = createBrowserSupabase();
  if (!db) return 0;

  const { count, error } = await db
    .from("shipment_requests")
    .select("id", { count: "exact", head: true })
    .eq("status", status);

  if (error) throw new Error(error.message);
  return count ?? 0;
}

/** Satu shipment request milik pembeli login by id (RLS membatasi kepemilikan). */
export async function fetchCustomerShipmentByIdFromSupabase(
  id: string
): Promise<CustomerShipment | null> {
  const db = createBrowserSupabase();
  if (!db) return null;

  const { data, error } = await db
    .from("shipment_requests")
    .select(CUSTOMER_SHIPMENT_DETAIL_SELECT)
    .eq("id", id)
    .maybeSingle();

  if (error || !data) return null;

  return mapCustomerShipmentRow(data);
}

/**
 * Pembeli/admin koreksi resi mandiri (mig 164) — Nama Ekspedisi + Nomor Resi —
 * selama paket BELUM dikirim (status paid/waiting_payment, hanya shipment
 * `self_managed`). Validasi & guard di RPC.
 */
export async function updateSelfShipmentResiInSupabase(
  shipmentId: string,
  expedition: string,
  resi: string
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum dikonfigurasi.");
  const { error } = await db.rpc("update_self_shipment_resi", {
    p_shipment_id: shipmentId,
    p_expedition: expedition.trim(),
    p_resi: resi.trim()
  });
  if (error) throw new Error(error.message);
}

/**
 * Pembeli konfirmasi paket sudah sampai (mig 082): shipment 'shipped' → 'done'.
 * Owner-or-admin; idempoten bila sudah 'done'.
 */
export async function markShipmentReceivedInSupabase(
  shipmentId: string
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum dikonfigurasi.");
  const { error } = await db.rpc("mark_shipment_received", {
    p_shipment_id: shipmentId
  });
  if (error) throw new Error(error.message);
}

/**
 * Pembeli (owner) tarik / batalkan request kirim MILIKNYA sendiri (mig 125):
 * hanya selama shipment masih draft / waiting_payment (sebelum ongkir dibayar
 * & dikemas). Item kembali ke Keranjang (keep dipulihkan per pelunasan), nota
 * ongkir yang belum tersentuh bayaran di-void (tersentuh → RPC RAISE), shipment
 * jadi 'cancelled'. Notif 'order_updated' dikirim dari RPC.
 */
export async function cancelMyShipmentRequestInSupabase(
  shipmentId: string
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum dikonfigurasi.");
  const { error } = await db.rpc("cancel_shipment_request", {
    p_shipment_id: shipmentId
  });
  if (error) throw new Error(error.message);
}
